import React, { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import StatDoctorWordmark from "./brand/StatDoctorWordmark";
import { useNavigate } from "react-router-dom";
import {
  addDays,
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Check,
  Loader2,
  ExternalLink,
  Calendar,
  Plus,
  Minus,
  AlertTriangle,
  FileSpreadsheet,
  UploadCloud,
  Mail,
  MapPin,
  Building2,
  Camera,
} from "lucide-react";
import AiShiftProvenancePanel from "@/components/AiShiftProvenancePanel";
import InviteDoctorsModal from "@/components/InviteDoctorsModal";
import AiShiftValidationReviewDialog from "@/components/AiShiftValidationReviewDialog";
import AcceptCriteriaEditor from "@/components/AcceptCriteriaEditor";
import SearchableSelectField from "@/components/SearchableSelectField";
import {
  AcceptCriterion,
  buildAcceptCriteriaFromPrefill,
  buildEligibilityRows,
  dedupeAcceptCriteria,
  excludePrimaryFromAcceptCriteria,
  formatAcceptCriterionRate,
  suggestAcceptCriteriaGroups,
} from "@/utils/acceptCriteriaUtils";
import {
  releaseOperationalFocus,
  setOperationalFocus,
} from "../features/operational-ai/focusStore";
import SkillLevelWithInfo from "@/components/skill/SkillLevelWithInfo";
import { useSkillLevelNamingOptional } from "../contexts/SkillLevelNamingContext";
import { buildSkillOptionsFromCatalog } from "@/constants/skillLevelStateNaming";
import {
  getAiMappedFieldAlerts,
  mergeEligibleSuggestions,
  suggestEmailEligibleRoles,
} from "@/utils/aiShiftReviewUtils";
import { message, Spin, TimePicker } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import api, {
  bulkAddShiftsV2,
  editShiftV2,
  uploadHospitalAiShiftDocument,
} from "../api/shift";
import apiAccount from "../api/account";
import { createTemplate, getTemplates, parseTemplateListResponse, getTemplateListError, formatTemplateDefaultRate, formatTemplateAcceptCriterionLabel } from "../api/template";
import { listHospitalAutomations } from "../api/automation";
import {
  isPostShiftAutomation,
  listPostShiftAutomations,
  postingAutomationPayload,
  releaseStagesFromAutomation,
  summarizeReleasePlan,
} from "../features/automation/posting";
import type { HospitalAutomation } from "../features/automation/types";
import {
  buildPastedEmailDocumentFile,
  extractEmailPasteFromClipboard,
  pastedEmailHasEnoughContent,
  PastedEmailDisplay,
  readEmailFromSystemClipboard,
} from "../utils/pastedEmailPreview";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { isRosterUploadFile } from "../utils/mapRosterExtractionToPrefill";
import { filterExtraDocumentsOnly } from "../utils/extraDocumentsFilter";
import { useGrandmasterExperience } from "../contexts/GrandmasterExperienceContext";
import { setHospitals, type Hospital } from "../app/currentHospital";
import { updateChildHospitalImage, getHospitalsByParentName } from "../api/hospitals";
import { resolveHospitalImageUrl } from "../utils/hospitalImageUrl";
import apiCommon from "../api/common";
import {
  clinicNetworkBrands,
  clinicNetworkLeafSites,
  clinicNetworkSitesForBrand,
  clinicSiteShortLabel,
  defaultContractedRateForBrand,
  locumDescriptionForBrand,
  isPriorityClinic,
  pickClinicNetworkSkill,
  pickClinicNetworkSpecialty,
  filterClinicNetworkSpecialties,
  pickPriorityClinicIds,
  sortClinicsPriorityFirst,
} from "../utils/clinicNetworkSites";

// Types
interface QuickShiftDate {
  date: string;
  displayDate: string;
  shiftsDaysId?: string;
  startTime: string;
  startAMPM: "AM" | "PM";
  endTime: string;
  endAMPM: "AM" | "PM";
  rate: string;
  rateType: "perHour" | "contracted";
  onCall: boolean;
  onCallDetails: string;
  /** Per-row eligibility from roster upload (raw label, resolved on submit). */
  specialty?: string;
  skillLevel?: string;
  label?: string;
}

export type ShiftPremiereAudience =
  | "INTERNAL"
  | "PRE_CREDENTIALED"
  | "FAVORITE"
  | "WORKED_BEFORE";

export interface QuickCreateShiftPrefillShift {
  date: string;
  shiftsDaysId?: string;
  startTime?: string;
  endTime?: string;
  rate?: string | number;
  rateType?: "perHour" | "contracted";
  onCall?: boolean;
  onCallDetails?: string;
  specialty?: string;
  skillLevel?: string;
  label?: string;
}

export interface QuickCreateShiftPrefill {
  shifts: QuickCreateShiftPrefillShift[];
  postMode?: "group" | "separate";
  lockPostMode?: boolean;
  rosterSplitByEligibility?: boolean;
  editWarning?: string;
  specialty?: string;
  skillLevel?: string;
  supportLevel?: string;
  description?: string;
  travel?: string;
  transportIncluded?: boolean;
  transportSelected?: "variable" | "set" | "other" | "";
  transportType?: string;
  transportRate?: string;
  transportOtherDetails?: string;
  accommodation?: string;
  accommodationIncluded?: boolean;
  accommodationType?: string;
  accommodationOtherDetails?: string;
  additionalFilesRequired?: string[];
  documentUrls?: string[];
  contactEmail?: string;
  contactFullName?: string;
  contactNumber?: string;
  /** Hire review metadata from AI queue — not posted to live shift API */
  fieldSources?: Record<string, any>;
  specialtyAlternatives?: string[];
  skillLevelAlternatives?: string[];
  /** Paired rules persisted to live shifts.accept_criteria on publish */
  acceptCriteria?: AcceptCriterion[];
  positionsRequired?: number;
  /** Target hospital when publishing an AI-validated shift (master / grandmaster). */
  publishHospitalId?: string;
  publishHospitalOptions?: {
    id: string;
    name: string;
    image?: string;
  }[];
  premiereEnabled?: boolean;
  premiereAudiences?: ShiftPremiereAudience[];
  premiereDays?: number;
}

export type QuickCreateCreatedDay = {
  date: string;
  shiftId: string;
  shiftDayId: string;
};

export type QuickCreateShiftResult = {
  submittedDates?: string[];
  documentQueued?: boolean;
  documentQueueCount?: number;
  createdShiftIds?: string[];
  createdDays?: QuickCreateCreatedDay[];
  premiere?: {
    audiences: ShiftPremiereAudience[];
    expiresAt: number;
  };
};

interface CreateShiftModalState {
  open: boolean;
  loading: boolean;
  submitting: boolean;
  step: number;
  selectedDates: string[];
  postMode: "group" | "separate";
  shifts: QuickShiftDate[];
  currentShiftIndex: number;
  applyTimeForAll: boolean;
  applyRateForAll: boolean;
  groupOnCall: boolean;
  groupOnCallDetails: string;
  specialty: string;
  skillLevel: string;
  supportLevel: string;
  description: string;
  transportIncluded: boolean;
  transportSelected: "variable" | "set" | "other" | "";
  transportType: string;
  transportRate: string;
  transportOtherDetails: string;
  accommodationIncluded: boolean;
  accommodationType: string;
  accommodationOtherDetails: string;
  requestAdditionalDocs: boolean;
  requireAdditionalFiles: boolean;
  editingDateIndex: number;
  reviewSelectedIndex: number;
  additionalDocs: string[];
  specialtyOptions: { value: string; label: string }[];
  skillOptions: { value: string; label: string }[];
  acmdOptions: { value: string; label: string }[];
  isDragging: boolean;
  dragStart: string | null;
}

interface QuickCreateShiftModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: (result?: QuickCreateShiftResult) => void;
  editTemplateMode?: boolean;
  initialTemplateData?: any;
  prefill?: QuickCreateShiftPrefill | null;
  editShiftId?: string | null;
  onRejectAiShift?: () => void | Promise<void>;
}

const TOTAL_STEPS = 5;

function extractCreatedShiftIds(results: unknown): string[] {
  const list = Array.isArray(results) ? results : results ? [results] : [];
  const ids: string[] = [];
  for (const result of list as any[]) {
    if (result?.success === true) {
      const id = result?.shifts?.shiftsId;
      if (typeof id === "string" && id.trim()) {
        ids.push(id.trim());
      }
    }
  }
  return ids;
}

function extractCreatedDays(results: unknown): QuickCreateCreatedDay[] {
  const list = Array.isArray(results) ? results : results ? [results] : [];
  const created: QuickCreateCreatedDay[] = [];
  for (const result of list as any[]) {
    if (result?.success !== true) continue;
    const shift = result?.shifts || {};
    const shiftId = String(shift?.shiftsId || "").trim();
    const days =
      shift?.daysVOList ||
      shift?.shiftsDaysVOList ||
      (shift?.shiftsDaysVO ? [shift.shiftsDaysVO] : []);
    if (!shiftId || !Array.isArray(days)) continue;
    days.forEach((day: any) => {
      const shiftDayId = String(day?.shiftsDaysId || "").trim();
      let date = String(day?.shiftsDays || "").trim();
      if (!date && Number(day?.startTime)) {
        const raw = Number(day.startTime);
        date = dayjs(raw < 1_000_000_000_000 ? raw * 1000 : raw).format(
          "YYYY-MM-DD",
        );
      }
      date = date.replace(/\//g, "-").slice(0, 10);
      if (date && shiftDayId) {
        created.push({ date, shiftId, shiftDayId });
      }
    });
  }
  return created;
}

const PREMIERE_AUDIENCE_OPTIONS: {
  id: ShiftPremiereAudience;
  label: string;
  description: string;
}[] = [
  {
    id: "INTERNAL",
    label: "Internal bank",
    description: "Doctors attached to this hospital — they see it under Jobs → Internal",
  },
  {
    id: "PRE_CREDENTIALED",
    label: "Locum Bank",
    description: "Doctors on the locum / pre-credential bank",
  },
  {
    id: "FAVORITE",
    label: "Favorites",
    description: "Doctors on your favorites list",
  },
  {
    id: "WORKED_BEFORE",
    label: "Worked here before",
    description: "Doctors who completed a shift at your hospital",
  },
];

const STEP_BODY_CLASS =
  "p-4 sm:p-6 flex flex-col h-full min-h-0 overflow-y-auto";
/** Fixed shell height for every step so the dialog does not jump while advancing. */
const MODAL_SHELL_HEIGHT = "min(92vh, 860px)";

function locumWeekDateStrings(dateStr: string): string[] {
  const day = new Date(dateStr.replace(/\//g, "-") + "T00:00:00");
  if (Number.isNaN(day.getTime())) return [dateStr];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return eachDayOfInterval({
    start: startOfWeek(day, { weekStartsOn: 1 }),
    end: addDays(startOfWeek(day, { weekStartsOn: 1 }), 6),
  })
    .filter((date) => date >= today)
    .map((date) => format(date, "yyyy/MM/dd"));
}

const initialState: CreateShiftModalState = {
  open: false,
  loading: false,
  submitting: false,
  step: 1,
  selectedDates: [],
  postMode: "group",
  shifts: [],
  currentShiftIndex: 0,
  applyTimeForAll: true,
  applyRateForAll: true,
  groupOnCall: false,
  groupOnCallDetails: "",
  specialty: "",
  skillLevel: "",
  supportLevel: "",
  description: "",
  transportIncluded: false,
  transportSelected: "",
  transportType: "/km and flights included",
  transportRate: "",
  transportOtherDetails: "",
  accommodationIncluded: false,
  accommodationType: "",
  accommodationOtherDetails: "",
  requestAdditionalDocs: false,
  requireAdditionalFiles: false,
  editingDateIndex: 0,
  reviewSelectedIndex: 0,
  additionalDocs: [""],
  specialtyOptions: [],
  skillOptions: [],
  acmdOptions: [
    { value: "On site", label: "On site" },
    {
      value: "Nearby but organised by hospital",
      label: "Nearby but organised by hospital",
    },
    {
      value: "Locum to organise but funded by hospital",
      label: "Locum to organise but funded by hospital",
    },
    { value: "Other", label: "Other" },
  ],
  isDragging: false,
  dragStart: null,
};

const fieldSelectClass =
  "w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl text-base bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200 hover:border-indigo-300";

const stepPanelClass =
  "rounded-2xl border border-gray-200/80 bg-gradient-to-br from-slate-50 to-gray-100 p-5 shadow-inner";

const stepFieldLabelClass =
  "text-base font-semibold text-gray-700 mb-2.5 flex items-center gap-2";

const stepFieldIconClass =
  "w-8 h-8 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-lg flex items-center justify-center text-base shadow-sm shrink-0";

const SUPPORT_LEVEL_OPTIONS = [
  { value: "Senior on site", label: "Senior on site" },
  { value: "Senior on call", label: "Senior on call" },
  { value: "No seniors available", label: "No seniors available" },
];

const adjustTextareaHeight = (el: HTMLTextAreaElement | null) => {
  if (!el) return;
  el.style.height = "auto";
  el.style.height = `${Math.max(el.scrollHeight + 2, 72)}px`;
};

const parseTemplateClock = (
  raw?: string | null,
): { time: string; ampm: "AM" | "PM" } | null => {
  if (!raw) return null;
  const match = raw.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (!match) return null;
  return {
    time: `${Number(match[1])}:${match[2]}`,
    ampm: match[3].toUpperCase() as "AM" | "PM",
  };
};

const isOfficeDocumentUrl = (url: string) =>
  /(\.doc|\.docx|\.ppt|\.pptx|\.xls|\.xlsx)(?:$|[?#])/i.test(
    url.split("#")[0].split("?")[0],
  );

/** Backend proxy — Office Online cannot fetch private S3 presigned URLs directly. */
const buildDocumentDownloadProxyUrl = (url: string) => {
  const base = String(url || "")
    .trim()
    .split("#")[0]
    .split("?")[0];
  if (!base || !process.env.REACT_APP_URL) return "";
  return `${process.env.REACT_APP_URL}/v1/public/upload/download?url=${encodeURIComponent(base)}`;
};

const buildOfficePreviewUrl = (url: string) => {
  const proxyUrl = buildDocumentDownloadProxyUrl(url);
  if (!proxyUrl) return "";
  return `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(proxyUrl)}`;
};

const QuickCreateShiftModal: React.FC<QuickCreateShiftModalProps> = ({
  open,
  onClose,
  onSuccess,
  prefill,
  editShiftId,
  onRejectAiShift,
}) => {
  const navigate = useNavigate();
  const isEditMode = Boolean(editShiftId);
  const dispatch = useDispatch();
  const { isClinicNetworkExperience } = useGrandmasterExperience();
  const isPostModeLocked = Boolean(prefill?.lockPostMode);
  const hospitalList = useSelector(
    (state: any) => (state.currentHospital?.list || []) as Hospital[],
  );
  const currentHospital = useSelector(
    (state: any) => state.currentHospital?.current as Hospital | null,
  );
  const clinicBrands = useMemo(
    () => clinicNetworkBrands(hospitalList),
    [hospitalList],
  );
  const [selectedBrandId, setSelectedBrandId] = useState<string>("");
  const clinicSites = useMemo(
    () =>
      selectedBrandId
        ? clinicNetworkSitesForBrand(hospitalList, selectedBrandId)
        : clinicNetworkLeafSites(hospitalList),
    [hospitalList, selectedBrandId],
  );
  const selectedBrand = useMemo(
    () => clinicBrands.find((brand) => brand.id === selectedBrandId) || null,
    [clinicBrands, selectedBrandId],
  );
  const [selectedClinicIds, setSelectedClinicIds] = useState<string[]>([]);
  const [clinicQuery, setClinicQuery] = useState("");
  const [brandImageUploadingId, setBrandImageUploadingId] = useState<string | null>(null);
  const [clinicNetworkPickerDone, setClinicNetworkPickerDone] = useState(false);
  const visibleClinicSites = useMemo(() => {
    const sorted = sortClinicsPriorityFirst(clinicSites, selectedBrand?.name);
    const query = clinicQuery.trim().toLowerCase();
    if (!query) return sorted;
    return sorted.filter((site) => {
      const label = `${site.name || ""} ${clinicSiteShortLabel(site.name)}`.toLowerCase();
      return label.includes(query);
    });
  }, [clinicSites, clinicQuery, selectedBrand?.name]);
  const clinicDefaultsForBrandRef = useRef<string | null>(null);
  const brandRefreshAttemptedRef = useRef(false);

  useEffect(() => {
    if (!open) {
      brandRefreshAttemptedRef.current = false;
      return;
    }
    if (!isClinicNetworkExperience || clinicBrands.length > 0) return;
    if (brandRefreshAttemptedRef.current) return;
    brandRefreshAttemptedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const list = await getHospitalsByParentName("", true);
        if (!cancelled && list?.length) {
          dispatch(setHospitals(list));
        }
      } catch {
        // Brand cards stay empty until the hierarchy is available.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isClinicNetworkExperience, clinicBrands.length, dispatch]);

  const [createShiftModal, setCreateShiftModal] =
    useState<CreateShiftModalState>(initialState);

  useEffect(() => {
    if (!open) return;
    const step = createShiftModal.step;
    const stepLabel =
      step === 1
        ? "Dates"
        : step === 2
          ? "Role & eligibility"
          : step === 3
            ? "Transport & extras"
            : step === 4
              ? "Time & rate"
              : "Review";
    const owner = setOperationalFocus({
      pageType: "POST_SHIFT",
      label: `Post shift · ${stepLabel}`,
    });
    return () => releaseOperationalFocus(owner);
  }, [open, createShiftModal.step]);
  const clinicSpecialtyOptions = useMemo(() => {
    const options = createShiftModal.specialtyOptions || [];
    if (!isClinicNetworkExperience) return options;
    const filtered = filterClinicNetworkSpecialties(options);
    return filtered;
  }, [createShiftModal.specialtyOptions, isClinicNetworkExperience]);
  const [acceptCriteria, setAcceptCriteria] = useState<AcceptCriterion[]>([]);
  const [positionsRequired, setPositionsRequired] = useState<number>(1);
  const [eligibilityManualPromptShown, setEligibilityManualPromptShown] =
    useState(false);
  const [eligibilitySuggestionsPromptShown, setEligibilitySuggestionsPromptShown] =
    useState(false);
  const [eligibleRolesDialogOpen, setEligibleRolesDialogOpen] = useState(false);
  const [focusEligibilityManualAdd, setFocusEligibilityManualAdd] =
    useState(false);
  const [premiereEnabled, setPremiereEnabled] = useState(false);
  const [premiereAudiences, setPremiereAudiences] = useState<
    ShiftPremiereAudience[]
  >(["INTERNAL", "PRE_CREDENTIALED", "FAVORITE", "WORKED_BEFORE"]);
  const [premiereDays, setPremiereDays] = useState(7);
  const [releaseMode, setReleaseMode] = useState<"automation" | "premiere">(
    "premiere",
  );
  const [selectedAutomationId, setSelectedAutomationId] = useState("");
  const [remotePostingAutomations, setRemotePostingAutomations] = useState<
    HospitalAutomation[]
  >([]);
  const postingAutomations = useMemo(() => {
    if (remotePostingAutomations.length) return remotePostingAutomations;
    return listPostShiftAutomations(String(currentHospital?.id || ""));
  }, [remotePostingAutomations, currentHospital?.id, open]);
  const selectedPostingAutomation =
    postingAutomations.find((item) => item.id === selectedAutomationId) ||
    postingAutomations[0] ||
    null;

  useEffect(() => {
    if (!open) return;
    listHospitalAutomations()
      .then((items) =>
        setRemotePostingAutomations(
          items.filter((item) => isPostShiftAutomation(item)),
        ),
      )
      .catch(() => setRemotePostingAutomations([]));
  }, [open, currentHospital?.id]);
  useEffect(() => {
    if (!open || !postingAutomations.length) return;
    setSelectedAutomationId((current) => current || postingAutomations[0].id);
  }, [open, postingAutomations]);
  const [postCreateInviteShiftId, setPostCreateInviteShiftId] = useState<
    string | null
  >(null);
  const pendingOnSuccessRef = useRef<
    Parameters<NonNullable<QuickCreateShiftModalProps["onSuccess"]>>[0]
  >();

  const eligibilitySuggestionGroups = useMemo(
    () =>
      suggestAcceptCriteriaGroups({
        anchorSpecialtyId: createShiftModal.specialty,
        anchorSkillId: createShiftModal.skillLevel,
        acceptCriteria,
        specialtyOptions: createShiftModal.specialtyOptions,
        skillOptions: createShiftModal.skillOptions,
      }),
    [
      createShiftModal.specialty,
      createShiftModal.skillLevel,
      createShiftModal.specialtyOptions,
      createShiftModal.skillOptions,
      acceptCriteria,
    ],
  );

  const eligibilityRows = useMemo(
    () =>
      buildEligibilityRows(
        createShiftModal.specialty,
        createShiftModal.skillLevel,
        acceptCriteria,
      ),
    [createShiftModal.specialty, createShiftModal.skillLevel, acceptCriteria],
  );

  const aiMappedFieldAlerts = useMemo(
    () =>
      getAiMappedFieldAlerts({
        fieldSources: prefill?.fieldSources,
        specialtyOptions: createShiftModal.specialtyOptions,
        skillOptions: createShiftModal.skillOptions,
        specialtyId: createShiftModal.specialty,
        skillLevelId: createShiftModal.skillLevel,
      }),
    [
      prefill?.fieldSources,
      createShiftModal.specialtyOptions,
      createShiftModal.skillOptions,
      createShiftModal.specialty,
      createShiftModal.skillLevel,
    ],
  );

  const emailEligibleSuggestions = useMemo(
    () =>
      suggestEmailEligibleRoles({
        primarySpecialtyId: createShiftModal.specialty,
        primarySkillId: createShiftModal.skillLevel,
        specialtyAlternatives: prefill?.specialtyAlternatives,
        skillLevelAlternatives: prefill?.skillLevelAlternatives,
        acceptCriteria,
        specialtyOptions: createShiftModal.specialtyOptions,
        skillOptions: createShiftModal.skillOptions,
      }),
    [
      createShiftModal.specialty,
      createShiftModal.skillLevel,
      prefill?.specialtyAlternatives,
      prefill?.skillLevelAlternatives,
      acceptCriteria,
      createShiftModal.specialtyOptions,
      createShiftModal.skillOptions,
    ],
  );

  const pendingEligibleSuggestions = useMemo(
    () =>
      mergeEligibleSuggestions({
        emailSuggestions: emailEligibleSuggestions,
        suggestionGroups: eligibilitySuggestionGroups,
        acceptCriteria,
        primarySpecialtyId: createShiftModal.specialty,
        primarySkillId: createShiftModal.skillLevel,
      }),
    [
      emailEligibleSuggestions,
      eligibilitySuggestionGroups,
      acceptCriteria,
      createShiftModal.specialty,
      createShiftModal.skillLevel,
    ],
  );

  const specialtyFieldMapped = aiMappedFieldAlerts.some(
    (row) => row.fieldKey === "specialty",
  );
  const skillFieldMapped = aiMappedFieldAlerts.some(
    (row) => row.fieldKey === "minimum_skill_level",
  );
  const mappedFieldHighlightClass =
    "ring-2 ring-amber-300/80 border-amber-300 bg-amber-50/30";

  const isAiReview = Boolean(prefill?.fieldSources);
  const showRosterUpload = !isEditMode && !isAiReview && !isClinicNetworkExperience;
  const [publishHospitalId, setPublishHospitalId] = useState("");
  const [rejectingAiShift, setRejectingAiShift] = useState(false);
  const publishHospitalOptions = prefill?.publishHospitalOptions || [];
  const showPublishHospitalPicker =
    isAiReview && publishHospitalOptions.length > 0;
  const [modalCalendarDate, setModalCalendarDate] = useState<Date>(new Date());
  const [dragMode, setDragMode] = useState<"select" | "deselect" | null>(null);
  const dateDragRef = useRef<{
    active: boolean;
    mode: "select" | "deselect";
    start: string;
    moved: boolean;
  } | null>(null);
  const selectedDatesRef = useRef<string[]>([]);
  selectedDatesRef.current = createShiftModal.selectedDates;
  const [rosterUploadLoading, setRosterUploadLoading] = useState(false);
  const [rosterDragActive, setRosterDragActive] = useState(false);
  const [rosterSplitByEligibility, setRosterSplitByEligibility] = useState(false);
  /** Pick dates on calendar OR send roster/email to AI — not both at once. */
  const [createInputMode, setCreateInputMode] = useState<
    "calendar" | "ai_import" | null
  >(null);
  const [showEmailPaste, setShowEmailPaste] = useState(false);
  const [emailPasteText, setEmailPasteText] = useState("");
  const [emailPasteHtml, setEmailPasteHtml] = useState("");
  const [emailPasteLoading, setEmailPasteLoading] = useState(false);
  const [emailPasteReading, setEmailPasteReading] = useState(false);
  const rosterFileInputRef = useRef<HTMLInputElement>(null);
  const emailPasteTextareaRef = useRef<HTMLTextAreaElement>(null);
  const calendarLocked = isClinicNetworkExperience
    ? false
    : createInputMode === "ai_import" ||
      rosterUploadLoading ||
      emailPasteLoading ||
      Boolean(emailPasteText.trim());
  const aiImportLocked =
    createInputMode === "calendar" ||
    createShiftModal.shifts.length > 0 ||
    rosterUploadLoading ||
    emailPasteLoading;
  const showCalendarPanel =
    (!showRosterUpload || createInputMode !== "ai_import") &&
    (!isClinicNetworkExperience || clinicNetworkPickerDone || isEditMode);
  const showAiImportPanel =
    showRosterUpload && createInputMode !== "calendar";

  const applyEmailPasteFromClipboard = useCallback(
    (data: DataTransfer | null | undefined) => {
      const { text, html } = extractEmailPasteFromClipboard(data);
      if (!text.trim() && !html.trim()) return false;
      setShowEmailPaste(true);
      setEmailPasteText(text);
      setEmailPasteHtml(html);
      setCreateInputMode("ai_import");
      return true;
    },
    [],
  );

  const pasteEmailFromSystemClipboard = useCallback(async () => {
    if (aiImportLocked) {
      message.info("Clear selected dates before pasting an email");
      return false;
    }
    setEmailPasteReading(true);
    try {
      const { text, html } = await readEmailFromSystemClipboard();
      if (!text.trim() && !html.trim()) {
        message.info("Copy a shift email from Gmail first, then click to paste");
        return false;
      }
      setShowEmailPaste(true);
      setEmailPasteText(text);
      setEmailPasteHtml(html);
      setCreateInputMode("ai_import");
      return true;
    } catch {
      message.warning(
        "Could not read your clipboard. Allow clipboard access in the browser, or use Ctrl+V.",
      );
      return false;
    } finally {
      setEmailPasteReading(false);
    }
  }, [aiImportLocked]);

  const openEmailPastePanel = useCallback(async () => {
    if (aiImportLocked) return;
    setCreateInputMode("ai_import");
    setShowEmailPaste(true);
    await pasteEmailFromSystemClipboard();
  }, [aiImportLocked, pasteEmailFromSystemClipboard]);

  const cancelEmailPaste = useCallback(() => {
    if (emailPasteLoading) return;
    setShowEmailPaste(false);
    setEmailPasteText("");
    setEmailPasteHtml("");
    if (createShiftModal.shifts.length === 0) {
      setCreateInputMode(null);
    }
  }, [createShiftModal.shifts.length, emailPasteLoading]);

  const replaceEmailPaste = useCallback(() => {
    if (emailPasteLoading) return;
    setEmailPasteText("");
    setEmailPasteHtml("");
    requestAnimationFrame(() => emailPasteTextareaRef.current?.focus());
  }, [emailPasteLoading]);

  useEffect(() => {
    if (!showEmailPaste || emailPasteText.trim() || emailPasteHtml.trim()) {
      return;
    }
    const frame = requestAnimationFrame(() => {
      emailPasteTextareaRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [showEmailPaste, emailPasteText, emailPasteHtml]);

  useEffect(() => {
    if (!open || !isAiReview) return;
    const defaultId =
      prefill?.publishHospitalId ||
      prefill?.publishHospitalOptions?.[0]?.id ||
      "";
    setPublishHospitalId(defaultId);
  }, [open, isAiReview, prefill?.publishHospitalId, prefill?.publishHospitalOptions]);

  useEffect(() => {
    if (!open) {
      setSelectedClinicIds([]);
      setClinicQuery("");
      setSelectedBrandId("");
      clinicDefaultsForBrandRef.current = null;
      setClinicNetworkPickerDone(false);
      setPremiereEnabled(false);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedBrandId) return;
    setSelectedClinicIds((prev) =>
      prev.filter((id) => clinicSites.some((site) => site.id === id)),
    );
  }, [selectedBrandId, clinicSites]);

  useEffect(() => {
    if (!open || !isClinicNetworkExperience || !selectedBrandId) return;
    const specialtyOptions = createShiftModal.specialtyOptions;
    const skillOptions = createShiftModal.skillOptions;
    if (!specialtyOptions.length || !skillOptions.length) return;
    if (clinicDefaultsForBrandRef.current === selectedBrandId) return;
    clinicDefaultsForBrandRef.current = selectedBrandId;
    const specialty = pickClinicNetworkSpecialty(specialtyOptions);
    const skill = pickClinicNetworkSkill(skillOptions);
    const rate = defaultContractedRateForBrand(selectedBrand?.name);
    const description = locumDescriptionForBrand(selectedBrand?.name);
    setCreateShiftModal((prev) => ({
      ...prev,
      specialty: specialty || prev.specialty,
      skillLevel: skill || prev.skillLevel,
      description,
      postMode: "group",
      shifts: prev.shifts.map((shift) => ({
        ...shift,
        startTime: shift.startTime || "8:00",
        startAMPM: shift.startAMPM || "AM",
        endTime: shift.endTime || "5:00",
        endAMPM: shift.endAMPM || "PM",
        rate,
        rateType: "contracted",
      })),
    }));
    setSelectedClinicIds([]);
  }, [
    open,
    isClinicNetworkExperience,
    selectedBrandId,
    selectedBrand?.name,
    createShiftModal.specialtyOptions,
    createShiftModal.skillOptions,
    clinicSites,
  ]);

  const [showApplyPulse, setShowApplyPulse] = useState<{
    time: boolean;
    rate: boolean;
    onCall: boolean;
  }>({ time: false, rate: false, onCall: false });

  // Template handling states
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [applyingTemplateId, setApplyingTemplateId] = useState<string | null>(
    null,
  );
  const [templateName, setTemplateName] = useState("");
  const [isDocumentRailCollapsed, setIsDocumentRailCollapsed] = useState(false);
  const [
    selectedPrefillDocumentPreviewUrl,
    setSelectedPrefillDocumentPreviewUrl,
  ] = useState("");
  const [isDocumentPreviewLoading, setIsDocumentPreviewLoading] =
    useState(false);
  const prefillDocumentObjectUrlRef = React.useRef<string | null>(null);

  // Hospital account contact info – fetched once on open, used as fallback in API payloads
  const hospitalContactRef = useRef({ email: "", name: "", phone: "" });
  const descriptionTextareaRef = useRef<HTMLTextAreaElement>(null);
  const transportDetailsRef = useRef<HTMLTextAreaElement>(null);
  const accommodationDetailsRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (!open) return;
    if (createShiftModal.step === 3) {
      adjustTextareaHeight(transportDetailsRef.current);
      adjustTextareaHeight(accommodationDetailsRef.current);
    }
  }, [
    open,
    createShiftModal.step,
    createShiftModal.transportOtherDetails,
    createShiftModal.accommodationOtherDetails,
    createShiftModal.transportIncluded,
    createShiftModal.accommodationIncluded,
    createShiftModal.transportSelected,
    createShiftModal.accommodationType,
  ]);
  useEffect(() => {
    if (!open) return;
    apiAccount.actInfo({
      callback: (res: any) => {
        if (res?.data?.code === 200) {
          const raw = res.data.data;
          hospitalContactRef.current = {
            email: raw.email || "",
            name: raw.adminName || "",
            phone: raw.mobileNumber || "",
          };
        }
      },
    });
  }, [open]);
  const prefillDocumentUrls = Array.isArray(prefill?.documentUrls)
    ? (prefill?.documentUrls ?? [])
    : [];
  /** Reference doc rail — AI validate/review only (not when hospital just uploaded a roster). */
  const hasPrefillDocuments = prefillDocumentUrls.length > 0;
  const selectedPrefillDocumentUrl = prefillDocumentUrls[0] || "";
  const selectedPrefillDocumentDownloadUrl =
    buildDocumentDownloadProxyUrl(selectedPrefillDocumentUrl) ||
    selectedPrefillDocumentUrl;
  const originalPrefillDates = React.useMemo(
    () =>
      Array.isArray(prefill?.shifts)
        ? prefill!.shifts
            .map((shift) => {
              const raw = String(shift?.date || "").trim();
              if (!raw) return "";
              const strictParsed = dayjs(
                raw,
                ["YYYY/MM/DD", "YYYY-MM-DD"],
                true,
              );
              if (strictParsed.isValid()) {
                return strictParsed.format("YYYY/MM/DD");
              }
              const fallbackParsed = dayjs(raw);
              return fallbackParsed.isValid()
                ? fallbackParsed.format("YYYY/MM/DD")
                : "";
            })
            .filter(Boolean)
        : [],
    [prefill],
  );

  useEffect(() => {
    if (prefillDocumentObjectUrlRef.current) {
      URL.revokeObjectURL(prefillDocumentObjectUrlRef.current);
      prefillDocumentObjectUrlRef.current = null;
    }

    const normalizedUrl = String(selectedPrefillDocumentUrl || "").trim();
    if (!normalizedUrl) {
      setSelectedPrefillDocumentPreviewUrl("");
      setIsDocumentPreviewLoading(false);
      return;
    }

    const safeUrl = normalizedUrl.split("#")[0].split("?")[0].toLowerCase();
    const isOfficeDoc = isOfficeDocumentUrl(normalizedUrl);
    const downloadProxyUrl = buildDocumentDownloadProxyUrl(normalizedUrl);

    if (isOfficeDoc && downloadProxyUrl) {
      setSelectedPrefillDocumentPreviewUrl(
        buildOfficePreviewUrl(normalizedUrl),
      );
      setIsDocumentPreviewLoading(false);
      return;
    }

    const getMimeType = () => {
      if (safeUrl.endsWith(".pdf")) return "application/pdf";
      if (safeUrl.endsWith(".png")) return "image/png";
      if (safeUrl.endsWith(".jpg") || safeUrl.endsWith(".jpeg")) {
        return "image/jpeg";
      }
      if (safeUrl.endsWith(".gif")) return "image/gif";
      if (safeUrl.endsWith(".webp")) return "image/webp";
      if (safeUrl.endsWith(".svg")) return "image/svg+xml";
      if (safeUrl.endsWith(".html") || safeUrl.endsWith(".htm")) {
        return "text/html;charset=utf-8";
      }
      if (safeUrl.endsWith(".txt")) return "text/plain;charset=utf-8";
      return "application/octet-stream";
    };

    setIsDocumentPreviewLoading(true);
    const tokenHeader = {
      "doctor-admin-token": localStorage.getItem("token-name") || "",
    };
    const proxyCandidates = [
      downloadProxyUrl,
      `${process.env.REACT_APP_URL}/v1/public/upload/download?url=${encodeURIComponent(normalizedUrl)}`,
      normalizedUrl,
    ].filter(Boolean);

    const tryFetch = async (index: number): Promise<void> => {
      if (index >= proxyCandidates.length) {
        setSelectedPrefillDocumentPreviewUrl("");
        setIsDocumentPreviewLoading(false);
        return;
      }

      try {
        const response = await axios.get(proxyCandidates[index], {
          headers: tokenHeader,
          responseType: "arraybuffer",
        });
        const contentType = String(
          response?.headers?.["content-type"] || "",
        ).toLowerCase();
        const mimeType = getMimeType();
        if (
          contentType.includes("text/html") ||
          contentType.includes("text/plain") ||
          mimeType.startsWith("text/html") ||
          mimeType.startsWith("text/plain")
        ) {
          const blob = new Blob([response.data], { type: mimeType });
          const blobUrl = URL.createObjectURL(blob);
          prefillDocumentObjectUrlRef.current = blobUrl;
          setSelectedPrefillDocumentPreviewUrl(blobUrl);
          setIsDocumentPreviewLoading(false);
          return;
        }
        if (contentType.includes("application/json")) {
          await tryFetch(index + 1);
          return;
        }
        const blob = new Blob([response.data], { type: getMimeType() });
        const blobUrl = URL.createObjectURL(blob);
        prefillDocumentObjectUrlRef.current = blobUrl;
        setSelectedPrefillDocumentPreviewUrl(blobUrl);
        setIsDocumentPreviewLoading(false);
      } catch {
        await tryFetch(index + 1);
      }
    };

    void tryFetch(0);
  }, [selectedPrefillDocumentUrl]);

  useEffect(() => {
    return () => {
      if (prefillDocumentObjectUrlRef.current) {
        URL.revokeObjectURL(prefillDocumentObjectUrlRef.current);
        prefillDocumentObjectUrlRef.current = null;
      }
    };
  }, []);

  const resolveOptionValue = (
    options: { value: string; label: string }[],
    rawValue: string,
  ) => {
    const normalized = String(rawValue || "")
      .trim()
      .toLowerCase();
    if (!normalized) return "";

    const exactValue = options.find(
      (option) => String(option.value).toLowerCase() === normalized,
    );
    if (exactValue) return exactValue.value;

    const exactLabel = options.find(
      (option) => String(option.label).trim().toLowerCase() === normalized,
    );
    if (exactLabel) return exactLabel.value;

    return "";
  };

  const resolveEligibilityForShift = (
    shift: QuickShiftDate,
    fallback: { specialty: string; skillLevel: string },
    specialtyOptions: { value: string; label: string }[],
    skillOptions: { value: string; label: string }[],
  ) => {
    const specRaw = String(shift.specialty || fallback.specialty || "").trim();
    const skillRaw = String(shift.skillLevel || fallback.skillLevel || "").trim();
    return {
      specialty:
        resolveOptionValue(specialtyOptions, specRaw) || specRaw || fallback.specialty,
      skillLevel:
        resolveOptionValue(skillOptions, skillRaw) || skillRaw || fallback.skillLevel,
    };
  };

  const normalizeDateForShift = (value?: string) => {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const strictParsed = dayjs(raw, ["YYYY/MM/DD", "YYYY-MM-DD"], true);
    if (strictParsed.isValid()) return strictParsed.format("YYYY/MM/DD");

    const fallbackParsed = dayjs(raw);
    if (!fallbackParsed.isValid()) return "";
    return fallbackParsed.format("YYYY/MM/DD");
  };

  const parseTimeForShift = (
    value?: string,
    fallbackAmPm: "AM" | "PM" = "AM",
  ) => {
    const raw = String(value || "").trim();
    if (!raw) {
      return { time: "", ampm: fallbackAmPm };
    }

    const parsed = dayjs(raw, ["HH:mm:ss", "HH:mm", "h:mm A", "h:mmA"], true);
    if (parsed.isValid()) {
      return {
        time: parsed.format("h:mm"),
        ampm: parsed.format("A") as "AM" | "PM",
      };
    }

    const simpleTime = raw.match(/^(\d{1,2}):(\d{2})$/);
    if (simpleTime) {
      let hour = Number(simpleTime[1]);
      const minute = simpleTime[2];
      if (!Number.isNaN(hour) && hour >= 0 && hour <= 23) {
        const ampm: "AM" | "PM" = hour >= 12 ? "PM" : "AM";
        hour = hour % 12 || 12;
        return { time: `${hour}:${minute}`, ampm };
      }
    }

    return { time: raw, ampm: fallbackAmPm };
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const res = await getTemplates();
      const err = getTemplateListError(res);
      if (err) {
        setAvailableTemplates([]);
        message.error(err);
        return;
      }
      setAvailableTemplates(parseTemplateListResponse(res));
    } catch (err) {
      console.error("Error fetching templates", err);
      setAvailableTemplates([]);
      message.error("Failed to load templates. Check you are logged in as the hospital account.");
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    if (open && !isEditMode) {
      loadTemplates();
    }
  }, [open, isEditMode]);

  useEffect(() => {
    if (showApplyTemplate) {
      loadTemplates();
    }
  }, [showApplyTemplate]);

  const handleApplyTemplate = (templateData: any) => {
    setApplyingTemplateId(templateData?.templateId || null);

    let transportSelected = "";
    let transportRate = "";
    let transportOtherDetails = "";
    let transportType = "/km and flights included";

    if (templateData.travelFlag === 1) {
      if (templateData.travelType === "Variable") {
        transportSelected = "variable";
        transportRate = templateData.travelDetails || "";
        transportType =
          templateData.fightsIncludedFlag === 1
            ? "/km and flights included"
            : "/km no flights included";
      } else if (templateData.travelType === "Fixed rate") {
        transportSelected = "set";
        transportRate = templateData.travelDetails || "";
      } else if (templateData.travelType === "Other") {
        transportSelected = "other";
        transportOtherDetails = templateData.travelDetails || "";
      }
    }

    let accommodationType = templateData.accommodationType || "";
    let accommodationOtherDetails = "";
    if (accommodationType === "Other") {
      accommodationOtherDetails = templateData.accommodation || "";
    }

    const additionalFiles = filterExtraDocumentsOnly(
      Array.isArray(templateData.additionalFilesRequired)
        ? templateData.additionalFilesRequired
        : [],
    );
    const requireFiles = additionalFiles.length > 0;

    setCreateShiftModal((prev) => ({
      ...prev,
      specialty: templateData.specialty || "",
      skillLevel: templateData.skillLevel || "", // keep correct API ID
      supportLevel: templateData.supportLevel || "",
      description: templateData.description || "",

      transportIncluded: templateData.travelFlag === 1,
      transportSelected: transportSelected as any,
      transportRate: transportRate,
      transportOtherDetails: transportOtherDetails,
      transportType: transportType,

      accommodationIncluded: templateData.accommodationFlag === 1,
      accommodationType: accommodationType,
      accommodationOtherDetails: accommodationOtherDetails,

      requireAdditionalFiles: requireFiles,
      additionalDocs: requireFiles ? additionalFiles : [""],
    }));

    const templateCriteria = Array.isArray(templateData.acceptCriteria)
      ? templateData.acceptCriteria.filter(
          (row: { specialtyId?: string; minSkillLevelId?: string }) =>
            row?.specialtyId && row?.minSkillLevelId,
        )
      : [];
    setAcceptCriteria(dedupeAcceptCriteria(templateCriteria));
    setPositionsRequired(
      templateData.positionsRequired != null && templateData.positionsRequired > 0
        ? templateData.positionsRequired
        : 1,
    );

    const startParsed = parseTemplateClock(templateData.startTime);
    const endParsed = parseTemplateClock(templateData.endTime);
    const templateOnCall = Boolean(templateData.onCallFlag);
    const templateOnCallDetails = templateData.onCallDetails || "";
    const templateRate = templateData.defaultRate
      ? String(templateData.defaultRate)
      : "";
    const templateRateType =
      templateData.rateType === "contracted" ? "contracted" : "perHour";

    setCreateShiftModal((prev) => ({
      ...prev,
      groupOnCall: templateOnCall,
      groupOnCallDetails: templateOnCallDetails,
      shifts: prev.shifts.map((shift) => ({
        ...shift,
        ...(startParsed
          ? { startTime: startParsed.time, startAMPM: startParsed.ampm }
          : {}),
        ...(endParsed
          ? { endTime: endParsed.time, endAMPM: endParsed.ampm }
          : {}),
        ...(templateRate
          ? { rate: templateRate, rateType: templateRateType }
          : {}),
        ...(templateOnCall
          ? {
              onCall: true,
              onCallDetails: templateOnCallDetails,
            }
          : {}),
      })),
    }));

    setApplyingTemplateId(null);
    setShowApplyTemplate(false);
    message.success("Template applied!");
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      message.error("Please enter a template name");
      return;
    }

    let travelType = null;
    let travelDetails = null;
    let fightsIncludedFlag = 0;

    if (createShiftModal.transportIncluded) {
      if (createShiftModal.transportSelected === "variable") {
        travelType = "Variable";
        travelDetails = createShiftModal.transportRate;
        fightsIncludedFlag =
          createShiftModal.transportType === "/km and flights included" ? 1 : 0;
      } else if (createShiftModal.transportSelected === "set") {
        travelType = "Fixed rate";
        travelDetails = createShiftModal.transportRate;
      } else if (createShiftModal.transportSelected === "other") {
        travelType = "Other";
        travelDetails = createShiftModal.transportOtherDetails;
      }
    }

    let accommodationType = null;
    let accommodation = null;
    if (createShiftModal.accommodationIncluded) {
      accommodationType = createShiftModal.accommodationType;
      if (accommodationType === "Other") {
        accommodation = createShiftModal.accommodationOtherDetails;
      }
    }

    const additionalFiles = createShiftModal.requireAdditionalFiles
      ? filterExtraDocumentsOnly(
          createShiftModal.additionalDocs.filter((d: string) => d.trim() !== ""),
        )
      : [];

    try {
      const criteriaPayload = excludePrimaryFromAcceptCriteria(
        dedupeAcceptCriteria(acceptCriteria),
        createShiftModal.specialty,
        createShiftModal.skillLevel,
      );
      const firstShift = createShiftModal.shifts?.[0];
      const templateOnCall =
        createShiftModal.groupOnCall || createShiftModal.shifts.some((s) => s.onCall);
      const payload = {
        templateName: templateName.trim(),
        specialty: createShiftModal.specialty || null,
        skillLevel: createShiftModal.skillLevel || null,
        supportLevel: createShiftModal.supportLevel || null,
        description: createShiftModal.description || "",
        travelFlag: createShiftModal.transportIncluded ? 1 : 0,
        travelType: travelType,
        travelDetails: travelDetails,
        fightsIncludedFlag: fightsIncludedFlag,
        accommodationFlag: createShiftModal.accommodationIncluded ? 1 : 0,
        accommodationType: accommodationType,
        accommodation: accommodation,
        additionalFilesRequired: additionalFiles,
        onCallFlag: templateOnCall,
        onCallDetails: createShiftModal.groupOnCall
          ? createShiftModal.groupOnCallDetails
          : createShiftModal.shifts.find((s) => s.onCall)?.onCallDetails || "",
        startTime: firstShift?.startTime
          ? `${firstShift.startTime}${firstShift.startAMPM || ""}`
          : undefined,
        endTime: firstShift?.endTime
          ? `${firstShift.endTime}${firstShift.endAMPM || ""}`
          : undefined,
        defaultRate: firstShift?.rate || undefined,
        rateType: firstShift?.rateType || "perHour",
        ...(positionsRequired > 0 ? { positionsRequired } : {}),
        ...(criteriaPayload.length ? { acceptCriteria: criteriaPayload } : {}),
      };

      const res = await createTemplate(payload as any);
      if (res?.code === 200 || res?.code === 0) {
        message.success("Template saved successfully!");
        setShowSaveDialog(false);
        setTemplateName("");
        await loadTemplates();
      } else {
        message.error(res?.message || "Failed to save template");
      }
    } catch (err) {
      message.error("An error occurred while saving the template");
    }
  };

  // Modal calendar days calculation
  const modalMonthStart = startOfMonth(modalCalendarDate);
  const modalMonthEnd = endOfMonth(modalCalendarDate);
  const modalCalendarStart = startOfWeek(modalMonthStart, { weekStartsOn: 0 });
  const modalCalendarEnd = addDays(
    startOfWeek(modalMonthEnd, { weekStartsOn: 0 }),
    6,
  );
  const modalAllDays = eachDayOfInterval({
    start: modalCalendarStart,
    end: modalCalendarEnd,
  });

  const naming = useSkillLevelNamingOptional();

  // Fetch options when modal opens
  useEffect(() => {
    if (open) {
      if (prefill?.shifts?.length) {
        const mappedShifts = prefill.shifts
          .map((shift, index) => {
            const normalizedDate = normalizeDateForShift(shift.date);
            if (!normalizedDate) return null;

            const parsedStart = parseTimeForShift(
              shift.startTime,
              index === 0 ? "AM" : "AM",
            );
            const parsedEnd = parseTimeForShift(
              shift.endTime,
              parsedStart.ampm === "AM" ? "PM" : parsedStart.ampm,
            );

            return {
              date: normalizedDate,
              displayDate: format(
                new Date(normalizedDate.replace(/\//g, "-")),
                "EEE, MMM d, yyyy",
              ),
              shiftsDaysId: shift.shiftsDaysId,
              startTime: parsedStart.time,
              startAMPM: parsedStart.ampm,
              endTime: parsedEnd.time,
              endAMPM: parsedEnd.ampm,
              rate:
                shift.rate === null || shift.rate === undefined
                  ? ""
                  : String(shift.rate),
              rateType: shift.rateType || "perHour",
              onCall: Boolean(shift.onCall),
              onCallDetails: String(shift.onCallDetails || ""),
              specialty: String(shift.specialty || "").trim() || undefined,
              skillLevel: String(shift.skillLevel || "").trim() || undefined,
              label: String(shift.label || "").trim() || undefined,
            };
          })
          .filter(Boolean) as QuickShiftDate[];

        const sortedShifts = mappedShifts.sort((a, b) =>
          a.date.localeCompare(b.date),
        );
        const selectedDates = Array.from(
          new Set(sortedShifts.map((shift) => shift.date)),
        );
        const firstShift = sortedShifts[0];
        const firstShiftDate = firstShift?.date
          ? dayjs(firstShift.date, ["YYYY/MM/DD", "YYYY-MM-DD"], true)
          : null;

        const travelText = String(prefill.travel || "").trim();
        const accommodationText = String(prefill.accommodation || "").trim();
        const additionalFiles = filterExtraDocumentsOnly(
          Array.isArray(prefill.additionalFilesRequired)
            ? prefill.additionalFilesRequired.filter(Boolean)
            : [],
        );
        const transportIncluded =
          typeof prefill.transportIncluded === "boolean"
            ? prefill.transportIncluded
            : Boolean(travelText);
        const accommodationIncluded =
          typeof prefill.accommodationIncluded === "boolean"
            ? prefill.accommodationIncluded
            : Boolean(accommodationText);

        setCreateShiftModal((prev) => ({
          ...prev,
          open: true,
          loading: false,
          step: prefill?.fieldSources ? 2 : 1,
          selectedDates,
          shifts: sortedShifts,
          postMode:
            prefill.postMode ||
            (sortedShifts.length > 1 ? "group" : "separate"),
          currentShiftIndex: 0,
          applyTimeForAll: false,
          applyRateForAll: false,
          groupOnCall: Boolean(firstShift?.onCall),
          groupOnCallDetails: firstShift?.onCallDetails || "",
          specialty: String(prefill.specialty || "").trim(),
          skillLevel: String(prefill.skillLevel || "").trim(),
          supportLevel: String(prefill.supportLevel || "").trim(),
          description: String(prefill.description || "").trim(),
          transportIncluded,
          transportSelected:
            prefill.transportSelected ?? (travelText ? "other" : ""),
          transportType:
            String(prefill.transportType || "").trim() ||
            "/km and flights included",
          transportRate: String(prefill.transportRate || "").trim(),
          transportOtherDetails: String(
            prefill.transportOtherDetails || travelText,
          ).trim(),
          accommodationIncluded,
          accommodationType:
            String(prefill.accommodationType || "").trim() ||
            (accommodationText ? "Other" : ""),
          accommodationOtherDetails: String(
            prefill.accommodationOtherDetails || accommodationText,
          ).trim(),
          requestAdditionalDocs: additionalFiles.length > 0,
          requireAdditionalFiles: additionalFiles.length > 0,
          additionalDocs: additionalFiles.length ? additionalFiles : [""],
          editingDateIndex: 0,
          reviewSelectedIndex: 0,
        }));

        if (firstShiftDate?.isValid()) {
          setModalCalendarDate(firstShiftDate.toDate());
        }

        const prefillPremiereAudiences = prefill.premiereAudiences || [];
        setPremiereEnabled(
          prefill.premiereEnabled ??
            (prefillPremiereAudiences.length > 0),
        );
        setPremiereAudiences(
          prefillPremiereAudiences.length
            ? prefillPremiereAudiences
            : ["INTERNAL", "PRE_CREDENTIALED", "FAVORITE", "WORKED_BEFORE"],
        );
        setPremiereDays(prefill.premiereDays || 7);
      } else if (!isEditMode) {
        setCreateShiftModal({
          ...initialState,
          open: true,
          loading: false,
        });
        setCreateInputMode(null);
        setShowEmailPaste(false);
        setEmailPasteText("");
        setEmailPasteHtml("");
        setEmailPasteLoading(false);
        setEmailPasteReading(false);
      } else {
        setCreateShiftModal((prev) => ({
          ...prev,
          open: true,
          loading: false,
        }));
      }

      setIsDocumentRailCollapsed(false);

      Promise.all([
        new Promise<any>((resolve) => {
          api.getList({
            params: { type: "specialties" },
            callback: (res: any) => resolve(res),
          });
        }),
        new Promise<any>((resolve) => {
          api.getList({
            params: { type: "skill" },
            callback: (res: any) => resolve(res),
          });
        }),
      ])
        .then(([specialtyRes, skillRes]) => {
          const specialtyOptions =
            specialtyRes?.data?.code === 200
              ? specialtyRes.data.data.map((s: any) => ({
                  value: s.sysId,
                  label: s.sysName,
                }))
              : [];
          const skillOptions =
            skillRes?.data?.code === 200
              ? buildSkillOptionsFromCatalog(
                  skillRes.data.data,
                  naming?.namingState || "DEFAULT",
                )
              : [];

          const resolvedSpecialty = prefill?.specialty
            ? resolveOptionValue(specialtyOptions, prefill.specialty)
            : "";
          const resolvedSkill = prefill?.skillLevel
            ? resolveOptionValue(skillOptions, prefill.skillLevel)
            : "";

          setCreateShiftModal((prev) => ({
            ...prev,
            specialtyOptions,
            skillOptions,
            specialty: resolvedSpecialty || prev.specialty,
            skillLevel: resolvedSkill || prev.skillLevel,
          }));

          setAcceptCriteria(
            buildAcceptCriteriaFromPrefill({
              primarySpecialtyId: resolvedSpecialty,
              primarySkillId: resolvedSkill,
              specialtyAlternatives: prefill?.specialtyAlternatives,
              skillLevelAlternatives: prefill?.skillLevelAlternatives,
              acceptCriteria: prefill?.acceptCriteria,
              specialtyOptions,
              skillOptions,
            }),
          );
          setPositionsRequired(
            prefill?.positionsRequired != null && prefill.positionsRequired > 0
              ? prefill.positionsRequired
              : 1,
          );
        })
        .catch((error) => {
          console.error("Failed to load options:", error);
          message.error("Failed to load shift options");
        });
    }
  }, [open, prefill, naming?.namingState]);

  const addedDates = createShiftModal.selectedDates.filter(
    (date) => !originalPrefillDates.includes(date),
  );
  const removedDates = originalPrefillDates.filter(
    (date) => !createShiftModal.selectedDates.includes(date),
  );

  const clinicNetworkDefaultsRef = useRef({
    enabled: false,
    rate: "",
  });
  clinicNetworkDefaultsRef.current = {
    enabled: isClinicNetworkExperience,
    rate: defaultContractedRateForBrand(selectedBrand?.name),
  };

  const handleBrandImageUpload = useCallback(
    async (brandId: string, file: File, kind: "logo" | "cover" = "cover") => {
      if (!brandId || !file) return;
      setBrandImageUploadingId(brandId);
      try {
        const formData = new FormData();
        formData.append("file", file);
        const uploaded = await new Promise<{ key: string; preview: string }>(
          (resolve, reject) => {
            apiCommon.uploadApi({
              params: formData,
              callback: (res: any) => {
                if (!res?.data || res.data.code !== 200 || !res.data.data) {
                  reject(new Error(res?.data?.message || "Upload failed"));
                  return;
                }
                const key = String(res.data.data);
                resolve({
                  key,
                  preview: String(res.data.previewUrl || key),
                });
              },
            });
          },
        );
        const persisted = await updateChildHospitalImage(
          brandId,
          kind === "cover"
            ? { hospitalBackground: uploaded.key }
            : { hospitalImage: uploaded.key },
        );
        const nextImage =
          (kind === "cover"
            ? persisted.hospitalBackground
            : persisted.hospitalImage) || uploaded.preview;
        const siteIds = new Set(
          clinicNetworkSitesForBrand(hospitalList, brandId).map((site) => site.id),
        );
        dispatch(
          setHospitals(
            hospitalList.map((hospital) => {
              if (hospital.id !== brandId && !siteIds.has(hospital.id)) {
                return hospital;
              }
              return kind === "cover"
                ? { ...hospital, hospitalBackground: nextImage }
                : { ...hospital, hospitalImage: nextImage };
            }),
          ),
        );
        message.success(kind === "cover" ? "Cover image saved" : "Brand logo saved");
      } catch (error: any) {
        message.error(error?.message || "Could not save that photo");
      } finally {
        setBrandImageUploadingId(null);
      }
    },
    [dispatch, hospitalList],
  );

  const buildInheritedShift = useCallback(
    (prev: CreateShiftModalState, dateStr: string): QuickShiftDate => {
      const displayDate = format(
        new Date(dateStr.replace(/\//g, "-")),
        "EEE, MMM d, yyyy",
      );
      const firstShift = prev.shifts[0];
      const useGroupSettings =
        prev.postMode === "group" && prev.shifts.length > 0;
      const clinic = clinicNetworkDefaultsRef.current;
      const inheritTime =
        useGroupSettings || Boolean(prev.applyTimeForAll && firstShift);
      const inheritRate =
        useGroupSettings || Boolean(prev.applyRateForAll && firstShift);
      const fallbackStart = clinic.enabled ? "8:00" : "";
      const fallbackEnd = clinic.enabled ? "5:00" : "";
      const fallbackRate = clinic.enabled ? clinic.rate : "";
      const fallbackRateType = clinic.enabled ? "contracted" : "perHour";

      return {
        date: dateStr,
        displayDate,
        startTime: inheritTime
          ? firstShift?.startTime || fallbackStart
          : fallbackStart,
        startAMPM: inheritTime ? firstShift?.startAMPM || "AM" : "AM",
        endTime: inheritTime ? firstShift?.endTime || fallbackEnd : fallbackEnd,
        endAMPM: inheritTime
          ? firstShift?.endAMPM || "PM"
          : clinic.enabled
            ? "PM"
            : "PM",
        rate: inheritRate ? firstShift?.rate || fallbackRate : fallbackRate,
        rateType: inheritRate
          ? firstShift?.rateType || fallbackRateType
          : fallbackRateType,
        onCall: useGroupSettings ? prev.groupOnCall : false,
        onCallDetails: useGroupSettings ? prev.groupOnCallDetails : "",
      };
    },
    [],
  );

  const applyPrefillData = useCallback(
    (prefillData: QuickCreateShiftPrefill) => {
      const mappedShifts = (prefillData.shifts || [])
        .map((shift, index) => {
          const normalizedDate = normalizeDateForShift(shift.date);
          if (!normalizedDate) return null;

          const parsedStart = parseTimeForShift(
            shift.startTime,
            index === 0 ? "AM" : "AM",
          );
          const parsedEnd = parseTimeForShift(
            shift.endTime,
            parsedStart.ampm === "AM" ? "PM" : parsedStart.ampm,
          );

          return {
            date: normalizedDate,
            displayDate: format(
              new Date(normalizedDate.replace(/\//g, "-")),
              "EEE, MMM d, yyyy",
            ),
            shiftsDaysId: shift.shiftsDaysId,
            startTime: parsedStart.time,
            startAMPM: parsedStart.ampm,
            endTime: parsedEnd.time,
            endAMPM: parsedEnd.ampm,
            rate:
              shift.rate === null || shift.rate === undefined
                ? ""
                : String(shift.rate),
            rateType: shift.rateType || "perHour",
            onCall: Boolean(shift.onCall),
            onCallDetails: String(shift.onCallDetails || ""),
            specialty: String(shift.specialty || "").trim() || undefined,
            skillLevel: String(shift.skillLevel || "").trim() || undefined,
            label: String(shift.label || "").trim() || undefined,
          };
        })
        .filter(Boolean) as QuickShiftDate[];

      if (!mappedShifts.length) return;

      const sortedShifts = mappedShifts.sort((a, b) =>
        a.date.localeCompare(b.date),
      );
      const selectedDates = Array.from(
        new Set(sortedShifts.map((shift) => shift.date)),
      );
      const firstShift = sortedShifts[0];
      const firstShiftDate = firstShift?.date
        ? dayjs(firstShift.date, ["YYYY/MM/DD", "YYYY-MM-DD"], true)
        : null;

      const travelText = String(prefillData.travel || "").trim();
      const accommodationText = String(prefillData.accommodation || "").trim();
      const additionalFiles = filterExtraDocumentsOnly(
        Array.isArray(prefillData.additionalFilesRequired)
          ? prefillData.additionalFilesRequired.filter(Boolean)
          : [],
      );
      const transportIncluded =
        typeof prefillData.transportIncluded === "boolean"
          ? prefillData.transportIncluded
          : Boolean(travelText);
      const accommodationIncluded =
        typeof prefillData.accommodationIncluded === "boolean"
          ? prefillData.accommodationIncluded
          : Boolean(accommodationText);

      const resolvedSpecialty = prefillData.specialty
        ? resolveOptionValue(
            createShiftModal.specialtyOptions,
            prefillData.specialty,
          )
        : "";
      const resolvedSkill = prefillData.skillLevel
        ? resolveOptionValue(createShiftModal.skillOptions, prefillData.skillLevel)
        : "";

      setCreateShiftModal((prev) => ({
        ...prev,
        step: 1,
        selectedDates,
        shifts: sortedShifts,
        postMode:
          prefillData.postMode ||
          (sortedShifts.length > 1 ? "group" : "separate"),
        currentShiftIndex: 0,
        applyTimeForAll: false,
        applyRateForAll: false,
        groupOnCall: Boolean(firstShift?.onCall),
        groupOnCallDetails: firstShift?.onCallDetails || "",
        specialty: resolvedSpecialty || String(prefillData.specialty || "").trim(),
        skillLevel: resolvedSkill || String(prefillData.skillLevel || "").trim(),
        supportLevel: String(prefillData.supportLevel || "").trim(),
        description: String(prefillData.description || "").trim(),
        transportIncluded,
        transportSelected:
          prefillData.transportSelected ?? (travelText ? "other" : ""),
        transportType:
          String(prefillData.transportType || "").trim() ||
          "/km and flights included",
        transportRate: String(prefillData.transportRate || "").trim(),
        transportOtherDetails: String(
          prefillData.transportOtherDetails || travelText,
        ).trim(),
        accommodationIncluded,
        accommodationType:
          String(prefillData.accommodationType || "").trim() ||
          (accommodationText ? "Other" : ""),
        accommodationOtherDetails: String(
          prefillData.accommodationOtherDetails || accommodationText,
        ).trim(),
        requestAdditionalDocs: additionalFiles.length > 0,
        requireAdditionalFiles: additionalFiles.length > 0,
        additionalDocs: additionalFiles.length ? additionalFiles : [""],
        editingDateIndex: 0,
        reviewSelectedIndex: 0,
      }));

      setAcceptCriteria(
        prefillData.rosterSplitByEligibility
          ? []
          : buildAcceptCriteriaFromPrefill({
              primarySpecialtyId:
                resolvedSpecialty || prefillData.specialty || "",
              primarySkillId: resolvedSkill || prefillData.skillLevel || "",
              specialtyAlternatives: prefillData.specialtyAlternatives,
              skillLevelAlternatives: prefillData.skillLevelAlternatives,
              acceptCriteria: prefillData.acceptCriteria,
              specialtyOptions: createShiftModal.specialtyOptions,
              skillOptions: createShiftModal.skillOptions,
            }),
      );
      setRosterSplitByEligibility(Boolean(prefillData.rosterSplitByEligibility));
      setPositionsRequired(
        prefillData.positionsRequired != null && prefillData.positionsRequired > 0
          ? prefillData.positionsRequired
          : 1,
      );

      if (prefillData.premiereAudiences?.length) {
        setPremiereEnabled(true);
        setPremiereAudiences(prefillData.premiereAudiences);
        setPremiereDays(prefillData.premiereDays || 7);
      } else if (prefillData.premiereEnabled === false) {
        setPremiereEnabled(false);
      }

      if (firstShiftDate?.isValid()) {
        setModalCalendarDate(firstShiftDate.toDate());
      }
      setIsDocumentRailCollapsed(false);
    },
    [
      createShiftModal.specialtyOptions,
      createShiftModal.skillOptions,
      resolveOptionValue,
    ],
  );

  const queueDocumentFile = useCallback(
    async (file: File) => {
      setCreateInputMode("ai_import");
      setRosterUploadLoading(true);
      try {
        const result = await uploadHospitalAiShiftDocument(file, { queue: true });
        const parseStatus = String(
          result?.parseResult?.status ||
            result?.parseResult?.extraction?.status ||
            "",
        ).trim();

        if (parseStatus && parseStatus !== "success" && !result?.queued) {
          const parseErr =
            result?.parseResult?.error ||
            result?.parseResult?.extraction?.error ||
            "Could not find shift dates in that file";
          message.error(String(parseErr));
          return;
        }

        if (!result?.queued) {
          message.error(
            "Document parsed but could not be added to AI Shifts. Check that the AI agents are deployed.",
          );
          return;
        }

        const queueCount = Number(result?.ingestResult?.shift_bodies || 0);
        message.success(
          queueCount > 0
            ? `"${file.name}" sent to AI Shifts — ${queueCount} vacancies queued for review`
            : `"${file.name}" sent to AI Shifts for review`,
          6,
        );
        onSuccess?.({
          documentQueued: true,
          documentQueueCount: queueCount > 0 ? queueCount : undefined,
        });
      } catch (error: any) {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Upload failed. Check that the document AI agent is deployed.";
        message.error(msg);
      } finally {
        setRosterUploadLoading(false);
        setEmailPasteLoading(false);
        setRosterDragActive(false);
      }
    },
    [onSuccess],
  );

  const handleRosterFiles = useCallback(
    async (files: FileList | File[]) => {
      if (aiImportLocked) {
        message.info("Clear selected dates first, or finish the calendar flow");
        return;
      }
      const candidates = Array.from(files).filter(isRosterUploadFile);
      if (!candidates.length) {
        message.warning("Use a PDF, Excel, CSV, Word, or text roster file");
        return;
      }
      await queueDocumentFile(candidates[0]);
    },
    [aiImportLocked, queueDocumentFile],
  );

  const handleEmailPasteQueue = useCallback(async () => {
    if (aiImportLocked) {
      message.info("Clear selected dates first, or finish the calendar flow");
      return;
    }
    if (!pastedEmailHasEnoughContent(emailPasteText, emailPasteHtml)) {
      message.warning("Paste the full shift email (subject line + body)");
      return;
    }
    setEmailPasteLoading(true);
    setCreateInputMode("ai_import");
    const file = buildPastedEmailDocumentFile(emailPasteText, emailPasteHtml);
    await queueDocumentFile(file);
  }, [aiImportLocked, emailPasteText, emailPasteHtml, queueDocumentFile]);

  useEffect(() => {
    if (!open || createShiftModal.step !== 1 || !showRosterUpload) return;

    const onPaste = (event: Event) => {
      const clipboardEvent = event as ClipboardEvent;
      const pastedFiles = clipboardEvent.clipboardData?.files;
      if (pastedFiles?.length && isRosterUploadFile(pastedFiles[0])) {
        if (aiImportLocked) {
          message.info("Clear selected dates before pasting a roster file");
          return;
        }
        clipboardEvent.preventDefault();
        void handleRosterFiles(pastedFiles);
        return;
      }

      const { text, html } = extractEmailPasteFromClipboard(
        clipboardEvent.clipboardData,
      );
      if ((!text || text.length < 40) && !html) return;
      if (aiImportLocked) {
        message.info("Clear selected dates before pasting an email");
        return;
      }
      clipboardEvent.preventDefault();
      applyEmailPasteFromClipboard(clipboardEvent.clipboardData);
    };

    window.addEventListener("paste", onPaste as EventListener);
    return () => window.removeEventListener("paste", onPaste as EventListener);
  }, [
    open,
    createShiftModal.step,
    showRosterUpload,
    handleRosterFiles,
    aiImportLocked,
    applyEmailPasteFromClipboard,
  ]);

  useEffect(() => {
    if (!rosterSplitByEligibility || !createShiftModal.shifts.length) return;
    const current =
      createShiftModal.shifts[createShiftModal.currentShiftIndex] ||
      createShiftModal.shifts[0];
    if (!current) return;
    const specRaw = current.specialty?.trim();
    const skillRaw = current.skillLevel?.trim();
    if (!specRaw && !skillRaw) return;

    setCreateShiftModal((prev) => ({
      ...prev,
      specialty: specRaw
        ? resolveOptionValue(prev.specialtyOptions, specRaw) || specRaw
        : prev.specialty,
      skillLevel: skillRaw
        ? resolveOptionValue(prev.skillOptions, skillRaw) || skillRaw
        : prev.skillLevel,
    }));
    setAcceptCriteria([]);
  }, [
    rosterSplitByEligibility,
    createShiftModal.currentShiftIndex,
    createShiftModal.shifts,
  ]);

  const closeModal = useCallback(() => {
    setCreateShiftModal({
      ...initialState,
      open: false,
    });
    setModalCalendarDate(new Date());
    setDragMode(null);
    setRosterUploadLoading(false);
    setRosterDragActive(false);
    setRosterSplitByEligibility(false);
    setCreateInputMode(null);
    setShowEmailPaste(false);
    setEmailPasteText("");
    setEmailPasteHtml("");
    setEmailPasteLoading(false);
    setEmailPasteReading(false);
    setShowApplyPulse({ time: false, rate: false, onCall: false });
    setAcceptCriteria([]);
    setPositionsRequired(1);
    setEligibilityManualPromptShown(false);
    setEligibilitySuggestionsPromptShown(false);
    setEligibleRolesDialogOpen(false);
    setFocusEligibilityManualAdd(false);
    setPremiereEnabled(false);
    setPremiereAudiences(["INTERNAL", "PRE_CREDENTIALED", "FAVORITE", "WORKED_BEFORE"]);
    setPremiereDays(7);
    setReleaseMode("premiere");
    setSelectedAutomationId("");
    setSelectedBrandId("");
    setSelectedClinicIds([]);
    setClinicQuery("");
    clinicDefaultsForBrandRef.current = null;
    setClinicNetworkPickerDone(false);
    onClose();
  }, [onClose]);

  const completeCreateSuccess = useCallback(
    (
      results: unknown,
      payload?: Parameters<NonNullable<QuickCreateShiftModalProps["onSuccess"]>>[0],
    ) => {
      const createdShiftIds = extractCreatedShiftIds(results);
      const createdDays = extractCreatedDays(results);
      const successPayload = {
        ...payload,
        ...(createdShiftIds.length ? { createdShiftIds } : {}),
        ...(createdDays.length ? { createdDays } : {}),
      };

      if (!isEditMode && createdShiftIds.length > 0) {
        pendingOnSuccessRef.current = successPayload;
        setPostCreateInviteShiftId(createdShiftIds[0]);
        closeModal();
        return;
      }

      onSuccess?.(successPayload);
      closeModal();
    },
    [closeModal, isEditMode, onSuccess],
  );

  const handlePostCreateInviteClose = useCallback(() => {
    setPostCreateInviteShiftId(null);
    onSuccess?.(pendingOnSuccessRef.current);
    pendingOnSuccessRef.current = undefined;
  }, [onSuccess]);

  const advanceToNextStep = useCallback(() => {
    setCreateShiftModal((prev) => ({
      ...prev,
      step: prev.step + 1,
      reviewSelectedIndex: 0,
    }));
    setFocusEligibilityManualAdd(false);
  }, []);

  const handleAdvanceStep = useCallback(() => {
    if (
      createShiftModal.step === 1 &&
      isClinicNetworkExperience &&
      !isEditMode &&
      !clinicNetworkPickerDone
    ) {
      if (!selectedBrandId || selectedClinicIds.length === 0) {
        message.error(
          !selectedBrandId
            ? "Choose Jones Radiology or I-MED Radiology first"
            : "Choose at least one clinic for that brand",
        );
        return;
      }
      setClinicNetworkPickerDone(true);
      return;
    }
    if (
      createShiftModal.step === 1 &&
      isClinicNetworkExperience &&
      (!selectedBrandId || selectedClinicIds.length === 0)
    ) {
      message.error(
        !selectedBrandId
          ? "Choose Jones Radiology or I-MED Radiology first"
          : "Choose at least one clinic for that brand",
      );
      return;
    }
    if (
      createShiftModal.step === 2 &&
      acceptCriteria.length === 0 &&
      !isClinicNetworkExperience
    ) {
      if (
        pendingEligibleSuggestions.length === 0 &&
        !eligibilityManualPromptShown
      ) {
        setEligibilityManualPromptShown(true);
        setFocusEligibilityManualAdd(true);
        return;
      }
    }

    advanceToNextStep();
  }, [
    acceptCriteria.length,
    advanceToNextStep,
    createShiftModal.step,
    eligibilityManualPromptShown,
    isClinicNetworkExperience,
    isEditMode,
    clinicNetworkPickerDone,
    pendingEligibleSuggestions.length,
    selectedBrandId,
    selectedClinicIds.length,
  ]);

  const addEligibleRoles = useCallback((rows: AcceptCriterion[]) => {
    if (!rows.length) return;
    setAcceptCriteria((prev) => dedupeAcceptCriteria([...prev, ...rows]));
  }, []);

  // Add/remove a single date from selection
  const addDateToSelection = useCallback(
    (dateStr: string) => {
      setCreateShiftModal((prev) => {
        if (prev.selectedDates.includes(dateStr)) return prev;
        const newDates = [...prev.selectedDates, dateStr].sort();
        const newShift = buildInheritedShift(prev, dateStr);
        const newShifts = [...prev.shifts, newShift].sort((a, b) =>
          a.date.localeCompare(b.date),
        );
        return {
          ...prev,
          selectedDates: newDates,
          shifts: newShifts,
          currentShiftIndex: Math.min(
            prev.currentShiftIndex,
            Math.max(0, newShifts.length - 1),
          ),
          postMode:
            isPostModeLocked || newDates.length <= 1
              ? prev.postMode
              : "separate",
        };
      });
    },
    [buildInheritedShift, isPostModeLocked],
  );

  const removeDateFromSelection = useCallback((dateStr: string) => {
    setCreateShiftModal((prev) => {
      const newDates = prev.selectedDates.filter((d) => d !== dateStr);
      const newShifts = prev.shifts.filter((s) => s.date !== dateStr);
      return {
        ...prev,
        selectedDates: newDates,
        shifts: newShifts,
        currentShiftIndex: Math.min(
          prev.currentShiftIndex,
          Math.max(0, newShifts.length - 1),
        ),
      };
    });
  }, []);

  const toggleDateSelection = useCallback(
    (dateStr: string) => {
      setCreateShiftModal((prev) => {
        const isSelected = prev.selectedDates.includes(dateStr);
        if (isSelected) {
          const newDates = prev.selectedDates.filter((d) => d !== dateStr);
          const newShifts = prev.shifts.filter((s) => s.date !== dateStr);
          return {
            ...prev,
            selectedDates: newDates,
            shifts: newShifts,
            currentShiftIndex: Math.min(
              prev.currentShiftIndex,
              Math.max(0, newShifts.length - 1),
            ),
          };
        } else {
          const newDates = [...prev.selectedDates, dateStr].sort();
          const newShift = buildInheritedShift(prev, dateStr);
          const newShifts = [...prev.shifts, newShift].sort((a, b) =>
            a.date.localeCompare(b.date),
          );
          return {
            ...prev,
            selectedDates: newDates,
            shifts: newShifts,
            currentShiftIndex: Math.min(
              prev.currentShiftIndex,
              Math.max(0, newShifts.length - 1),
            ),
            postMode:
              isPostModeLocked || newDates.length <= 1
                ? prev.postMode
                : "separate",
          };
        }
      });
    },
    [buildInheritedShift, isPostModeLocked],
  );

  const getSelectableDatesInRange = useCallback(
    (startDateStr: string, endDateStr: string) => {
      const start = new Date(startDateStr.replace(/\//g, "-") + "T00:00:00");
      const end = new Date(endDateStr.replace(/\//g, "-") + "T00:00:00");

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        return [] as string[];
      }

      const from = start <= end ? start : end;
      const to = start <= end ? end : start;
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return eachDayOfInterval({ start: from, end: to })
        .filter((day) => day >= today)
        .map((day) => format(day, "yyyy/MM/dd"));
    },
    [],
  );

  const applyDateSelectionByMode = useCallback(
    (dateList: string[], mode: "select" | "deselect") => {
      if (dateList.length === 0) return;

      setCreateShiftModal((prev) => {
        if (mode === "deselect") {
          const removeSet = new Set(dateList);
          const newDates = prev.selectedDates.filter((d) => !removeSet.has(d));

          if (newDates.length === prev.selectedDates.length) return prev;

          const newShifts = prev.shifts.filter((s) => !removeSet.has(s.date));
          return {
            ...prev,
            selectedDates: newDates,
            shifts: newShifts,
            currentShiftIndex: Math.min(
              prev.currentShiftIndex,
              Math.max(0, newShifts.length - 1),
            ),
          };
        }

        const existingDates = new Set(prev.selectedDates);
        const datesToAdd = dateList.filter((d) => !existingDates.has(d));

        if (datesToAdd.length === 0) return prev;

        const newShiftsToAdd: QuickShiftDate[] = datesToAdd.map((dateStr) =>
          buildInheritedShift(prev, dateStr),
        );

        const newDates = [...prev.selectedDates, ...datesToAdd].sort();
        const newShifts = [...prev.shifts, ...newShiftsToAdd].sort((a, b) =>
          a.date.localeCompare(b.date),
        );

        return {
          ...prev,
          selectedDates: newDates,
          shifts: newShifts,
          currentShiftIndex: Math.min(
            prev.currentShiftIndex,
            Math.max(0, newShifts.length - 1),
          ),
          postMode:
            isPostModeLocked || newDates.length <= 1
              ? prev.postMode
              : "separate",
        };
      });
    },
    [buildInheritedShift, isPostModeLocked],
  );

  const replaceSelectedDates = useCallback(
    (dateList: string[]) => {
      const unique = Array.from(new Set(dateList)).sort();
      setCreateShiftModal((prev) => {
        const existing = new Map(prev.shifts.map((shift) => [shift.date, shift]));
        const newShifts = unique.map(
          (dateStr) => existing.get(dateStr) || buildInheritedShift(prev, dateStr),
        );
        return {
          ...prev,
          selectedDates: unique,
          shifts: newShifts,
          currentShiftIndex: Math.min(
            prev.currentShiftIndex,
            Math.max(0, newShifts.length - 1),
          ),
          postMode: isPostModeLocked ? "group" : prev.postMode,
        };
      });
    },
    [buildInheritedShift, isPostModeLocked],
  );

  const calendarLockedRef = useRef(calendarLocked);
  calendarLockedRef.current = calendarLocked;

  const endDateDrag = useCallback(() => {
    const drag = dateDragRef.current;
      if (isClinicNetworkExperience && drag?.active && !drag.moved) {
      const week = locumWeekDateStrings(drag.start);
      applyDateSelectionByMode(week, drag.mode);
    }
    dateDragRef.current = null;
    setCreateShiftModal((prev) => ({
      ...prev,
      isDragging: false,
      dragStart: null,
    }));
    setDragMode(null);
  }, [
    applyDateSelectionByMode,
    isClinicNetworkExperience,
  ]);

  const startDateDrag = useCallback(
    (dateStr: string, isPast: boolean, isCurrentMonth: boolean) => {
      if (isPast || calendarLockedRef.current) return false;
      setCreateInputMode("calendar");
      const isCurrentlySelected = selectedDatesRef.current.includes(dateStr);
      const mode = isCurrentlySelected ? "deselect" : "select";
      dateDragRef.current = { active: true, mode, start: dateStr, moved: false };
      setDragMode(mode);
      setCreateShiftModal((prev) => ({
        ...prev,
        isDragging: true,
        dragStart: dateStr,
      }));
      if (!isClinicNetworkExperience) {
        applyDateSelectionByMode([dateStr], mode);
      }
      return true;
    },
    [applyDateSelectionByMode, isClinicNetworkExperience],
  );

  const moveDateDrag = useCallback(
    (dateStr: string, isPast: boolean, isCurrentMonth: boolean) => {
      const drag = dateDragRef.current;
      if (!drag?.active || isPast) return;
      if (dateStr !== drag.start) drag.moved = true;
      const range = getSelectableDatesInRange(drag.start, dateStr);
      applyDateSelectionByMode(range, drag.mode);
    },
    [
      applyDateSelectionByMode,
      getSelectableDatesInRange,
    ],
  );

  const handleCalendarPointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (event.button !== 0) return;
    const cell = (event.target as HTMLElement).closest(
      "[data-shift-date]",
    ) as HTMLElement | null;
    if (!cell) return;
    const started = startDateDrag(
      cell.dataset.shiftDate || "",
      cell.dataset.shiftPast === "1",
      cell.dataset.shiftCurrent === "1",
    );
    if (!started) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const handleCalendarPointerMove = (
    event: React.PointerEvent<HTMLDivElement>,
  ) => {
    if (!dateDragRef.current?.active) return;
    const el = document.elementFromPoint(
      event.clientX,
      event.clientY,
    ) as HTMLElement | null;
    const cell = el?.closest?.("[data-shift-date]") as HTMLElement | null;
    if (!cell) return;
    moveDateDrag(
      cell.dataset.shiftDate || "",
      cell.dataset.shiftPast === "1",
      cell.dataset.shiftCurrent === "1",
    );
  };

  useEffect(() => {
    const onUp = () => {
      if (!dateDragRef.current?.active) return;
      endDateDrag();
    };
    window.addEventListener("pointerup", onUp);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("mouseup", onUp);
    };
  }, [endDateDrag]);

  const formatTimeForAPI = (time: string, ampm: string): string => {
    if (!time) return "";
    let [hours, minutes] = time.split(":").map(Number);
    if (ampm === "PM" && hours !== 12) hours += 12;
    if (ampm === "AM" && hours === 12) hours = 0;
    return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
  };

  const parseTypedTimeInput = (
    rawValue: string,
    currentAMPM: "AM" | "PM",
  ): { time: string; ampm: "AM" | "PM" } | null => {
    if (!rawValue) return null;

    const value = rawValue.trim().toLowerCase();
    const match = value.match(/^(\d{1,2})(?::(\d{1,2}))?\s*([ap]m?|am|pm)?$/i);
    if (!match) return null;

    const hourRaw = Number(match[1]);
    const minuteRaw = match[2] ? Number(match[2]) : 0;
    const suffix = (match[3] || "").toLowerCase();

    if (Number.isNaN(hourRaw) || Number.isNaN(minuteRaw) || minuteRaw > 59)
      return null;

    if (suffix) {
      if (hourRaw < 1 || hourRaw > 12) return null;
      const ampm = suffix.startsWith("p") ? "PM" : "AM";
      return {
        time: `${hourRaw}:${minuteRaw.toString().padStart(2, "0")}`,
        ampm,
      };
    }

    if (hourRaw < 0 || hourRaw > 23) return null;

    if (hourRaw === 0) {
      return {
        time: `12:${minuteRaw.toString().padStart(2, "0")}`,
        ampm: "AM",
      };
    }

    if (hourRaw > 12) {
      return {
        time: `${hourRaw - 12}:${minuteRaw.toString().padStart(2, "0")}`,
        ampm: "PM",
      };
    }

    return {
      time: `${hourRaw}:${minuteRaw.toString().padStart(2, "0")}`,
      ampm: currentAMPM,
    };
  };

  const applyTypedTimeToEditedShift = (
    type: "start" | "end",
    rawValue: string,
  ) => {
    const idx = createShiftModal.editingDateIndex || 0;
    const current = createShiftModal.shifts[idx];
    if (!current) return;

    const parsed = parseTypedTimeInput(
      rawValue,
      type === "start" ? current.startAMPM : current.endAMPM,
    );
    if (!parsed) return;

    setCreateShiftModal((prev) => {
      const newShifts = [...prev.shifts];
      newShifts[idx] = {
        ...newShifts[idx],
        ...(type === "start"
          ? { startTime: parsed.time, startAMPM: parsed.ampm }
          : { endTime: parsed.time, endAMPM: parsed.ampm }),
      };
      return { ...prev, shifts: newShifts };
    });

    if (createShiftModal.shifts.length > 1) {
      setShowApplyPulse((prev) => ({ ...prev, time: true }));
    }
  };

  const getEndDateForAPI = (
    shiftDate: string,
    startTime: string,
    startAMPM: "AM" | "PM",
    endTime: string,
    endAMPM: "AM" | "PM",
  ): string => {
    const apiDate = shiftDate.replace(/\//g, "-");
    const start24 = formatTimeForAPI(startTime, startAMPM);
    const end24 = formatTimeForAPI(endTime, endAMPM);
    if (!start24 || !end24) return apiDate;

    const [startH, startM] = start24.split(":").map(Number);
    const [endH, endM] = end24.split(":").map(Number);
    const startTotal = startH * 60 + startM;
    const endTotal = endH * 60 + endM;

    if (endTotal <= startTotal) {
      return dayjs(apiDate).add(1, "day").format("YYYY-MM-DD");
    }

    return apiDate;
  };

  const submitQuickCreate = async () => {
    const publishRequestOptions =
      isAiReview && publishHospitalId
        ? { hospitalId: publishHospitalId }
        : undefined;
    const usingAutomation =
      releaseMode === "automation" && selectedPostingAutomation;
    const automationStages = usingAutomation
      ? releaseStagesFromAutomation(selectedPostingAutomation)
      : [];
    const automationDays = automationStages
      .filter((stage) => stage.audience !== "public")
      .reduce((sum, stage) => sum + (Number(stage.days) || 0), 0);
    const specialtyLabel =
      createShiftModal.specialtyOptions.find(
        (option) => option.value === createShiftModal.specialty,
      )?.label || createShiftModal.specialty;
    const skillLabel =
      createShiftModal.skillOptions.find(
        (option) => option.value === createShiftModal.skillLevel,
      )?.label || createShiftModal.skillLevel;
    const releaseExtra = usingAutomation
      ? postingAutomationPayload(selectedPostingAutomation, {
          grouped:
            createShiftModal.postMode === "group" &&
            createShiftModal.shifts.length > 1,
          specialty: specialtyLabel,
          skillLevel: skillLabel,
          days: createShiftModal.shifts.map((shift) => {
            const start = formatTimeForAPI(shift.startTime, shift.startAMPM);
            const end = formatTimeForAPI(shift.endTime, shift.endAMPM);
            const date = String(shift.date || "").replace(/\//g, "-");
            return {
              date: shift.displayDate || shift.date,
              startTime: start,
              endTime: end,
              specialty: specialtyLabel,
              skillLevel: skillLabel,
              startMs: Date.parse(`${date}T${start || "00:00"}`),
            };
          }),
        })
      : premiereEnabled && premiereAudiences.length
        ? {
            premiereConfig: {
              enabled: true,
              audiences: premiereAudiences,
              days: premiereDays,
            },
          }
        : {};
    const premiereResult =
      usingAutomation && automationDays
        ? {
            premiere: {
              audiences: automationStages.flatMap((stage) =>
                stage.audience === "internal"
                  ? (["INTERNAL"] as ShiftPremiereAudience[])
                  : stage.audience === "precredential"
                    ? (["PRE_CREDENTIALED"] as ShiftPremiereAudience[])
                    : stage.audience === "favorite"
                      ? (["FAVORITE"] as ShiftPremiereAudience[])
                      : stage.audience === "worked_before"
                        ? (["WORKED_BEFORE"] as ShiftPremiereAudience[])
                        : [],
              ),
              expiresAt: Date.now() + automationDays * 24 * 60 * 60 * 1000,
            },
          }
        : premiereEnabled && premiereAudiences.length
          ? {
              premiere: {
                audiences: [...premiereAudiences],
                expiresAt:
                  Date.now() + premiereDays * 24 * 60 * 60 * 1000,
              },
            }
          : {};
    // Validation
    if (isClinicNetworkExperience && selectedClinicIds.length === 0) {
      message.error("Please choose at least one clinic");
      return;
    }
    if (createShiftModal.shifts.length === 0) {
      message.error("Please select at least one date");
      return;
    }
    if (!createShiftModal.specialty) {
      message.error("Please select a specialty");
      return;
    }
    if (!createShiftModal.skillLevel) {
      message.error("Please select a skill level");
      return;
    }

    const hasEmptyTimes = createShiftModal.shifts.some(
      (s) => !s.startTime || !s.endTime,
    );
    if (hasEmptyTimes) {
      message.error("Please fill in start and end times for all shifts");
      return;
    }
    const hasEmptyRates = createShiftModal.shifts.some((s) => !s.rate);
    if (hasEmptyRates) {
      message.error("Please fill in rates for all shifts");
      return;
    }

    // On-call description validation
    if (
      createShiftModal.postMode === "group" ||
      createShiftModal.shifts.length === 1
    ) {
      if (
        createShiftModal.groupOnCall &&
        !createShiftModal.groupOnCallDetails?.trim()
      ) {
        message.error("Please provide on-call description");
        return;
      }
    } else {
      const shiftsWithMissingOnCallDetails = createShiftModal.shifts.filter(
        (s) => s.onCall && !s.onCallDetails?.trim(),
      );
      if (shiftsWithMissingOnCallDetails.length > 0) {
        message.error(
          `Please provide on-call description for ${shiftsWithMissingOnCallDetails.length} shift(s)`,
        );
        return;
      }
    }

    if (createShiftModal.transportIncluded && !createShiftModal.transportSelected) {
      message.error(
        "Please choose a travel type: Variable (/km), Set rate, or Other",
      );
      return;
    }

    if (
      createShiftModal.accommodationIncluded &&
      !createShiftModal.accommodationType?.trim()
    ) {
      message.error("Please choose an accommodation type");
      return;
    }

    setCreateShiftModal((prev) => ({ ...prev, submitting: true }));

    try {
      const {
        shifts,
        postMode,
        specialty,
        skillLevel,
        supportLevel,
        groupOnCall,
        groupOnCallDetails,
        description,
        transportIncluded,
        transportSelected,
        transportType,
        transportRate,
        transportOtherDetails,
        accommodationIncluded,
        accommodationType,
        accommodationOtherDetails,
        requireAdditionalFiles,
        additionalDocs,
      } = createShiftModal;

      const formatDateForAPI = (date: string) => date.replace(/\//g, "-");

      // --- Logic for Transport / Travel ---
      const travelFlag = transportIncluded ? 1 : 0;
      // "Variable" -> variableRate
      // "Set" (User calls it 'separate') -> fixedRate
      // "Other" -> otherRate
      const variableRateVal =
        transportIncluded && transportSelected === "variable"
          ? Number(transportRate) || null
          : null;
      const fixedRateVal =
        transportIncluded && transportSelected === "set"
          ? Number(transportRate) || null
          : null;
      const otherRateVal =
        transportIncluded && transportSelected === "other"
          ? transportOtherDetails
          : "";

      // Flights flag only applies if variable + explicit selection
      const flightsIncludedFlag =
        transportIncluded &&
        transportSelected === "variable" &&
        transportType === "/km and flights included"
          ? 1
          : 0;

      // --- Logic for Accommodation ---
      const accFlag = accommodationIncluded ? 1 : 0;
      // If "Other" is selected, send the custom details string as the accommodation value
      // Otherwise, send the dropdown value
      const finalAccommodation = accommodationIncluded
        ? accommodationType === "Other"
          ? accommodationOtherDetails
          : accommodationType
        : "";

      let response: any;

      const criteriaPayload = rosterSplitByEligibility
        ? []
        : excludePrimaryFromAcceptCriteria(
            dedupeAcceptCriteria(acceptCriteria),
            createShiftModal.specialty,
            createShiftModal.skillLevel,
          );

      const eligibilityFallback = { specialty, skillLevel };
      const { specialtyOptions, skillOptions } = createShiftModal;

      const buildDayDto = (shift: QuickShiftDate, useGroupOnCall = false) => {
        const startFormatted = formatTimeForAPI(
          shift.startTime,
          shift.startAMPM,
        );
        const endFormatted = formatTimeForAPI(shift.endTime, shift.endAMPM);
        const apiDate = formatDateForAPI(shift.date);
        const endApiDate = getEndDateForAPI(
          shift.date,
          shift.startTime,
          shift.startAMPM,
          shift.endTime,
          shift.endAMPM,
        );

        return {
          shiftsDays: apiDate,
          startTimeS: `${apiDate} ${startFormatted}`,
          endTimeS: `${endApiDate} ${endFormatted}`,
          ratePerHour: shift.rateType === "perHour" ? Number(shift.rate) : 0,
          contractedRate:
            shift.rateType === "contracted" ? Number(shift.rate) : null,
          rateType: shift.rateType,
          onCallFlag: useGroupOnCall ? groupOnCall : shift.onCall,
          onCallDetails: useGroupOnCall
            ? groupOnCallDetails || ""
            : shift.onCallDetails || "",
          durationTime: "",
          shiftsDaysId: shift.shiftsDaysId || "",
        };
      };

      const buildShiftPostParams = (
        partShifts: QuickShiftDate[],
        partSpecialty: string,
        partSkillLevel: string,
        shiftsType: "1" | "2" | "3",
      ) => ({
        accommodation: finalAccommodation,
        accommodationFlag: accFlag,
        additionalFilesRequired: requireAdditionalFiles
          ? filterExtraDocumentsOnly(additionalDocs.filter((d) => d.trim()))
          : [],
        contactEmail: hospitalContactRef.current.email,
        contactFullName: hospitalContactRef.current.name,
        contactNumber: hospitalContactRef.current.phone,
        daysDTOList: partShifts.map((s) =>
          buildDayDto(s, shiftsType === "2" && partShifts.length > 1),
        ),
        fixedRate: fixedRateVal,
        variableRate: variableRateVal,
        otherRate: otherRateVal,
        shiftsDescription: description,
        shiftsType,
        skillLevel: partSkillLevel,
        specialty: partSpecialty,
        supportLevel: supportLevel || "",
        travelFlag: travelFlag,
        flightsIncludedFlag: flightsIncludedFlag,
        ...(criteriaPayload.length ? { acceptCriteria: criteriaPayload } : {}),
        ...(positionsRequired && positionsRequired > 0 && !rosterSplitByEligibility
          ? { positionsRequired }
          : {}),
        ...releaseExtra,
        ...(selectedClinicIds.length ? { hospitalIds: selectedClinicIds } : {}),
      });

      const buildDaysDTOList = () =>
        shifts.map((shift) => buildDayDto(shift, postMode === "group" && shifts.length > 1));

      const baseParams = {
        accommodation: finalAccommodation,
        accommodationFlag: accFlag,
        additionalFilesRequired: requireAdditionalFiles
          ? filterExtraDocumentsOnly(additionalDocs.filter((d) => d.trim()))
          : [],
        contactEmail: prefill?.contactEmail || hospitalContactRef.current.email,
        contactFullName:
          prefill?.contactFullName || hospitalContactRef.current.name,
        contactNumber:
          prefill?.contactNumber || hospitalContactRef.current.phone,
        fixedRate: fixedRateVal,
        variableRate: variableRateVal,
        otherRate: otherRateVal,
        shiftsDescription: description,
        shiftsType:
          shifts.length === 1 ? "1" : postMode === "group" ? "2" : "3",
        skillLevel,
        specialty,
        supportLevel: supportLevel || "",
        travelFlag: travelFlag,
        flightsIncludedFlag: flightsIncludedFlag,
        ...(criteriaPayload.length ? { acceptCriteria: criteriaPayload } : {}),
        ...(positionsRequired && positionsRequired > 0
          ? { positionsRequired }
          : {}),
        ...releaseExtra,
      };

      if (isEditMode && editShiftId) {
        const editPayload = {
          ...baseParams,
          daysDTOList: buildDaysDTOList().map((day) => ({
            ...day,
            shiftsDays: day.shiftsDays.replace(/\//g, "-"),
            startTimeS: day.startTimeS.replace(/\//g, "-"),
            endTimeS: day.endTimeS.replace(/\//g, "-"),
          })),
        };

        await editShiftV2(editShiftId, editPayload);
        message.success(
          `Shift${shifts.length > 1 ? "s" : ""} updated successfully! 🎉`,
        );
        onSuccess?.({
          submittedDates: shifts
            .map((shift) => String(shift.date || "").trim())
            .filter(Boolean),
        });
        closeModal();
        return;
      }

      if (rosterSplitByEligibility) {
        if (postMode === "group" && shifts.length > 1) {
          const partitions = new Map<string, QuickShiftDate[]>();
          for (const shift of shifts) {
            const elig = resolveEligibilityForShift(
              shift,
              eligibilityFallback,
              specialtyOptions,
              skillOptions,
            );
            const key = `${elig.specialty}|${elig.skillLevel}`;
            const list = partitions.get(key) || [];
            list.push(shift);
            partitions.set(key, list);
          }
          const bulkParams = Array.from(partitions.entries()).map(
            ([key, partShifts]) => {
              const [partSpecialty, partSkillLevel] = key.split("|");
              return buildShiftPostParams(
                partShifts,
                partSpecialty,
                partSkillLevel,
                partShifts.length > 1 ? "2" : "1",
              );
            },
          );
          response = await bulkAddShiftsV2(bulkParams, publishRequestOptions);
        } else {
          const bulkParams = shifts.map((shift) => {
            const elig = resolveEligibilityForShift(
              shift,
              eligibilityFallback,
              specialtyOptions,
              skillOptions,
            );
            return buildShiftPostParams(
              [shift],
              elig.specialty,
              elig.skillLevel,
              "1",
            );
          });
          response = await bulkAddShiftsV2(bulkParams, publishRequestOptions);
        }
      } else if (postMode === "group" && shifts.length > 1) {
        const daysDTOList = shifts.map((shift) => {
          const startFormatted = formatTimeForAPI(
            shift.startTime,
            shift.startAMPM,
          );
          const endFormatted = formatTimeForAPI(shift.endTime, shift.endAMPM);
          const apiDate = formatDateForAPI(shift.date);
          const endApiDate = getEndDateForAPI(
            shift.date,
            shift.startTime,
            shift.startAMPM,
            shift.endTime,
            shift.endAMPM,
          );

          return {
            shiftsDays: apiDate,
            startTimeS: `${apiDate} ${startFormatted}`,
            endTimeS: `${endApiDate} ${endFormatted}`,
            ratePerHour: shift.rateType === "perHour" ? Number(shift.rate) : 0,
            contractedRate:
              shift.rateType === "contracted" ? Number(shift.rate) : null,
            rateType: shift.rateType,
            onCallFlag: shift.onCall,
            onCallDetails: shift.onCallDetails || "",
            durationTime: "",
            shiftsDaysId: "",
          };
        });

        const params = {
          accommodation: finalAccommodation,
          accommodationFlag: accFlag,
          additionalFilesRequired: requireAdditionalFiles
            ? filterExtraDocumentsOnly(additionalDocs.filter((d) => d.trim()))
            : [],
          contactEmail: hospitalContactRef.current.email,
          contactFullName: hospitalContactRef.current.name,
          contactNumber: hospitalContactRef.current.phone,
          daysDTOList,
          fixedRate: fixedRateVal,
          variableRate: variableRateVal,
          otherRate: otherRateVal,
          shiftsDescription: description,
          shiftsType: "2",
          skillLevel,
          specialty,
          supportLevel: supportLevel || "",
          travelFlag: travelFlag,
          flightsIncludedFlag: flightsIncludedFlag,
          ...(criteriaPayload.length ? { acceptCriteria: criteriaPayload } : {}),
        ...(positionsRequired && positionsRequired > 0
          ? { positionsRequired }
          : {}),
        ...releaseExtra,
        ...(selectedClinicIds.length ? { hospitalIds: selectedClinicIds } : {}),
        };

        response = await bulkAddShiftsV2([params], publishRequestOptions);
      } else {
        const bulkParams = shifts.map((shift) => {
          const elig = rosterSplitByEligibility
            ? resolveEligibilityForShift(
                shift,
                eligibilityFallback,
                specialtyOptions,
                skillOptions,
              )
            : eligibilityFallback;

          return buildShiftPostParams(
            [shift],
            elig.specialty,
            elig.skillLevel,
            shifts.length === 1 ? "1" : "3",
          );
        });

        response = await bulkAddShiftsV2(bulkParams, publishRequestOptions);
      }

      const results = Array.isArray(response) ? response : [response];

      if (rosterSplitByEligibility) {
        const failed = results.filter((result) => result?.success === false);
        if (failed.length > 0) {
          message.error(
            `${failed.length} of ${results.length} roster shift(s) failed to post`,
          );
          return;
        }
        message.success(
          `Posted ${results.length} standalone shift${results.length === 1 ? "" : "s"} from roster 🎉`,
        );
        completeCreateSuccess(results, {
          ...premiereResult,
          submittedDates: shifts
            .map((shift) => String(shift.date || "").trim())
            .filter(Boolean),
        });
      } else if (postMode === "group" && shifts.length > 1) {
        const result = results[0];
        if (result?.success === false) {
          message.error(
            `Group shift failed: ${result?.message || "Unknown error"}`,
          );
          return;
        }
        message.success(
          `Group shift with ${shifts.length} dates created successfully! 🎉`,
        );
        completeCreateSuccess(results, {
          ...premiereResult,
          submittedDates: shifts
            .map((shift) => String(shift.date || "").trim())
            .filter(Boolean),
        });
      } else {
        const successfulIndices: number[] = [];
        const failedShifts: { date: string; message: string }[] = [];

        results.forEach((result: any, idx: number) => {
          if (result?.success === true) {
            successfulIndices.push(idx);
          } else {
            const shiftDate = shifts[idx]?.date;
            failedShifts.push({
              date: shiftDate
                ? format(new Date(shiftDate.replace(/\//g, "-")), "MMM d")
                : `Shift ${idx + 1}`,
              message: result?.message || "Unknown error",
            });
          }
        });

        if (failedShifts.length === 0) {
          message.success(
            `${shifts.length} shift${shifts.length > 1 ? "s" : ""} created successfully! 🎉`,
          );
          completeCreateSuccess(results, {
            ...premiereResult,
            submittedDates: shifts
              .map((shift) => String(shift.date || "").trim())
              .filter(Boolean),
          });
        } else if (successfulIndices.length > 0) {
          const uniqueErrors = Array.from(
            new Set(failedShifts.map((f) => f.message)),
          ).join("; ");

          const remainingShifts = shifts.filter(
            (_, idx) => !successfulIndices.includes(idx),
          );
          const remainingDates = remainingShifts.map((s) => s.date);

          setCreateShiftModal((prev) => ({
            ...prev,
            shifts: remainingShifts,
            selectedDates: remainingDates,
            editingDateIndex: 0,
            reviewSelectedIndex: 0,
          }));

          message.open({
            type: "warning",
            content: (
              <div style={{ maxWidth: 350 }}>
                <div
                  style={{ fontWeight: 600, color: "#16a34a", marginBottom: 4 }}
                >
                  ✅ {successfulIndices.length} shift
                  {successfulIndices.length > 1 ? "s" : ""} created successfully
                </div>
                <div
                  style={{ fontWeight: 600, color: "#dc2626", marginBottom: 4 }}
                >
                  ❌ {failedShifts.length} shift
                  {failedShifts.length > 1 ? "s" : ""} failed
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    borderTop: "1px solid #e5e7eb",
                    paddingTop: 4,
                    marginTop: 4,
                  }}
                >
                  {uniqueErrors}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 4 }}>
                  Failed shifts remain in modal for editing
                </div>
              </div>
            ),
            duration: 6,
          });

          const createdShiftIds = extractCreatedShiftIds(results);
          const createdDays = extractCreatedDays(results);
          const partialPayload = {
            ...premiereResult,
            submittedDates: successfulIndices
              .map((idx) => String(shifts[idx]?.date || "").trim())
              .filter(Boolean),
            ...(createdShiftIds.length ? { createdShiftIds } : {}),
            ...(createdDays.length ? { createdDays } : {}),
          };
          if (createdShiftIds.length > 0) {
            pendingOnSuccessRef.current = partialPayload;
            setPostCreateInviteShiftId(createdShiftIds[0]);
          } else {
            onSuccess?.(partialPayload);
          }
        } else {
          const uniqueErrors = Array.from(
            new Set(failedShifts.map((f) => f.message)),
          ).join("; ");
          message.error(`All shifts failed: ${uniqueErrors}`);
        }
      }
    } catch (error) {
      console.error(
        isEditMode ? "Failed to update shifts:" : "Failed to create shifts:",
        error,
      );
      message.error(
        isEditMode
          ? "Failed to update shift. Please try again."
          : "Failed to create shifts. Please try again.",
      );
    } finally {
      setCreateShiftModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[11000] flex items-center justify-center bg-black/50 animate-modalBgIn p-4"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl flex flex-col w-[min(1480px,99vw)] max-w-[99vw] overflow-hidden"
        style={{ height: MODAL_SHELL_HEIGHT, maxHeight: MODAL_SHELL_HEIGHT }}
      >
        {/* Compact Header */}
        <div className="flex items-start sm:items-center justify-between gap-3 px-4 py-2 border-b border-gray-100 shrink-0">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0">
            <h2 className="text-base sm:text-lg font-bold text-gray-900 shrink-0">
              {isEditMode ? (
                "Edit Shift"
              ) : isAiReview ? (
                "Validate AI shift"
              ) : (
                <span className="inline-flex items-center gap-2">
                  <StatDoctorWordmark className="h-4" />
                  <span className="text-sm font-semibold text-gray-600">
                    Post shift
                  </span>
                </span>
              )}
            </h2>
            <div className="flex items-center gap-1">
              {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((s) => (
                <div key={s} className="flex items-center">
                  <button
                    onClick={() => {
                      if (s <= createShiftModal.step) {
                        setCreateShiftModal((prev) => ({
                          ...prev,
                          step: s,
                          reviewSelectedIndex: 0,
                        }));
                      }
                    }}
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
                      createShiftModal.step === s
                        ? "bg-indigo-600 text-white scale-110"
                        : createShiftModal.step > s
                          ? "bg-green-500 text-white hover:bg-green-600 cursor-pointer hover:scale-110"
                          : "bg-gray-200 text-gray-500"
                    }`}
                  >
                    {createShiftModal.step > s ? "✓" : s}
                  </button>
                  {s < TOTAL_STEPS && (
                    <div
                      className={`w-6 h-0.5 mx-1 transition-all duration-300 ${createShiftModal.step > s ? "bg-green-500" : "bg-gray-200"}`}
                    />
                  )}
                </div>
              ))}
            </div>
            <span className="text-xs sm:text-sm text-gray-500 truncate">
              {createShiftModal.step === 1 &&
                (isClinicNetworkExperience &&
                !clinicNetworkPickerDone &&
                !isEditMode
                  ? "Choose brand"
                  : "Select Dates")}
              {createShiftModal.step === 2 &&
                (isClinicNetworkExperience ? "Role" : "Role & eligibility")}
              {createShiftModal.step === 3 && "Transport & extras"}
              {createShiftModal.step === 4 && "Time & Rate"}
              {createShiftModal.step === 5 && "Review"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={closeModal}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-500" />
            </button>
          </div>
        </div>

        {isClinicNetworkExperience &&
          selectedBrandId &&
          (clinicNetworkPickerDone || isEditMode) && (
          <div className="px-4 py-1.5 border-b border-slate-100 bg-slate-50 shrink-0 flex items-center gap-2">
            {resolveHospitalImageUrl(
              selectedBrand?.hospitalBackground,
              selectedBrand?.hospitalImage,
            ) ? (
              <img
                src={resolveHospitalImageUrl(
                  selectedBrand?.hospitalBackground,
                  selectedBrand?.hospitalImage,
                )}
                alt=""
                className="w-6 h-6 rounded object-cover border border-slate-200 shrink-0"
              />
            ) : null}
            <p className="text-sm font-medium text-slate-800 truncate">
              {selectedBrand?.name}
            </p>
            <span className="text-xs text-slate-500 shrink-0">
              {selectedClinicIds.length} clinic
              {selectedClinicIds.length === 1 ? "" : "s"}
            </span>
            <button
              type="button"
              className="ml-auto shrink-0 text-xs font-semibold text-indigo-700 hover:text-indigo-900 px-2 py-1"
              onClick={() => {
                setSelectedBrandId("");
                setSelectedClinicIds([]);
                setClinicQuery("");
                clinicDefaultsForBrandRef.current = null;
                setClinicNetworkPickerDone(false);
                setCreateShiftModal((prev) => ({
                  ...prev,
                  step: 1,
                  description: "",
                  shifts: prev.shifts.map((shift) => ({
                    ...shift,
                    rate: "",
                    rateType: "contracted",
                  })),
                }));
              }}
            >
              Change
            </button>
          </div>
        )}

        {createShiftModal.loading ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Spin size="large" />
          </div>
        ) : (
          <>
            {isAiReview && (showPublishHospitalPicker || prefill?.fieldSources) && (
              <div className="px-6 py-2.5 border-b border-gray-100 bg-slate-50/80 shrink-0 space-y-2">
                {showPublishHospitalPicker && (
                  <div className="flex flex-wrap items-center gap-3">
                    <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 shrink-0">
                      Publish to hospital
                    </label>
                    <select
                      value={publishHospitalId}
                      onChange={(e) => setPublishHospitalId(e.target.value)}
                      className="min-w-[220px] max-w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 shadow-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
                    >
                      {publishHospitalOptions.map((opt) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {prefill?.fieldSources && (
                  <AiShiftProvenancePanel
                    fieldSources={prefill.fieldSources}
                    specialtyAlternatives={prefill.specialtyAlternatives}
                    skillLevelAlternatives={prefill.skillLevelAlternatives}
                    defaultExpanded={false}
                    compact
                  />
                )}
              </div>
            )}
            {/* Content Area */}
            <div className="flex-1 min-h-0 overflow-hidden flex flex-col relative">
              <div
                className={`h-full min-h-0 flex flex-col ${hasPrefillDocuments ? (isDocumentRailCollapsed ? "pr-20" : "pr-[520px]") : ""}`}
              >
                {/* ========== STEP 1: Dates ========== */}
                {createShiftModal.step === 1 && (
                  <div
                    className={
                      isClinicNetworkExperience &&
                      !clinicNetworkPickerDone &&
                      !isEditMode
                        ? "p-3 flex flex-col h-full min-h-0 overflow-y-auto"
                        : "p-6 flex flex-col h-full min-h-0 overflow-hidden"
                    }
                  >
                    {showRosterUpload && (
                      <input
                        ref={rosterFileInputRef}
                        type="file"
                        accept=".pdf,.xlsx,.xlsm,.xls,.csv,.docx,.txt,.md"
                        className="hidden"
                        disabled={aiImportLocked}
                        onChange={(event) => {
                          const files = event.target.files;
                          if (files?.length) {
                            void handleRosterFiles(files);
                          }
                          event.target.value = "";
                        }}
                      />
                    )}
                    {isClinicNetworkExperience &&
                    !clinicNetworkPickerDone &&
                    !isEditMode ? (
                      <div className="flex-1 min-h-0 overflow-y-auto flex flex-col">
                        <p className="text-sm text-slate-500 mb-3 shrink-0">
                          Pick Jones Radiology or I-MED Radiology, then tick the
                          clinics for this locum week.
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 shrink-0 items-stretch">
                          {clinicBrands.map((brand) => {
                            const selected = selectedBrandId === brand.id;
                            const coverUrl = resolveHospitalImageUrl(
                              brand.hospitalBackground,
                            );
                            const logoUrl = resolveHospitalImageUrl(
                              brand.hospitalImage,
                            );
                            return (
                            <div
                              key={brand.id}
                                className={`relative h-full min-h-[10rem] sm:min-h-[13.5rem] rounded-2xl border overflow-hidden bg-white flex flex-col transition-all ${
                                selected
                                  ? "border-indigo-500 ring-2 ring-indigo-200"
                                  : "border-slate-200 hover:border-indigo-400"
                              }`}
                            >
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedBrandId(brand.id);
                                  setClinicQuery("");
                                  clinicDefaultsForBrandRef.current = null;
                                  setSelectedClinicIds([]);
                                }}
                                className="w-full text-left flex-1 flex flex-col"
                              >
                                <div className="h-24 sm:h-32 bg-slate-100 relative shrink-0">
                                  {coverUrl ? (
                                    <img
                                      src={coverUrl}
                                      alt=""
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex flex-col items-center justify-center text-slate-400 gap-1">
                                      <Camera size={18} />
                                      <span className="text-[11px] font-medium">
                                        Add a cover image
                                      </span>
                                    </div>
                                  )}
                                  {logoUrl ? (
                                    <img
                                      src={logoUrl}
                                      alt=""
                                      className="absolute left-3 bottom-3 w-10 h-10 rounded-lg object-cover border-2 border-white shadow"
                                    />
                                  ) : null}
                                </div>
                                <div className="px-4 py-3 mt-auto">
                                  <div className="font-semibold text-slate-900 truncate leading-tight">
                                    {brand.name}
                                  </div>
                                  <p className="text-xs text-slate-500 mt-1 truncate">
                                    {clinicNetworkSitesForBrand(hospitalList, brand.id).length}{" "}
                                    clinics
                                    {selected
                                      ? ` · ${selectedClinicIds.length} selected`
                                      : ""}
                                  </p>
                                </div>
                              </button>
                              <label
                                className="absolute top-3 right-3 inline-flex items-center gap-1 text-[11px] font-medium text-white bg-black/55 hover:bg-black/70 rounded-lg px-2 py-1 cursor-pointer"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Camera size={12} />
                                {brandImageUploadingId === brand.id
                                  ? "Saving…"
                                  : coverUrl
                                    ? "Change cover"
                                    : "Add cover"}
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="hidden"
                                  disabled={brandImageUploadingId === brand.id}
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    e.target.value = "";
                                    if (file) {
                                      void handleBrandImageUpload(
                                        brand.id,
                                        file,
                                        "cover",
                                      );
                                    }
                                  }}
                                />
                              </label>
                            </div>
                            );
                          })}
                        </div>
                        {clinicBrands.length === 0 ? (
                          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-10 text-center text-sm text-slate-500">
                            Jones Radiology and I-MED Radiology will show here
                            once the clinic network has loaded.
                          </div>
                        ) : null}
                        {selectedBrandId ? (
                          <div className="mt-4 min-h-[16rem] max-h-[min(52vh,28rem)] rounded-xl border border-slate-200 bg-white p-3 flex flex-col">
                            <div className="flex items-center justify-between gap-2 mb-2 shrink-0">
                              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Clinics
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  className="text-[11px] font-medium text-indigo-600"
                                  onClick={() =>
                                    setSelectedClinicIds(
                                      pickPriorityClinicIds(
                                        clinicNetworkSitesForBrand(
                                          hospitalList,
                                          selectedBrandId,
                                        ),
                                        selectedBrand?.name,
                                        selectedBrandId,
                                      ),
                                    )
                                  }
                                >
                                  Priority
                                </button>
                                <button
                                  type="button"
                                  className="text-[11px] font-medium text-indigo-600"
                                  onClick={() =>
                                    setSelectedClinicIds(
                                      selectedClinicIds.length ===
                                      clinicSites.length
                                        ? []
                                        : clinicSites.map((site) => site.id),
                                    )
                                  }
                                >
                                  {selectedClinicIds.length ===
                                  clinicSites.length
                                    ? "Clear"
                                    : "Select all"}
                                </button>
                              </div>
                            </div>
                            <input
                              value={clinicQuery}
                              onChange={(e) => setClinicQuery(e.target.value)}
                              placeholder="Search suburb"
                              className="w-full mb-2 rounded-lg border border-slate-200 px-3 py-1.5 text-sm shrink-0"
                            />
                            <div
                              className="flex-1 overflow-y-auto space-y-1 min-h-0 overscroll-contain"
                              onWheel={(e) => e.stopPropagation()}
                            >
                              {visibleClinicSites.map((site) => {
                                const checked = selectedClinicIds.includes(site.id);
                                return (
                                  <label
                                    key={site.id}
                                    className={`flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm cursor-pointer ${
                                      checked
                                        ? "bg-indigo-50 text-indigo-900"
                                        : "hover:bg-slate-50"
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={checked}
                                      onChange={() =>
                                        setSelectedClinicIds((prev) =>
                                          prev.includes(site.id)
                                            ? prev.filter((id) => id !== site.id)
                                            : [...prev, site.id],
                                        )
                                      }
                                    />
                                    <span className="truncate flex-1">
                                      {clinicSiteShortLabel(site.name) ||
                                        site.name}
                                    </span>
                                    {isPriorityClinic(
                                      site,
                                      selectedBrand?.name,
                                    ) ? (
                                      <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                                        Priority
                                      </span>
                                    ) : null}
                                  </label>
                                );
                              })}
                              {visibleClinicSites.length === 0 && (
                                <div className="text-xs text-slate-400 px-1 py-2">
                                  No clinics match that search.
                                </div>
                              )}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 mt-4">
                            Select a brand to choose clinics.
                          </p>
                        )}
                      </div>
                    ) : (
                    <div className="flex gap-6 flex-1 overflow-hidden min-h-0">
                      {/* LEFT - Calendar */}
                      {showCalendarPanel && (
                      <div className="w-[380px] flex-shrink-0 overflow-y-auto">
                        <div className="flex items-center justify-between mb-3">
                          <button
                            type="button"
                            onClick={() =>
                              setModalCalendarDate(
                                subMonths(modalCalendarDate, 1),
                              )
                            }
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <ChevronLeft size={20} className="text-gray-600" />
                          </button>
                          <span className="text-base font-semibold text-gray-800">
                            {format(modalCalendarDate, "MMMM yyyy")}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              setModalCalendarDate(
                                addMonths(modalCalendarDate, 1),
                              )
                            }
                            className="p-2 hover:bg-gray-100 rounded-lg"
                          >
                            <ChevronRight size={20} className="text-gray-600" />
                          </button>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 select-none shadow-inner relative">
                          {calendarLocked && (
                            <div className="absolute inset-0 z-10 rounded-xl bg-white/75 flex items-center justify-center px-4 text-center">
                              <p className="text-xs font-medium text-indigo-800">
                                {rosterUploadLoading || emailPasteLoading
                                  ? "Sending to AI Shifts…"
                                  : "Using roster / pasted email — clear that to pick dates on the calendar"}
                              </p>
                            </div>
                          )}
                          <div
                            className={
                              calendarLocked ? "pointer-events-none opacity-50" : ""
                            }
                          >
                          <div className="grid grid-cols-7 gap-1.5 mb-2">
                            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d, i) => (
                              <div
                                key={i}
                                className="h-8 flex items-center justify-center text-xs font-semibold text-gray-400 uppercase tracking-wide"
                              >
                                {d}
                              </div>
                            ))}
                          </div>
                          <div
                            className="grid grid-cols-7 gap-1.5"
                            style={{ touchAction: "none" }}
                            onPointerDown={handleCalendarPointerDown}
                            onPointerMove={handleCalendarPointerMove}
                            onPointerUp={endDateDrag}
                            onPointerCancel={endDateDrag}
                          >
                            {modalAllDays.map((day, i) => {
                              const dateStr = format(day, "yyyy/MM/dd");
                              const isCurrentMonth = isSameMonth(
                                day,
                                modalCalendarDate,
                              );
                              const isSelected =
                                createShiftModal.selectedDates.includes(
                                  dateStr,
                                );
                              const isPast =
                                day < new Date(new Date().setHours(0, 0, 0, 0));
                              const isToday = isSameDay(day, new Date());
                              return (
                                <div
                                  key={i}
                                  data-shift-date={dateStr}
                                  data-shift-past={isPast ? "1" : "0"}
                                  data-shift-current={isCurrentMonth ? "1" : "0"}
                                  className={`h-10 rounded-lg text-sm font-medium cursor-pointer flex items-center justify-center ${
                                    isSelected
                                      ? "bg-indigo-600 text-white"
                                      : isPast
                                        ? "text-gray-300 cursor-not-allowed"
                                        : isCurrentMonth
                                          ? `text-gray-700 hover:bg-indigo-100 ${isToday ? "ring-2 ring-indigo-500 ring-inset font-bold" : ""}`
                                          : "text-gray-300"
                                  }`}
                                >
                                  {format(day, "d")}
                                </div>
                              );
                            })}
                          </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-400 mt-3 text-center">
                          Click a date for that locum week (Mon–Sun), or drag a custom range
                        </p>
                        {createShiftModal.shifts.length > 1 && (
                          <div className="shrink-0 mt-2 flex gap-2">
                            <button
                              type="button"
                              disabled={isPostModeLocked}
                              onClick={() => {
                                if (isPostModeLocked) return;
                                const hasAnyOnCall =
                                  createShiftModal.shifts.some((s) => s.onCall);
                                const firstOnCallDetails =
                                  createShiftModal.shifts.find((s) => s.onCall)
                                    ?.onCallDetails || "";
                                setCreateShiftModal((prev) => ({
                                  ...prev,
                                  postMode: "group",
                                  shifts: hasAnyOnCall
                                    ? prev.shifts.map((s) => ({
                                        ...s,
                                        onCall: true,
                                        onCallDetails: firstOnCallDetails,
                                      }))
                                    : prev.shifts,
                                }));
                              }}
                              className={`flex-1 min-w-0 py-1.5 px-2 rounded-lg text-xs font-semibold ${
                                createShiftModal.postMode === "group"
                                  ? "bg-indigo-600 text-white"
                                  : "bg-white text-gray-600 border border-gray-200"
                              }`}
                            >
                              Group (1 post)
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setCreateShiftModal((prev) => ({
                                  ...prev,
                                  postMode: "separate",
                                }))
                              }
                              className={`flex-1 min-w-0 py-1.5 px-2 rounded-lg text-xs font-semibold ${
                                createShiftModal.postMode === "separate"
                                  ? "bg-purple-600 text-white"
                                  : "bg-white text-gray-600 border border-gray-200"
                              }`}
                            >
                              Separate ({createShiftModal.shifts.length})
                            </button>
                          </div>
                        )}

                        {isEditMode &&
                          (addedDates.length > 0 ||
                            removedDates.length > 0) && (
                            <div className="mt-4 space-y-3 animate-slideUp">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                Edit changes
                              </p>
                              {addedDates.length > 0 && (
                                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5">
                                  <div className="text-xs font-semibold text-emerald-700 mb-1">
                                    Added dates
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {addedDates.map((date) => (
                                      <span
                                        key={`added-${date}`}
                                        className="px-2 py-1 rounded-lg bg-white text-emerald-700 text-xs font-medium border border-emerald-100"
                                      >
                                        {format(
                                          new Date(date.replace(/\//g, "-")),
                                          "EEE, MMM d",
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {removedDates.length > 0 && (
                                <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2.5">
                                  <div className="text-xs font-semibold text-red-700 mb-1">
                                    Removed dates
                                  </div>
                                  <div className="flex flex-wrap gap-1.5">
                                    {removedDates.map((date) => (
                                      <span
                                        key={`removed-${date}`}
                                        className="px-2 py-1 rounded-lg bg-white text-red-700 text-xs font-medium border border-red-100"
                                      >
                                        {format(
                                          new Date(date.replace(/\//g, "-")),
                                          "EEE, MMM d",
                                        )}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                      )}

                      {/* RIGHT - Selected Dates / AI import */}
                      <div className="flex-1 min-w-0 flex flex-col overflow-hidden h-full max-h-full">
                        <div
                          className="flex-1 flex flex-col overflow-hidden"
                          style={{ animationDelay: "100ms" }}
                        >
                          {showEmailPaste ? (
                            <div className="flex-1 flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm min-h-0 h-full">
                              <div className="px-3 py-2.5 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-2 flex-shrink-0">
                                <span className="text-sm font-semibold text-gray-700 inline-flex items-center gap-1.5">
                                  <Mail className="w-4 h-4 text-indigo-600" />
                                  Shift request email
                                </span>
                                <div className="flex items-center gap-2">
                                  {(emailPasteText.trim() ||
                                    emailPasteHtml.trim()) && (
                                    <button
                                      type="button"
                                      disabled={emailPasteLoading}
                                      onClick={replaceEmailPaste}
                                      className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                                    >
                                      Replace
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    disabled={emailPasteLoading}
                                    onClick={cancelEmailPaste}
                                    className="text-xs text-gray-500 hover:text-gray-800 font-medium"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                              <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                                {emailPasteReading ? (
                                  <div className="flex-1 flex flex-col items-center justify-center gap-3 text-sm text-gray-500 px-4">
                                    <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                                    <p>Reading clipboard…</p>
                                  </div>
                                ) : emailPasteText.trim() ||
                                  emailPasteHtml.trim() ? (
                                  <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
                                    <PastedEmailDisplay
                                      text={emailPasteText}
                                      html={emailPasteHtml}
                                      className="flex-1 min-h-0 h-full"
                                    />
                                  </div>
                                ) : (
                                  <textarea
                                    ref={emailPasteTextareaRef}
                                    disabled={aiImportLocked || emailPasteLoading}
                                    className="flex-1 w-full min-h-0 h-full resize-none border-0 px-4 py-4 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-200 placeholder:text-gray-400 leading-relaxed disabled:cursor-not-allowed disabled:bg-gray-50"
                                    placeholder={
                                      "Copy a shift email from Gmail, then paste here:\n• Right-click → Paste\n• Ctrl+V / ⌘V"
                                    }
                                    onPaste={(event) => {
                                      if (aiImportLocked) return;
                                      event.preventDefault();
                                      applyEmailPasteFromClipboard(
                                        event.clipboardData,
                                      );
                                    }}
                                  />
                                )}
                              </div>
                              <div className="px-3 py-2.5 border-t border-gray-100 flex items-center justify-between gap-2 bg-gray-50/80 flex-shrink-0">
                                <span className="text-[10px] text-gray-500">
                                  Each vacancy row → one AI Shifts card (contract dates, not daily)
                                </span>
                                <button
                                  type="button"
                                  disabled={
                                    aiImportLocked ||
                                    emailPasteLoading ||
                                    !pastedEmailHasEnoughContent(
                                      emailPasteText,
                                      emailPasteHtml,
                                    )
                                  }
                                  onClick={() => void handleEmailPasteQueue()}
                                  className="px-3 py-1.5 text-xs font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5 shrink-0"
                                >
                                  {emailPasteLoading ? (
                                    <>
                                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                      Sending…
                                    </>
                                  ) : (
                                    <>Send to AI Shifts</>
                                  )}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <>
                          <div className="flex items-center justify-between mb-3 flex-shrink-0">
                            <span className="text-base font-semibold text-gray-700 flex items-center gap-2">
                              <span className="w-6 h-6 bg-green-100 rounded flex items-center justify-center text-sm">
                                📅
                              </span>
                              Selected Dates
                              <span className="px-2.5 py-0.5 bg-indigo-100 text-indigo-700 rounded-full text-sm font-bold animate-bounce-subtle">
                                {createShiftModal.shifts.length}
                              </span>
                            </span>
                            <div className="flex items-center gap-2">
                              {!isEditMode && (
                                <button
                                  type="button"
                                  onClick={() => setShowApplyTemplate(true)}
                                  className="text-xs font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-lg hover:bg-amber-100"
                                >
                                  Apply template
                                </button>
                              )}
                            {createShiftModal.shifts.length > 0 && (
                              <button
                                onClick={() => {
                                  setCreateShiftModal((prev) => ({
                                    ...prev,
                                    selectedDates: [],
                                    shifts: [],
                                  }));
                                  setCreateInputMode(null);
                                }}
                                className="text-xs text-red-500 hover:text-red-600 font-medium hover:bg-red-50 px-2 py-1 rounded-lg transition-all duration-200"
                              >
                                🗑️ Clear all
                              </button>
                            )}
                            </div>
                          </div>
                          {showAiImportPanel && (
                            <>
                              <div
                                onDragEnter={(event) => {
                                  if (aiImportLocked) return;
                                  setShowEmailPaste(false);
                                  setCreateInputMode("ai_import");
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setRosterDragActive(true);
                                }}
                                onDragOver={(event) => {
                                  if (aiImportLocked) return;
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setRosterDragActive(true);
                                }}
                                onDragLeave={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setRosterDragActive(false);
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (aiImportLocked) {
                                    message.info(
                                      "Clear selected dates to upload a roster or paste an email",
                                    );
                                    setRosterDragActive(false);
                                    return;
                                  }
                                  if (event.dataTransfer.files?.length) {
                                    void handleRosterFiles(event.dataTransfer.files);
                                  } else {
                                    setRosterDragActive(false);
                                  }
                                }}
                                onClick={() => {
                                  if (aiImportLocked) {
                                    message.info(
                                      "Clear selected dates to upload a roster or paste an email",
                                    );
                                    return;
                                  }
                                  if (!rosterUploadLoading) {
                                    setShowEmailPaste(false);
                                    setCreateInputMode("ai_import");
                                    rosterFileInputRef.current?.click();
                                  }
                                }}
                                className={`mb-3 rounded-xl border-2 border-dashed px-4 py-4 text-center transition-all duration-300 flex-shrink-0 ${
                                  aiImportLocked
                                    ? "border-gray-200 bg-gray-50 opacity-60 cursor-not-allowed"
                                    : rosterDragActive
                                      ? "border-indigo-500 bg-indigo-50 scale-[1.01] shadow-md cursor-pointer"
                                      : "border-indigo-200 bg-white hover:border-indigo-400 hover:bg-indigo-50/40 cursor-pointer"
                                } ${rosterUploadLoading ? "pointer-events-none opacity-80" : ""}`}
                              >
                                <div className="flex flex-col items-center gap-2">
                                  <div
                                    className={`w-12 h-12 rounded-full flex items-center justify-center ${
                                      rosterUploadLoading
                                        ? "bg-indigo-100"
                                        : "bg-indigo-50 animate-pulse"
                                    }`}
                                  >
                                    {rosterUploadLoading ? (
                                      <Loader2 className="w-6 h-6 text-indigo-600 animate-spin" />
                                    ) : (
                                      <UploadCloud className="w-6 h-6 text-indigo-600" />
                                    )}
                                  </div>
                                  <div>
                                    <p className="text-sm font-semibold text-indigo-900">
                                      {aiImportLocked
                                        ? "Clear dates to use AI import"
                                        : rosterUploadLoading
                                          ? "Sending to AI Shifts…"
                                          : "Drop your roster here"}
                                    </p>
                                    <p className="text-xs text-indigo-700/80 mt-1">
                                      {aiImportLocked
                                        ? "Pick dates on the calendar, or clear them first"
                                        : "Drag, click, or paste PDF / Excel / CSV / Word → AI Shifts"}
                                    </p>
                                  </div>
                                  {!rosterUploadLoading && !aiImportLocked && (
                                    <span className="inline-flex items-center gap-1.5 text-[11px] font-medium text-indigo-600 bg-indigo-100/80 px-2.5 py-1 rounded-full">
                                      <FileSpreadsheet className="w-3.5 h-3.5" />
                                      → AI Shifts list
                                    </span>
                                  )}
                                </div>
                              </div>

                              <div className="mb-3 flex-shrink-0">
                                <button
                                  type="button"
                                  disabled={aiImportLocked}
                                  onClick={() => void openEmailPastePanel()}
                                  className="w-full text-xs font-semibold text-indigo-700 hover:text-indigo-900 disabled:text-gray-400 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1.5 py-2 rounded-lg border border-indigo-100 bg-indigo-50/50 hover:bg-indigo-50"
                                >
                                  <Mail className="w-3.5 h-3.5" />
                                  Or paste a copied shift email
                                </button>
                              </div>
                            </>
                          )}
                          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 flex-1 overflow-y-auto shadow-inner min-h-0">
                            {createShiftModal.shifts.length === 0 ? (
                              <div className="h-full flex flex-col items-center justify-center text-base text-gray-400 px-4">
                                <div className="w-20 h-20 bg-gray-200 rounded-full flex items-center justify-center mb-4">
                                  <Calendar
                                    size={32}
                                    className="text-gray-400"
                                  />
                                </div>
                                <p className="font-medium text-gray-500 text-lg">
                                  No dates yet
                                </p>
                                <p className="text-sm mt-1.5 text-center leading-relaxed max-w-sm">
                                  {showRosterUpload
                                    ? calendarLocked
                                      ? "Finish AI import or clear it to pick dates"
                                      : aiImportLocked
                                        ? "Selected dates shown above — clear to use roster or email import"
                                        : "Pick dates on the calendar, or use roster / email import above"
                                    : "Click or drag on the calendar to select dates"}
                                </p>
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {createShiftModal.shifts.map((s, idx) => (
                                  <div
                                    key={`${s.date}-${s.startTime}-${s.endTime}-${idx}`}
                                    className="flex items-center justify-between bg-white rounded-xl px-4 py-3 shadow-sm border border-transparent hover:border-indigo-100"
                                    style={{ animationDelay: `${idx * 30}ms` }}
                                  >
                                    <div className="flex items-center gap-3">
                                      <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex flex-col items-center justify-center text-white shadow-md">
                                        <span className="text-xs font-medium opacity-80">
                                          {format(
                                            new Date(
                                              s.date.replace(/\//g, "-"),
                                            ),
                                            "MMM",
                                          )}
                                        </span>
                                        <span className="text-lg font-bold -mt-0.5">
                                          {format(
                                            new Date(
                                              s.date.replace(/\//g, "-"),
                                            ),
                                            "d",
                                          )}
                                        </span>
                                      </div>
                                      <div>
                                        <div className="text-sm font-semibold text-gray-800">
                                          {s.label ||
                                            format(
                                              new Date(
                                                s.date.replace(/\//g, "-"),
                                              ),
                                              "EEEE",
                                            )}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                          {format(
                                            new Date(
                                              s.date.replace(/\//g, "-"),
                                            ),
                                            "EEE d MMM yyyy",
                                          )}
                                          {s.startTime
                                            ? ` · ${s.startTime} ${s.startAMPM}–${s.endTime} ${s.endAMPM}`
                                            : ""}
                                        </div>
                                      </div>
                                    </div>
                                    <button
                                      onClick={() => {
                                        setCreateShiftModal((prev) => {
                                          const next = prev.shifts.filter(
                                            (_, index) => index !== idx,
                                          );
                                          return {
                                            ...prev,
                                            shifts: next,
                                            selectedDates: Array.from(
                                              new Set(
                                                next.map((item) => item.date),
                                              ),
                                            ),
                                          };
                                        });
                                      }}
                                      className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500 transition-all duration-200 hover:scale-110 active:scale-95"
                                    >
                                      <X size={16} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    )}
                  </div>
                )}

                {/* ========== STEP 2: Role & eligibility ========== */}
                {createShiftModal.step === 2 && (
                  <div className="p-4 sm:p-6 animate-fadeSlideIn flex flex-col h-full min-h-0 overflow-y-auto">
                    <div className="flex flex-col xl:flex-row gap-6 xl:gap-8 flex-1 min-h-0">
                      {/* Left — role fields */}
                      <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-y-auto pr-1">
                        {!isEditMode && !isAiReview && !isClinicNetworkExperience && (
                          <div className="mb-5 shrink-0 flex items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
                            <div className="flex items-center gap-2.5 min-w-0">
                              <span className="text-base leading-none">✨</span>
                              <div className="min-w-0">
                                <p className="text-sm font-semibold text-amber-900">
                                  Quick Templates
                                </p>
                                <p className="text-xs text-amber-700/80">
                                  Auto-fill specialty, skill & support
                                </p>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowApplyTemplate(true);
                              }}
                              className="shrink-0 px-3 py-1.5 rounded-lg bg-white border border-amber-200 text-sm font-semibold text-amber-800 hover:bg-amber-100 flex items-center gap-1 transition-colors"
                            >
                              Apply template
                              <ChevronRight size={14} />
                            </button>
                          </div>
                        )}

                        <div className="flex flex-col gap-8 flex-1 min-h-0">
                          <div className="space-y-6">
                            <div>
                              <label className={stepFieldLabelClass}>
                                <span className={stepFieldIconClass}>🩺</span>
                                Specialty{" "}
                                <span className="text-red-500">*</span>
                                {isAiReview && specialtyFieldMapped ? (
                                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                    Mapped
                                  </span>
                                ) : null}
                              </label>
                              <div
                                className={
                                  isAiReview && specialtyFieldMapped
                                    ? `${mappedFieldHighlightClass} rounded-xl`
                                    : undefined
                                }
                              >
                                <SearchableSelectField
                                  value={createShiftModal.specialty}
                                  onChange={(specialty) =>
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      specialty,
                                    }))
                                  }
                                  options={
                                    isClinicNetworkExperience
                                      ? clinicSpecialtyOptions
                                      : createShiftModal.specialtyOptions
                                  }
                                  placeholder="Search specialty…"
                                />
                              </div>
                              {isAiReview && specialtyFieldMapped ? (
                                <p className="text-xs text-amber-800 mt-1.5 font-medium">
                                  Email text was mapped to this menu label — please
                                  confirm.
                                </p>
                              ) : null}
                            </div>

                            <div>
                              <label className={stepFieldLabelClass}>
                                <span
                                  className={`${stepFieldIconClass} from-purple-100 to-pink-100`}
                                >
                                  ⭐
                                </span>
                                Skill level{" "}
                                <span className="text-red-500">*</span>
                                <SkillLevelWithInfo
                                  infoOnly
                                  showInfo
                                  className="ml-1 inline-flex"
                                  infoClassName="h-4 w-4"
                                />
                                {isAiReview && skillFieldMapped ? (
                                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                                    Mapped
                                  </span>
                                ) : null}
                              </label>
                              <div
                                className={
                                  isAiReview && skillFieldMapped
                                    ? `${mappedFieldHighlightClass} rounded-xl`
                                    : undefined
                                }
                              >
                                <SearchableSelectField
                                  value={createShiftModal.skillLevel}
                                  onChange={(skillLevel) =>
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      skillLevel,
                                    }))
                                  }
                                  options={createShiftModal.skillOptions}
                                  placeholder="Search skill level…"
                                />
                              </div>
                              {isAiReview && skillFieldMapped ? (
                                <p className="text-xs text-amber-800 mt-1.5 font-medium">
                                  Email text was mapped to this menu label — please
                                  confirm.
                                </p>
                              ) : null}
                            </div>

                            <div>
                              <label className={stepFieldLabelClass}>
                                <span
                                  className={`${stepFieldIconClass} from-green-100 to-emerald-100`}
                                >
                                  👥
                                </span>
                                Support level
                              </label>
                              <SearchableSelectField
                                value={createShiftModal.supportLevel}
                                onChange={(supportLevel) =>
                                  setCreateShiftModal((prev) => ({
                                    ...prev,
                                    supportLevel,
                                  }))
                                }
                                options={SUPPORT_LEVEL_OPTIONS}
                                placeholder="Search support level…"
                              />
                            </div>
                          </div>

                          <div className="flex items-center justify-between gap-4 pt-5 mt-auto border-t border-gray-100 shrink-0">
                            <label className="text-base font-semibold text-gray-700">
                              Doctors needed
                              <span className="block text-sm font-normal text-gray-400 mt-0.5">
                                Optional · hospital only
                              </span>
                            </label>
                            <div className="inline-flex items-center rounded-xl border-2 border-gray-200 bg-white overflow-hidden">
                              <button
                                type="button"
                                onClick={() =>
                                  setPositionsRequired((prev) =>
                                    Math.max(1, prev - 1),
                                  )
                                }
                                className="px-4 py-3 text-gray-500 hover:text-indigo-600 hover:bg-gray-50 transition-colors"
                                aria-label="Decrease"
                              >
                                <Minus size={18} />
                              </button>
                              <span className="w-12 text-center text-lg font-semibold text-gray-900 tabular-nums">
                                {positionsRequired}
                              </span>
                              <button
                                type="button"
                                onClick={() =>
                                  setPositionsRequired((prev) =>
                                    Math.min(99, prev + 1),
                                  )
                                }
                                className="px-4 py-3 text-gray-500 hover:text-indigo-600 hover:bg-gray-50 transition-colors"
                                aria-label="Increase"
                              >
                                <Plus size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Right — description + also open to */}
                      <div className="flex-1 flex flex-col min-h-0 min-w-0 xl:border-l xl:border-gray-100 xl:pl-8">
                        <div className="flex flex-col flex-1 min-h-0 mb-5">
                          <label className={`${stepFieldLabelClass} shrink-0`}>
                            <span
                              className={`${stepFieldIconClass} from-amber-100 to-orange-100`}
                            >
                              📝
                            </span>
                            Description
                            <span className="text-sm font-normal text-gray-400">
                              optional
                            </span>
                          </label>
                          <div className="flex-1 min-h-0 mt-1">
                            <textarea
                              ref={descriptionTextareaRef}
                              placeholder="Additional details about the shift..."
                              value={createShiftModal.description}
                              onChange={(e) =>
                                setCreateShiftModal((prev) => ({
                                  ...prev,
                                  description: e.target.value,
                                }))
                              }
                              className={`${fieldSelectClass} min-h-[140px] h-full w-full bg-white resize-y overflow-y-auto text-base leading-relaxed`}
                            />
                          </div>
                        </div>

                        <div
                          className={`flex flex-col pt-5 border-t border-gray-100 ${
                            isClinicNetworkExperience
                              ? "shrink-0"
                              : "flex-1 min-h-0 overflow-y-auto"
                          }`}
                        >
                          {isClinicNetworkExperience ? (
                            <div className="space-y-4 text-base text-slate-600 leading-relaxed">
                              <p>
                                Rate is the contracted 1-week locum block for
                                this brand. Change it if this week is different.
                              </p>
                              <p className="text-sm text-slate-500">
                                When a doctor applies we send the Radiology
                                Skills Profile and ask for a CV in the
                                conversation. Before the shift: 2 references,
                                primary degree, AHPRA, and insurance.
                              </p>
                            </div>
                          ) : (
                            <>
                          <AcceptCriteriaEditor
                            criteria={acceptCriteria}
                            onChange={setAcceptCriteria}
                            specialtyOptions={
                              createShiftModal.specialtyOptions
                            }
                            skillOptions={createShiftModal.skillOptions}
                            primarySpecialtyId={createShiftModal.specialty}
                            primarySkillLevelId={createShiftModal.skillLevel}
                            variant="modal"
                            showRates={false}
                            suggestions={
                              pendingEligibleSuggestions.length > 0
                                ? []
                                : eligibilitySuggestionGroups.related
                            }
                            broadSuggestions={
                              pendingEligibleSuggestions.length > 0
                                ? []
                                : eligibilitySuggestionGroups.broad
                            }
                            autoOpenManualAdd={
                              focusEligibilityManualAdd &&
                              pendingEligibleSuggestions.length === 0
                            }
                            highlightManualAdd={
                              eligibilityManualPromptShown &&
                              pendingEligibleSuggestions.length === 0
                            }
                            onAutoOpenManualAddHandled={() =>
                              setFocusEligibilityManualAdd(false)
                            }
                          />
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ========== STEP 3: Transport & extras ========== */}
                {createShiftModal.step === 3 && (
                  <div className={STEP_BODY_CLASS}>
                    <div className="flex flex-col xl:flex-row gap-6 xl:gap-8 flex-1 min-h-0">
                      {/* Left — transport, accommodation & premiere (scrollable stack) */}
                      <div className="flex-1 flex flex-col min-h-0 min-w-0 overflow-y-auto overflow-x-hidden pr-1 gap-5">
                        <div className="shrink-0 min-w-0">
                          <label className="flex items-center justify-between cursor-pointer mb-3">
                            <span className={stepFieldLabelClass + " mb-0"}>
                              <span
                                className={`${stepFieldIconClass} from-blue-100 to-indigo-100`}
                              >
                                🚗
                              </span>
                              Transport included
                            </span>
                            <div
                              className={`w-12 h-7 rounded-full transition-all shrink-0 ${createShiftModal.transportIncluded ? "bg-indigo-600" : "bg-gray-300"} relative`}
                            >
                              <div
                                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${createShiftModal.transportIncluded ? "left-6" : "left-1"}`}
                              />
                            </div>
                            <input
                              type="checkbox"
                              checked={createShiftModal.transportIncluded}
                              onChange={(e) =>
                                setCreateShiftModal((prev) => ({
                                  ...prev,
                                  transportIncluded: e.target.checked,
                                }))
                              }
                              className="sr-only"
                            />
                          </label>
                          {createShiftModal.transportIncluded && (
                            <div className="space-y-4 rounded-2xl border border-blue-100 bg-blue-50/40 p-4">
                              <div className="flex gap-2">
                                {[
                                  { v: "variable", l: "Variable" },
                                  { v: "set", l: "Set rate" },
                                  { v: "other", l: "Other" },
                                ].map((opt) => (
                                  <button
                                    key={opt.v}
                                    type="button"
                                    onClick={() =>
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        transportSelected: opt.v as
                                          | "variable"
                                          | "set"
                                          | "other"
                                          | "",
                                      }))
                                    }
                                    className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${createShiftModal.transportSelected === opt.v ? "bg-indigo-600 text-white shadow-sm" : "bg-white border border-gray-200 text-gray-700 hover:border-indigo-300"}`}
                                  >
                                    {opt.l}
                                  </button>
                                ))}
                              </div>
                              {createShiftModal.transportSelected ===
                                "variable" && (
                                <div className="grid grid-cols-[140px_1fr] gap-3">
                                  <input
                                    type="text"
                                    placeholder="$0.72"
                                    value={createShiftModal.transportRate}
                                    onChange={(e) =>
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        transportRate: e.target.value,
                                      }))
                                    }
                                    className={fieldSelectClass}
                                  />
                                  <select
                                    value={createShiftModal.transportType}
                                    onChange={(e) =>
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        transportType: e.target.value,
                                      }))
                                    }
                                    className={fieldSelectClass}
                                  >
                                    <option value="/km and flights included">
                                      /km + flights included
                                    </option>
                                    <option value="/km no flights included">
                                      /km (no flights)
                                    </option>
                                  </select>
                                </div>
                              )}
                              {createShiftModal.transportSelected === "set" && (
                                <input
                                  type="text"
                                  placeholder="$50 total"
                                  value={createShiftModal.transportRate}
                                  onChange={(e) =>
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      transportRate: e.target.value,
                                    }))
                                  }
                                  className={fieldSelectClass}
                                />
                              )}
                              {createShiftModal.transportSelected ===
                                "other" && (
                                <textarea
                                  ref={transportDetailsRef}
                                  placeholder="Transport details..."
                                  value={createShiftModal.transportOtherDetails}
                                  onChange={(e) => {
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      transportOtherDetails: e.target.value,
                                    }));
                                    adjustTextareaHeight(e.target);
                                  }}
                                  className={`${fieldSelectClass} block w-full min-h-[120px] resize-y overflow-hidden bg-white`}
                                  rows={3}
                                />
                              )}
                            </div>
                          )}
                        </div>

                        <div className="shrink-0 min-w-0 pt-2 border-t border-gray-100">
                          <label className="flex items-center justify-between cursor-pointer mb-3 gap-3">
                            <span className={stepFieldLabelClass + " mb-0"}>
                              <span
                                className={`${stepFieldIconClass} from-purple-100 to-pink-100`}
                              >
                                🏨
                              </span>
                              Accommodation included
                            </span>
                            <div
                              className={`w-12 h-7 rounded-full transition-all shrink-0 ${createShiftModal.accommodationIncluded ? "bg-indigo-600" : "bg-gray-300"} relative`}
                            >
                              <div
                                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${createShiftModal.accommodationIncluded ? "left-6" : "left-1"}`}
                              />
                            </div>
                            <input
                              type="checkbox"
                              checked={createShiftModal.accommodationIncluded}
                              onChange={(e) =>
                                setCreateShiftModal((prev) => ({
                                  ...prev,
                                  accommodationIncluded: e.target.checked,
                                }))
                              }
                              className="sr-only"
                            />
                          </label>
                          {createShiftModal.accommodationIncluded && (
                            <div className="space-y-4 rounded-2xl border border-purple-100 bg-purple-50/40 p-4 min-w-0 overflow-hidden">
                              <SearchableSelectField
                                value={createShiftModal.accommodationType}
                                onChange={(accommodationType) =>
                                  setCreateShiftModal((prev) => ({
                                    ...prev,
                                    accommodationType,
                                  }))
                                }
                                options={createShiftModal.acmdOptions}
                                placeholder="Search accommodation type…"
                              />
                              {createShiftModal.accommodationType ===
                                "Other" && (
                                <textarea
                                  ref={accommodationDetailsRef}
                                  placeholder="Accommodation details..."
                                  value={
                                    createShiftModal.accommodationOtherDetails
                                  }
                                  onChange={(e) => {
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      accommodationOtherDetails:
                                        e.target.value,
                                    }));
                                    adjustTextareaHeight(e.target);
                                  }}
                                  className={`${fieldSelectClass} block w-full min-h-[96px] max-h-40 resize-y overflow-y-auto bg-white`}
                                  rows={3}
                                />
                              )}
                            </div>
                          )}
                        </div>

                        {!isClinicNetworkExperience && (
                        <div className="shrink-0 min-w-0 rounded-2xl border border-violet-200 bg-gradient-to-br from-violet-50 to-fuchsia-50/60 p-4 overflow-hidden">
                          <label className="flex items-center justify-between cursor-pointer gap-3">
                            <span className={stepFieldLabelClass + " mb-0"}>
                              <span
                                className={`${stepFieldIconClass} from-violet-100 to-fuchsia-100`}
                              >
                                ✨
                              </span>
                              Premiere
                            </span>
                            <div
                              className={`w-12 h-7 rounded-full transition-all shrink-0 ${premiereEnabled ? "bg-violet-600" : "bg-gray-300"} relative`}
                            >
                              <div
                                className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${premiereEnabled ? "left-6" : "left-1"}`}
                              />
                            </div>
                            <input
                              type="checkbox"
                              checked={premiereEnabled}
                              onChange={(e) =>
                                setPremiereEnabled(e.target.checked)
                              }
                              className="sr-only"
                            />
                          </label>
                          <p className="text-xs text-violet-800/80 mt-2 leading-relaxed break-words">
                            Give selected doctor groups early access before the
                            shift goes public. Each doctor is notified once,
                            even if they match more than one group.
                          </p>
                          {premiereEnabled ? (
                            <div className="mt-4 space-y-4 min-w-0">
                              <div className="space-y-2 min-w-0">
                                <p className="text-xs font-semibold text-violet-900 uppercase tracking-wide">
                                  Notify
                                </p>
                                <div className="grid grid-cols-1 gap-2">
                                  {PREMIERE_AUDIENCE_OPTIONS.map((option) => {
                                    const selected = premiereAudiences.includes(
                                      option.id,
                                    );
                                    return (
                                      <button
                                        key={option.id}
                                        type="button"
                                        onClick={() =>
                                          setPremiereAudiences((prev) =>
                                            selected
                                              ? prev.filter(
                                                  (id) => id !== option.id,
                                                )
                                              : [...prev, option.id],
                                          )
                                        }
                                        className={`w-full min-w-0 rounded-xl border px-3 py-2.5 text-left transition-all ${
                                          selected
                                            ? "border-violet-500 bg-white shadow-sm"
                                            : "border-violet-200 bg-white/60 hover:border-violet-300"
                                        }`}
                                      >
                                        <span className="block text-sm font-semibold text-violet-950 break-words">
                                          {option.label}
                                        </span>
                                        <span className="block text-[11px] text-violet-700/80 mt-0.5 break-words leading-snug">
                                          {option.description}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-xl border border-violet-200 bg-white/80 px-3 py-2.5 min-w-0">
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold text-violet-950">
                                    Early access period
                                  </p>
                                  <p className="text-[11px] text-violet-700/80 leading-snug break-words">
                                    Selected doctors are notified first for this
                                    many days while the shift stays private
                                  </p>
                                </div>
                                <div className="inline-flex items-center rounded-xl border border-violet-200 bg-white overflow-hidden shrink-0 self-start sm:self-center">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPremiereDays((prev) =>
                                        Math.max(1, prev - 1),
                                      )
                                    }
                                    className="px-2.5 py-2 text-violet-500 hover:bg-violet-50"
                                    aria-label="Decrease premiere days"
                                  >
                                    <Minus size={14} />
                                  </button>
                                  <input
                                    type="number"
                                    min={1}
                                    max={30}
                                    value={premiereDays}
                                    onChange={(e) => {
                                      const next = Number(e.target.value);
                                      if (!Number.isFinite(next)) return;
                                      setPremiereDays(
                                        Math.min(30, Math.max(1, next)),
                                      );
                                    }}
                                    className="w-12 text-center text-sm font-semibold text-violet-950 tabular-nums bg-transparent outline-none"
                                    aria-label="Premiere days"
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setPremiereDays((prev) =>
                                        Math.min(30, prev + 1),
                                      )
                                    }
                                    className="px-2.5 py-2 text-violet-500 hover:bg-violet-50"
                                    aria-label="Increase premiere days"
                                  >
                                    <Plus size={14} />
                                  </button>
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                        )}
                      </div>

                      {/* Right — additional documents */}
                      <div className="flex-1 flex flex-col min-h-0 min-w-0 xl:border-l xl:border-gray-100 xl:pl-8">
                        <label className="flex items-center justify-between cursor-pointer mb-4 shrink-0">
                          <span className={stepFieldLabelClass + " mb-0"}>
                            <span
                              className={`${stepFieldIconClass} from-amber-100 to-orange-100`}
                            >
                              📎
                            </span>
                            Require additional documents
                          </span>
                          <div
                            className={`w-12 h-7 rounded-full transition-all shrink-0 ${createShiftModal.requireAdditionalFiles ? "bg-indigo-600" : "bg-gray-300"} relative`}
                          >
                            <div
                              className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow-md transition-all ${createShiftModal.requireAdditionalFiles ? "left-6" : "left-1"}`}
                            />
                          </div>
                          <input
                            type="checkbox"
                            checked={
                              createShiftModal.requireAdditionalFiles || false
                            }
                            onChange={(e) =>
                              setCreateShiftModal((prev) => ({
                                ...prev,
                                requireAdditionalFiles: e.target.checked,
                                additionalDocs:
                                  e.target.checked &&
                                  prev.additionalDocs.length === 0
                                    ? [""]
                                    : prev.additionalDocs,
                              }))
                            }
                            className="sr-only"
                          />
                        </label>
                        {createShiftModal.requireAdditionalFiles ? (
                          <div className="flex flex-col flex-1 min-h-0 space-y-4 rounded-2xl border border-amber-100 bg-amber-50/40 p-4 overflow-y-auto">
                            <p className="text-sm text-gray-600 shrink-0">
                              Only hospital-specific extras are kept — CV, references,
                              police check, indemnity, WWCC, vaccination, degrees, and
                              other profile documents are ignored automatically.
                            </p>
                            {createShiftModal.additionalDocs.map((doc, idx) => (
                              <div key={idx} className="flex gap-2 shrink-0">
                                <div className="flex-1 relative">
                                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                                    {idx + 1}.
                                  </span>
                                  <input
                                    type="text"
                                    placeholder={`Document name (e.g., ${idx === 0 ? "Medical License" : "Insurance Certificate"})...`}
                                    value={doc}
                                    onChange={(e) => {
                                      const newDocs = [
                                        ...createShiftModal.additionalDocs,
                                      ];
                                      newDocs[idx] = e.target.value;
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        additionalDocs: newDocs,
                                      }));
                                    }}
                                    className={`${fieldSelectClass} pl-9 bg-white`}
                                  />
                                </div>
                                {createShiftModal.additionalDocs.length > 1 && (
                                  <button
                                    type="button"
                                    onClick={() =>
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        additionalDocs:
                                          prev.additionalDocs.filter(
                                            (_, i) => i !== idx,
                                          ),
                                      }))
                                    }
                                    className="p-3 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl"
                                  >
                                    <X size={18} />
                                  </button>
                                )}
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={() =>
                                setCreateShiftModal((prev) => ({
                                  ...prev,
                                  additionalDocs: [...prev.additionalDocs, ""],
                                }))
                              }
                              className="w-full py-3 text-sm font-semibold text-indigo-600 border border-dashed border-indigo-200 rounded-xl hover:bg-indigo-50 flex items-center justify-center gap-2 bg-white shrink-0"
                            >
                              <Plus size={16} /> Add document
                            </button>
                          </div>
                        ) : (
                          <div className="flex-1 flex items-center justify-center rounded-2xl border border-dashed border-gray-200 text-sm text-gray-400">
                            Toggle on to request extra documents from applicants
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ========== STEP 4: Time & Rate ========== */}
                {createShiftModal.step === 4 && (
                  <div className="p-4 sm:p-6 animate-fadeSlideIn flex flex-col flex-1 min-h-0 overflow-y-auto">
                    <div className="flex flex-col lg:flex-row gap-6">
                      {/* LEFT - Dates List */}
                      {createShiftModal.shifts.length > 1 && (
                        <div className="w-full lg:min-w-[280px] lg:w-[280px] flex-shrink-0 animate-slideRight flex flex-col max-h-56 lg:max-h-none overflow-hidden">
                          <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide flex items-center gap-2 flex-shrink-0">
                            <span className="w-5 h-5 bg-purple-100 rounded flex items-center justify-center text-xs">
                              📅
                            </span>
                            {createShiftModal.postMode === "group"
                              ? "Group Dates"
                              : "Select date to edit"}
                          </p>
                          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-2 space-y-1.5 flex-1 overflow-y-auto shadow-inner">
                            {createShiftModal.shifts.map((s, idx) => {
                              const isIncomplete =
                                !s.startTime ||
                                !s.endTime ||
                                !s.rate ||
                                (s.onCall && !s.onCallDetails?.trim());
                              const missingText =
                                !s.startTime || !s.endTime
                                  ? "Missing time"
                                  : !s.rate
                                    ? "Missing rate"
                                    : s.onCall && !s.onCallDetails?.trim()
                                      ? "Missing on-call details"
                                      : `${s.startTime} ${s.startAMPM}`;
                              return (
                                <div
                                  key={s.date}
                                  onClick={() =>
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      editingDateIndex: idx,
                                    }))
                                  }
                                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-all duration-300 transform hover:scale-101 ${
                                    (createShiftModal.editingDateIndex || 0) ===
                                    idx
                                      ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-200"
                                      : "bg-white hover:bg-gray-50 text-gray-700 shadow-sm hover:shadow-md"
                                  }`}
                                >
                                  <span
                                    className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold transition-all ${(createShiftModal.editingDateIndex || 0) === idx ? "bg-white/20" : isIncomplete ? "bg-red-100 text-red-600" : "bg-indigo-100 text-indigo-600"}`}
                                  >
                                    {isIncomplete
                                      ? "⚠️"
                                      : format(
                                          new Date(s.date.replace(/\//g, "-")),
                                          "d",
                                        )}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <div className="text-sm font-semibold">
                                      {format(
                                        new Date(s.date.replace(/\//g, "-")),
                                        "EEE, MMM d",
                                      )}
                                    </div>
                                    <div
                                      className={`text-xs leading-snug break-words ${(createShiftModal.editingDateIndex || 0) === idx ? "text-white/70" : isIncomplete ? "text-red-500" : "text-gray-400"}`}
                                    >
                                      {missingText}
                                      {s.onCall && (
                                        <span className="ml-1">📞</span>
                                      )}
                                    </div>
                                  </div>
                                  {(createShiftModal.editingDateIndex || 0) ===
                                    idx && (
                                    <span className="text-xs animate-pulse">
                                      ??
                                    </span>
                                  )}
                                  {(createShiftModal.postMode as string) ===
                                    "group" &&
                                    createShiftModal.shifts.length > 1 && (
                                      <div
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setCreateShiftModal((prev) => {
                                            const newShifts = [...prev.shifts];
                                            newShifts.splice(idx, 1);
                                            let newIndex =
                                              prev.editingDateIndex || 0;
                                            if (newIndex >= newShifts.length) {
                                              newIndex = newShifts.length - 1;
                                            }
                                            return {
                                              ...prev,
                                              shifts: newShifts,
                                              editingDateIndex: Math.max(
                                                0,
                                                newIndex,
                                              ),
                                            };
                                          });
                                        }}
                                        className="p-1 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 transition-colors ml-2 flex items-center justify-center pointer-events-auto"
                                        title="Delete Date"
                                      >
                                        <svg
                                          xmlns="http://www.w3.org/2000/svg"
                                          width="16"
                                          height="16"
                                          viewBox="0 0 24 24"
                                          fill="none"
                                          stroke="currentColor"
                                          strokeWidth="2"
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                        >
                                          <polyline points="3 6 5 6 21 6"></polyline>
                                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                          <line
                                            x1="10"
                                            y1="11"
                                            x2="10"
                                            y2="17"
                                          ></line>
                                          <line
                                            x1="14"
                                            y1="11"
                                            x2="14"
                                            y2="17"
                                          ></line>
                                        </svg>
                                      </div>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* RIGHT - Time/Rate Form */}
                      <div className="flex-1 space-y-5 min-w-0">
                        {/* Current Date Badge */}
                        {createShiftModal.shifts.length > 1 && (
                          <div className="flex items-center justify-between pb-4 border-b border-gray-100 animate-fadeIn">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-xl flex items-center justify-center text-white font-bold shadow-lg text-lg">
                                {format(
                                  new Date(
                                    createShiftModal.shifts[
                                      createShiftModal.editingDateIndex || 0
                                    ]?.date.replace(/\//g, "-"),
                                  ),
                                  "d",
                                )}
                              </div>
                              <div>
                                <span className="text-base font-bold text-gray-800">
                                  {format(
                                    new Date(
                                      createShiftModal.shifts[
                                        createShiftModal.editingDateIndex || 0
                                      ]?.date.replace(/\//g, "-"),
                                    ),
                                    "EEEE, MMMM d, yyyy",
                                  )}
                                </span>
                                <div className="text-xs text-gray-400">
                                  Currently editing this date
                                </div>
                              </div>
                            </div>
                            {createShiftModal.postMode === "group" && (
                              <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold">
                                📦 Group Mode
                              </span>
                            )}
                          </div>
                        )}

                        {createShiftModal.postMode === "group" && (
                          <div className="rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-xs text-indigo-800 animate-fadeIn">
                            Group mode — each date can have its own time and
                            rate. On-call settings apply to all dates in the
                            group. Transport and accommodation are shared
                            group-level settings from Step 2.
                          </div>
                        )}

                        {/* Time Picker */}
                        <div
                          className="animate-slideUp"
                          style={{ animationDelay: "50ms" }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <span className="w-7 h-7 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-lg flex items-center justify-center text-sm shadow-sm">
                                🕐
                              </span>
                              Shift Time
                            </label>
                            {createShiftModal.shifts.length > 1 &&
                              (() => {
                                const currentShift =
                                  createShiftModal.shifts[
                                    createShiftModal.editingDateIndex || 0
                                  ];
                                const bothTimesFilled =
                                  currentShift?.startTime &&
                                  currentShift?.endTime;
                                return bothTimesFilled ? (
                                  <button
                                    onClick={() => {
                                      const current =
                                        createShiftModal.shifts[
                                          createShiftModal.editingDateIndex || 0
                                        ];
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        shifts: prev.shifts.map((s) => ({
                                          ...s,
                                          startTime: current.startTime,
                                          startAMPM: current.startAMPM,
                                          endTime: current.endTime,
                                          endAMPM: current.endAMPM,
                                        })),
                                      }));
                                      setShowApplyPulse((prev) => ({
                                        ...prev,
                                        time: false,
                                      }));
                                      message.success(
                                        "⚡ Time applied to all dates!",
                                      );
                                    }}
                                    className={`text-xs text-indigo-600 hover:text-indigo-700 font-semibold px-2 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 rounded-lg transition-all duration-200 flex items-center gap-1 border border-indigo-100 flex-shrink-0 ${showApplyPulse.time ? "shadow-[0_0_10px_2px_rgba(99,102,241,0.4)]" : ""}`}
                                    style={
                                      showApplyPulse.time
                                        ? {
                                            animation:
                                              "softGlow 2s ease-in-out infinite",
                                          }
                                        : {}
                                    }
                                  >
                                    <span>⚡</span> Apply all
                                  </button>
                                ) : null;
                              })()}
                          </div>
                          <div className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-2xl p-5 shadow-inner">
                            <div className="flex items-center gap-6">
                              {/* Start Time */}
                              <div className="flex-1">
                                <div className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-wide">
                                  Start Time
                                </div>
                                <TimePicker
                                  use12Hours
                                  format="h:mm A"
                                  minuteStep={15}
                                  size="large"
                                  placeholder="Select time"
                                  className="w-full !rounded-xl"
                                  needConfirm={false}
                                  value={
                                    createShiftModal.shifts[
                                      createShiftModal.editingDateIndex || 0
                                    ]?.startTime
                                      ? dayjs(
                                          `${createShiftModal.shifts[createShiftModal.editingDateIndex || 0].startTime} ${createShiftModal.shifts[createShiftModal.editingDateIndex || 0].startAMPM}`,
                                          "h:mm A",
                                        )
                                      : null
                                  }
                                  onChange={(time) => {
                                    if (time) {
                                      const hour = time.format("h");
                                      const min = time.format("mm");
                                      const ampm = time.format("A") as
                                        | "AM"
                                        | "PM";
                                      const newTime = `${hour}:${min}`;
                                      const idx =
                                        createShiftModal.editingDateIndex || 0;
                                      setCreateShiftModal((prev) => {
                                        const newShifts = [...prev.shifts];
                                        newShifts[idx] = {
                                          ...newShifts[idx],
                                          startTime: newTime,
                                          startAMPM: ampm,
                                        };
                                        return { ...prev, shifts: newShifts };
                                      });
                                      if (createShiftModal.shifts.length > 1) {
                                        setShowApplyPulse((prev) => ({
                                          ...prev,
                                          time: true,
                                        }));
                                      }
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const typedValue = (
                                      e?.target as HTMLInputElement
                                    )?.value;
                                    if (typedValue) {
                                      applyTypedTimeToEditedShift(
                                        "start",
                                        typedValue,
                                      );
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const typedValue = (
                                        e.target as HTMLInputElement
                                      )?.value;
                                      if (typedValue) {
                                        applyTypedTimeToEditedShift(
                                          "start",
                                          typedValue,
                                        );
                                      }
                                    }
                                  }}
                                  classNames={{
                                    popup: { root: "time-picker-popup" },
                                  }}
                                />
                              </div>

                              {/* Arrow */}
                              <div className="flex flex-col items-center pt-6">
                                <div className="w-10 h-10 rounded-full bg-white shadow-md flex items-center justify-center">
                                  <span className="text-indigo-500 text-lg">
                                    →
                                  </span>
                                </div>
                              </div>

                              {/* End Time */}
                              <div className="flex-1">
                                <div className="text-xs text-gray-500 mb-2 font-semibold uppercase tracking-wide">
                                  End Time
                                </div>
                                <TimePicker
                                  use12Hours
                                  format="h:mm A"
                                  minuteStep={15}
                                  size="large"
                                  placeholder="Select time"
                                  className="w-full !rounded-xl"
                                  needConfirm={false}
                                  value={
                                    createShiftModal.shifts[
                                      createShiftModal.editingDateIndex || 0
                                    ]?.endTime
                                      ? dayjs(
                                          `${createShiftModal.shifts[createShiftModal.editingDateIndex || 0].endTime} ${createShiftModal.shifts[createShiftModal.editingDateIndex || 0].endAMPM}`,
                                          "h:mm A",
                                        )
                                      : null
                                  }
                                  onChange={(time) => {
                                    if (time) {
                                      const hour = time.format("h");
                                      const min = time.format("mm");
                                      const ampm = time.format("A") as
                                        | "AM"
                                        | "PM";
                                      const newTime = `${hour}:${min}`;
                                      const idx =
                                        createShiftModal.editingDateIndex || 0;
                                      setCreateShiftModal((prev) => {
                                        const newShifts = [...prev.shifts];
                                        newShifts[idx] = {
                                          ...newShifts[idx],
                                          endTime: newTime,
                                          endAMPM: ampm,
                                        };
                                        return { ...prev, shifts: newShifts };
                                      });
                                      if (createShiftModal.shifts.length > 1) {
                                        setShowApplyPulse((prev) => ({
                                          ...prev,
                                          time: true,
                                        }));
                                      }
                                    }
                                  }}
                                  onBlur={(e) => {
                                    const typedValue = (
                                      e?.target as HTMLInputElement
                                    )?.value;
                                    if (typedValue) {
                                      applyTypedTimeToEditedShift(
                                        "end",
                                        typedValue,
                                      );
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      const typedValue = (
                                        e.target as HTMLInputElement
                                      )?.value;
                                      if (typedValue) {
                                        applyTypedTimeToEditedShift(
                                          "end",
                                          typedValue,
                                        );
                                      }
                                    }
                                  }}
                                  classNames={{
                                    popup: { root: "time-picker-popup" },
                                  }}
                                />
                              </div>
                            </div>

                            {/* Duration Display */}
                            {(() => {
                              const shift =
                                createShiftModal.shifts[
                                  createShiftModal.editingDateIndex || 0
                                ];
                              if (!shift?.startTime || !shift?.endTime)
                                return null;
                              const [startHr, startMin] = shift.startTime
                                .split(":")
                                .map(Number);
                              const [endHr, endMin] = shift.endTime
                                .split(":")
                                .map(Number);
                              let startHr24 =
                                shift.startAMPM === "PM" && startHr !== 12
                                  ? startHr + 12
                                  : shift.startAMPM === "AM" && startHr === 12
                                    ? 0
                                    : startHr;
                              let endHr24 =
                                shift.endAMPM === "PM" && endHr !== 12
                                  ? endHr + 12
                                  : shift.endAMPM === "AM" && endHr === 12
                                    ? 0
                                    : endHr;
                              let totalMins =
                                endHr24 * 60 +
                                endMin -
                                (startHr24 * 60 + startMin);
                              if (totalMins <= 0) totalMins += 24 * 60;
                              const hours = Math.floor(totalMins / 60);
                              const mins = totalMins % 60;
                              const isOvernight =
                                endHr24 < startHr24 ||
                                (endHr24 === startHr24 && endMin <= startMin);
                              const durationKey = `${shift.startTime}-${shift.startAMPM}-${shift.endTime}-${shift.endAMPM}`;
                              return (
                                <div
                                  key={durationKey}
                                  className="mt-4 flex items-center justify-center gap-3 animate-fadeIn"
                                >
                                  <div className="px-5 py-2.5 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl flex items-center gap-2 shadow-lg shadow-green-200">
                                    <span className="text-white text-lg">
                                      ⏱️
                                    </span>
                                    <span className="text-base font-bold text-white">
                                      {hours > 0 ? `${hours}h` : ""}{" "}
                                      {mins > 0 ? `${mins}m` : ""}{" "}
                                      {hours === 0 && mins === 0 ? "0h" : ""}
                                    </span>
                                    {isOvernight && (
                                      <span className="px-2 py-0.5 bg-white/20 text-white rounded text-xs font-semibold">
                                        🌙 Overnight
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>

                        {/* Rate */}
                        <div
                          className="animate-slideUp"
                          style={{ animationDelay: "150ms" }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <label className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                              <span className="w-7 h-7 bg-gradient-to-br from-green-100 to-emerald-100 rounded-lg flex items-center justify-center text-sm shadow-sm">
                                💰
                              </span>
                              Rate
                            </label>
                            {createShiftModal.shifts.length > 1 &&
                              (() => {
                                const currentShift =
                                  createShiftModal.shifts[
                                    createShiftModal.editingDateIndex || 0
                                  ];
                                const hasRate =
                                  currentShift?.rate &&
                                  parseFloat(currentShift.rate) > 0;
                                return hasRate ? (
                                  <button
                                    onClick={() => {
                                      const current =
                                        createShiftModal.shifts[
                                          createShiftModal.editingDateIndex || 0
                                        ];
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        shifts: prev.shifts.map((s) => ({
                                          ...s,
                                          rate: current.rate,
                                          rateType: current.rateType,
                                        })),
                                      }));
                                      setShowApplyPulse((prev) => ({
                                        ...prev,
                                        rate: false,
                                      }));
                                      message.success(
                                        "⚡ Rate applied to all dates!",
                                      );
                                    }}
                                    className={`text-xs text-indigo-600 hover:text-indigo-700 font-semibold px-2 py-1 bg-gradient-to-r from-indigo-50 to-purple-50 hover:from-indigo-100 hover:to-purple-100 rounded-lg transition-all duration-200 flex items-center gap-1 border border-indigo-100 flex-shrink-0 ${showApplyPulse.rate ? "shadow-[0_0_10px_2px_rgba(99,102,241,0.4)]" : ""}`}
                                    style={
                                      showApplyPulse.rate
                                        ? {
                                            animation:
                                              "softGlow 2s ease-in-out infinite",
                                          }
                                        : {}
                                    }
                                  >
                                    <span>⚡</span> Apply all
                                  </button>
                                ) : null;
                              })()}
                          </div>
                          <div className="flex gap-3">
                            <div className="flex-1 relative group">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-xl font-bold group-focus-within:text-green-500 transition-colors">
                                $
                              </span>
                              <input
                                type="text"
                                placeholder="150"
                                value={
                                  createShiftModal.shifts[
                                    createShiftModal.editingDateIndex || 0
                                  ]?.rate || ""
                                }
                                onChange={(e) => {
                                  const val = e.target.value.replace(
                                    /[^\d.]/g,
                                    "",
                                  );
                                  const idx =
                                    createShiftModal.editingDateIndex || 0;
                                  setCreateShiftModal((prev) => {
                                    const newShifts = [...prev.shifts];
                                    newShifts[idx] = {
                                      ...newShifts[idx],
                                      rate: val,
                                    };
                                    return { ...prev, shifts: newShifts };
                                  });
                                  if (
                                    createShiftModal.shifts.length > 1 &&
                                    val
                                  ) {
                                    setShowApplyPulse((prev) => ({
                                      ...prev,
                                      rate: true,
                                    }));
                                  }
                                }}
                                className="w-full pl-11 pr-4 py-3.5 border-2 border-gray-200 rounded-xl text-2xl font-bold bg-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-green-300"
                              />
                            </div>
                            <select
                              value={
                                createShiftModal.shifts[
                                  createShiftModal.editingDateIndex || 0
                                ]?.rateType || "perHour"
                              }
                              onChange={(e) => {
                                const idx =
                                  createShiftModal.editingDateIndex || 0;
                                const newRateType = e.target.value as
                                  | "perHour"
                                  | "contracted";
                                setCreateShiftModal((prev) => {
                                  const newShifts = [...prev.shifts];
                                  newShifts[idx] = {
                                    ...newShifts[idx],
                                    rateType: newRateType,
                                  };
                                  return { ...prev, shifts: newShifts };
                                });
                                if (createShiftModal.shifts.length > 1) {
                                  setShowApplyPulse((prev) => ({
                                    ...prev,
                                    rate: true,
                                  }));
                                }
                              }}
                              className="px-4 py-3.5 border-2 border-gray-200 rounded-xl text-sm bg-white font-semibold focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 hover:border-green-300"
                            >
                              <option value="perHour">Per Hour</option>
                              <option value="contracted">Per Shift</option>
                            </select>
                          </div>
                          {(() => {
                            const currentShift =
                              createShiftModal.shifts[
                                createShiftModal.editingDateIndex || 0
                              ];
                            const shiftRateLabel =
                              currentShift?.rate &&
                              parseFloat(currentShift.rate) > 0
                                ? `$${currentShift.rate} ${
                                    currentShift.rateType === "contracted"
                                      ? "/shift"
                                      : "/hr"
                                  }`
                                : "Set shift rate above";
                            return eligibilityRows.length > 0 &&
                              !isClinicNetworkExperience ? (
                              <div
                                className="p-4 rounded-2xl border border-indigo-100 bg-indigo-50/40 space-y-3 animate-slideUp mt-3"
                                style={{ animationDelay: "200ms" }}
                              >
                                <div>
                                  <p className="text-sm font-semibold text-indigo-950">
                                    Pay by eligible role
                                  </p>
                                  <p className="text-xs text-indigo-800/70 mt-1 leading-relaxed">
                                    All eligible roles from step 2. Override pay
                                    per role, or leave blank to use the shift
                                    rate above.
                                  </p>
                                </div>
                                <ul className="space-y-2">
                                  {eligibilityRows.map((row, index) => {
                                    const isAnchor = row.isAnchor;
                                    const criteriaIndex = isAnchor
                                      ? -1
                                      : acceptCriteria.findIndex(
                                          (c) =>
                                            `${c.specialtyId}|${c.minSkillLevelId}` ===
                                            `${row.specialtyId}|${row.minSkillLevelId}`,
                                        );
                                    const specLabel =
                                      createShiftModal.specialtyOptions.find(
                                        (o) => o.value === row.specialtyId,
                                      )?.label || row.specialtyId;
                                    const skillLabel =
                                      createShiftModal.skillOptions.find(
                                        (o) =>
                                          o.value === row.minSkillLevelId,
                                      )?.label || row.minSkillLevelId;
                                    const rateType =
                                      row.rateType === "contracted"
                                        ? "contracted"
                                        : row.rateType === "perHour"
                                          ? "perHour"
                                          : "";
                                    const rateAmount =
                                      row.rateType === "contracted" &&
                                      row.contractedRate != null
                                        ? String(row.contractedRate)
                                        : row.rateType === "perHour" &&
                                            row.ratePerHour != null
                                          ? String(row.ratePerHour)
                                          : "";
                                    return (
                                      <li
                                        key={`${row.specialtyId}-${row.minSkillLevelId}-${index}`}
                                        className="flex flex-wrap items-center gap-2 rounded-xl bg-white border border-indigo-100 px-3 py-2"
                                      >
                                        <span className="text-sm font-medium text-gray-800 min-w-[120px] flex-1">
                                          {specLabel} · {skillLabel}
                                        </span>
                                        {isAnchor ? (
                                          <span className="text-xs text-gray-500 italic">
                                            {shiftRateLabel}
                                          </span>
                                        ) : (
                                          <>
                                            <select
                                              value={rateType}
                                              onChange={(e) => {
                                                const next = e.target.value as
                                                  | "perHour"
                                                  | "contracted"
                                                  | "";
                                                setAcceptCriteria((prev) =>
                                                  prev.map((c, i) => {
                                                    if (i !== criteriaIndex)
                                                      return c;
                                                    if (!next) {
                                                      const {
                                                        rateType: _rt,
                                                        ratePerHour: _rh,
                                                        contractedRate: _cr,
                                                        ...rest
                                                      } = c;
                                                      return rest;
                                                    }
                                                    if (next === "perHour") {
                                                      return {
                                                        ...c,
                                                        rateType: "perHour",
                                                        contractedRate:
                                                          undefined,
                                                      };
                                                    }
                                                    return {
                                                      ...c,
                                                      rateType: "contracted",
                                                      ratePerHour: undefined,
                                                    };
                                                  }),
                                                );
                                              }}
                                              className="px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
                                            >
                                              <option value="">
                                                Same as shift rate
                                              </option>
                                              <option value="perHour">
                                                $/hr
                                              </option>
                                              <option value="contracted">
                                                $/day
                                              </option>
                                            </select>
                                            {rateType ? (
                                              <input
                                                type="number"
                                                min={0}
                                                step="any"
                                                placeholder="Amount"
                                                value={rateAmount}
                                                onChange={(e) => {
                                                  const val = e.target.value;
                                                  setAcceptCriteria((prev) =>
                                                    prev.map((c, i) => {
                                                      if (i !== criteriaIndex)
                                                        return c;
                                                      const amount =
                                                        Number(val);
                                                      if (
                                                        !val.trim() ||
                                                        !Number.isFinite(
                                                          amount,
                                                        ) ||
                                                        amount <= 0
                                                      ) {
                                                        if (
                                                          c.rateType ===
                                                          "perHour"
                                                        ) {
                                                          return {
                                                            ...c,
                                                            ratePerHour:
                                                              undefined,
                                                          };
                                                        }
                                                        return {
                                                          ...c,
                                                          contractedRate:
                                                            undefined,
                                                        };
                                                      }
                                                      if (
                                                        c.rateType === "perHour"
                                                      ) {
                                                        return {
                                                          ...c,
                                                          ratePerHour: amount,
                                                        };
                                                      }
                                                      return {
                                                        ...c,
                                                        contractedRate: amount,
                                                      };
                                                    }),
                                                  );
                                                }}
                                                className="w-24 px-2 py-1.5 border border-gray-200 rounded-lg text-sm bg-white"
                                              />
                                            ) : null}
                                          </>
                                        )}
                                      </li>
                                    );
                                  })}
                                </ul>
                              </div>
                            ) : null;
                          })()}
                        </div>

                        {/* On-Call */}
                        <div
                          className="p-5 bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl border border-amber-100 animate-slideUp"
                          style={{ animationDelay: "250ms" }}
                        >
                          <div className="flex items-center justify-between">
                            <label className="flex items-center gap-3 cursor-pointer group flex-1">
                              <span className="w-10 h-10 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center text-lg shadow-sm group-hover:scale-110 transition-transform">
                                📞
                              </span>
                              <div>
                                <span className="text-sm font-bold text-gray-800">
                                  On-Call
                                </span>
                                <p className="text-xs text-gray-500">
                                  {createShiftModal.postMode === "group" &&
                                  createShiftModal.shifts.length > 1
                                    ? `Applies to all ${createShiftModal.shifts.length} dates (Group mode)`
                                    : "This shift includes on-call duties"}
                                </p>
                              </div>
                            </label>
                            <div className="flex items-center gap-3">
                              {createShiftModal.postMode === "separate" &&
                                createShiftModal.shifts.length > 1 && (
                                  <button
                                    onClick={() => {
                                      const current =
                                        createShiftModal.shifts[
                                          createShiftModal.editingDateIndex || 0
                                        ];
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        shifts: prev.shifts.map((s) => ({
                                          ...s,
                                          onCall: current.onCall,
                                          onCallDetails: current.onCallDetails,
                                        })),
                                      }));
                                      setShowApplyPulse((prev) => ({
                                        ...prev,
                                        onCall: false,
                                      }));
                                      message.success(
                                        "⚡ On-call applied to all dates!",
                                      );
                                    }}
                                    className={`text-xs text-amber-600 hover:text-amber-700 font-semibold px-2 py-1 bg-amber-100 hover:bg-amber-200 rounded-lg transition-all duration-200 flex items-center gap-1 flex-shrink-0 ${showApplyPulse.onCall ? "animate-pulse ring-2 ring-amber-400 ring-offset-1" : ""}`}
                                  >
                                    <span>⚡</span> Apply all
                                  </button>
                                )}
                              <div
                                onClick={() => {
                                  if (
                                    createShiftModal.postMode === "group" &&
                                    createShiftModal.shifts.length > 1
                                  ) {
                                    const newValue =
                                      !createShiftModal.shifts[0]?.onCall;
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      groupOnCall: newValue,
                                      shifts: prev.shifts.map((s) => ({
                                        ...s,
                                        onCall: newValue,
                                      })),
                                    }));
                                  } else {
                                    const idx =
                                      createShiftModal.editingDateIndex || 0;
                                    setCreateShiftModal((prev) => {
                                      const newShifts = [...prev.shifts];
                                      newShifts[idx] = {
                                        ...newShifts[idx],
                                        onCall: !newShifts[idx].onCall,
                                      };
                                      return { ...prev, shifts: newShifts };
                                    });
                                    if (createShiftModal.shifts.length > 1) {
                                      setShowApplyPulse((prev) => ({
                                        ...prev,
                                        onCall: true,
                                      }));
                                    }
                                  }
                                }}
                                className={`w-14 h-8 rounded-full transition-all duration-300 cursor-pointer ${createShiftModal.shifts[createShiftModal.editingDateIndex || 0]?.onCall ? "bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg shadow-amber-200" : "bg-gray-300"} relative`}
                              >
                                <div
                                  className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-all duration-300 ${createShiftModal.shifts[createShiftModal.editingDateIndex || 0]?.onCall ? "left-7" : "left-1"}`}
                                />
                              </div>
                            </div>
                          </div>
                          {createShiftModal.shifts[
                            createShiftModal.editingDateIndex || 0
                          ]?.onCall && (
                            <div className="mt-4 animate-slideDown">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-semibold text-gray-600">
                                  On-Call Details{" "}
                                  <span className="text-red-500">*</span>
                                </span>
                                {!createShiftModal.shifts[
                                  createShiftModal.editingDateIndex || 0
                                ]?.onCallDetails?.trim() && (
                                  <span className="text-xs text-red-500 font-medium">
                                    ⚠️ Required
                                  </span>
                                )}
                              </div>
                              <textarea
                                placeholder="Describe on-call requirements...&#10;e.g., Expected call frequency, response time, coverage needed"
                                value={
                                  createShiftModal.shifts[
                                    createShiftModal.editingDateIndex || 0
                                  ]?.onCallDetails || ""
                                }
                                onChange={(e) => {
                                  if (
                                    createShiftModal.postMode === "group" &&
                                    createShiftModal.shifts.length > 1
                                  ) {
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      groupOnCallDetails: e.target.value,
                                      shifts: prev.shifts.map((s) => ({
                                        ...s,
                                        onCallDetails: e.target.value,
                                      })),
                                    }));
                                  } else {
                                    const idx =
                                      createShiftModal.editingDateIndex || 0;
                                    setCreateShiftModal((prev) => {
                                      const newShifts = [...prev.shifts];
                                      newShifts[idx] = {
                                        ...newShifts[idx],
                                        onCallDetails: e.target.value,
                                      };
                                      return { ...prev, shifts: newShifts };
                                    });
                                  }
                                }}
                                className={`w-full px-4 py-3 border-2 rounded-xl text-sm bg-white focus:ring-2 focus:ring-amber-400 focus:border-amber-400 resize-none transition-all duration-200 ${
                                  !createShiftModal.shifts[
                                    createShiftModal.editingDateIndex || 0
                                  ]?.onCallDetails?.trim()
                                    ? "border-red-300 bg-red-50"
                                    : "border-amber-200"
                                }`}
                                rows={3}
                              />
                            </div>
                          )}
                        </div>

                        {(createShiftModal.postMode as string) !== "group" && (
                          <>
                            {/* --- Transport --- */}
                            <div
                              className="p-5 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-100 animate-slideUp"
                              style={{ animationDelay: "300ms" }}
                            >
                              <div className="flex items-center justify-between">
                                <label className="flex items-center gap-3 cursor-pointer group flex-1">
                                  <span className="w-10 h-10 bg-gradient-to-br from-blue-100 to-indigo-200 rounded-xl flex items-center justify-center text-lg shadow-sm group-hover:scale-110 transition-transform">
                                    ✈️
                                  </span>
                                  <div>
                                    <span className="text-sm font-bold text-gray-800">
                                      Transport Included?
                                    </span>
                                    <p className="text-xs text-gray-500">
                                      Cover travel expenses
                                    </p>
                                  </div>
                                </label>
                                <div
                                  onClick={() =>
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      transportIncluded:
                                        !prev.transportIncluded,
                                    }))
                                  }
                                  className={`w-14 h-8 rounded-full transition-all duration-300 cursor-pointer ${
                                    createShiftModal.transportIncluded
                                      ? "bg-gradient-to-r from-blue-500 to-indigo-500 shadow-lg shadow-blue-200"
                                      : "bg-gray-300"
                                  } relative`}
                                >
                                  <div
                                    className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-all duration-300 ${
                                      createShiftModal.transportIncluded
                                        ? "left-7"
                                        : "left-1"
                                    }`}
                                  />
                                </div>
                              </div>

                              {createShiftModal.transportIncluded && (
                                <div className="mt-4 animate-slideDown space-y-3">
                                  <div className="flex gap-2">
                                    {["variable", "set", "other"].map(
                                      (type) => (
                                        <button
                                          key={type}
                                          onClick={() =>
                                            setCreateShiftModal((prev) => ({
                                              ...prev,
                                              transportSelected: type as any,
                                            }))
                                          }
                                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide border transition-all ${
                                            createShiftModal.transportSelected ===
                                            type
                                              ? "bg-blue-500 text-white border-blue-600 shadow-md"
                                              : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
                                          }`}
                                        >
                                          {type}
                                        </button>
                                      ),
                                    )}
                                  </div>

                                  {createShiftModal.transportSelected ===
                                    "variable" && (
                                    <div className="space-y-3 pt-2">
                                      <select
                                        value={createShiftModal.transportType}
                                        onChange={(e) =>
                                          setCreateShiftModal((prev) => ({
                                            ...prev,
                                            transportType: e.target.value,
                                          }))
                                        }
                                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-400"
                                      >
                                        <option value="/km and flights included">
                                          /km + flights
                                        </option>
                                        <option value="/km no flights included">
                                          /km only
                                        </option>
                                      </select>
                                      <div className="relative">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                                          $
                                        </span>
                                        <input
                                          type="text"
                                          placeholder="0.80"
                                          value={createShiftModal.transportRate}
                                          onChange={(e) =>
                                            setCreateShiftModal((prev) => ({
                                              ...prev,
                                              transportRate:
                                                e.target.value.replace(
                                                  /[^\d.]/g,
                                                  "",
                                                ),
                                            }))
                                          }
                                          className="w-full pl-8 pr-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                                        />
                                      </div>
                                    </div>
                                  )}

                                  {createShiftModal.transportSelected ===
                                    "set" && (
                                    <div className="relative pt-2">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 font-bold">
                                        $
                                      </span>
                                      <input
                                        type="text"
                                        placeholder="50"
                                        value={createShiftModal.transportRate}
                                        onChange={(e) =>
                                          setCreateShiftModal((prev) => ({
                                            ...prev,
                                            transportRate:
                                              e.target.value.replace(
                                                /[^\d.]/g,
                                                "",
                                              ),
                                          }))
                                        }
                                        className="w-full pl-8 pr-3 py-2 border border-blue-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                                      />
                                    </div>
                                  )}

                                  {createShiftModal.transportSelected ===
                                    "other" && (
                                    <div className="pt-2">
                                      <textarea
                                        placeholder="Details..."
                                        value={
                                          createShiftModal.transportOtherDetails
                                        }
                                        onChange={(e) =>
                                          setCreateShiftModal((prev) => ({
                                            ...prev,
                                            transportOtherDetails:
                                              e.target.value,
                                          }))
                                        }
                                        className="w-full px-3 py-2 border border-blue-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-400 outline-none"
                                        rows={2}
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* --- Accommodation --- */}
                            <div
                              className="p-5 bg-gradient-to-br from-cyan-50 to-sky-50 rounded-2xl border border-cyan-100 animate-slideUp"
                              style={{ animationDelay: "350ms" }}
                            >
                              <div className="flex items-center justify-between">
                                <label className="flex items-center gap-3 cursor-pointer group flex-1">
                                  <span className="w-10 h-10 bg-gradient-to-br from-cyan-100 to-sky-200 rounded-xl flex items-center justify-center text-lg shadow-sm group-hover:scale-110 transition-transform">
                                    🏠
                                  </span>
                                  <div>
                                    <span className="text-sm font-bold text-gray-800">
                                      Accommodation Included?
                                    </span>
                                    <p className="text-xs text-gray-500">
                                      Provide housing
                                    </p>
                                  </div>
                                </label>
                                <div
                                  onClick={() =>
                                    setCreateShiftModal((prev) => ({
                                      ...prev,
                                      accommodationIncluded:
                                        !prev.accommodationIncluded,
                                    }))
                                  }
                                  className={`w-14 h-8 rounded-full transition-all duration-300 cursor-pointer ${
                                    createShiftModal.accommodationIncluded
                                      ? "bg-gradient-to-r from-cyan-500 to-sky-500 shadow-lg shadow-cyan-200"
                                      : "bg-gray-300"
                                  } relative`}
                                >
                                  <div
                                    className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-all duration-300 ${
                                      createShiftModal.accommodationIncluded
                                        ? "left-7"
                                        : "left-1"
                                    }`}
                                  />
                                </div>
                              </div>

                              {createShiftModal.accommodationIncluded && (
                                <div className="mt-4 animate-slideDown space-y-3">
                                  <select
                                    value={createShiftModal.accommodationType}
                                    onChange={(e) =>
                                      setCreateShiftModal((prev) => ({
                                        ...prev,
                                        accommodationType: e.target.value,
                                      }))
                                    }
                                    className="w-full px-3 py-2 border border-cyan-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-cyan-400 outline-none"
                                  >
                                    <option value="">Select type...</option>
                                    <option value="On site">On site</option>
                                    <option value="Nearby but organised by hospital">
                                      Nearby (Hospital organized)
                                    </option>
                                    <option value="Locum to organise but funded by hospital">
                                      Locum organized (Hospital funded)
                                    </option>
                                    <option value="Other">Other</option>
                                  </select>

                                  {createShiftModal.accommodationType ===
                                    "Other" && (
                                    <textarea
                                      placeholder="Details..."
                                      value={
                                        createShiftModal.accommodationOtherDetails
                                      }
                                      onChange={(e) =>
                                        setCreateShiftModal((prev) => ({
                                          ...prev,
                                          accommodationOtherDetails:
                                            e.target.value,
                                        }))
                                      }
                                      className="w-full px-3 py-2 border border-cyan-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-cyan-400 outline-none"
                                      rows={2}
                                    />
                                  )}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* ========== STEP 5: Review ========== */}
                {createShiftModal.step === 5 && (
                  <div className={STEP_BODY_CLASS}>
                    <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
                      {/* LEFT - Shifts List */}
                      <div className="w-full lg:w-[280px] flex-shrink-0 animate-slideRight flex flex-col max-h-56 lg:max-h-none overflow-hidden">
                        <div className="flex items-center justify-between mb-3 flex-shrink-0">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-2">
                            <span className="w-5 h-5 bg-green-100 rounded flex items-center justify-center text-xs">
                              ✅
                            </span>
                            {createShiftModal.shifts.length === 1
                              ? "1 Shift"
                              : createShiftModal.postMode === "group"
                                ? "1 Group Shift"
                                : `${createShiftModal.shifts.length} Shifts`}
                          </p>
                        </div>
                        <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-2 space-y-1.5 flex-1 overflow-y-auto shadow-inner">
                          {createShiftModal.shifts.map((shift, idx) => (
                            <div
                              key={shift.date}
                              onClick={() =>
                                setCreateShiftModal((prev) => ({
                                  ...prev,
                                  reviewSelectedIndex: idx,
                                }))
                              }
                              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all duration-300 transform hover:scale-101 animate-popIn relative group/item cursor-pointer ${
                                (createShiftModal.reviewSelectedIndex ?? 0) ===
                                idx
                                  ? "bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg shadow-indigo-200"
                                  : "bg-white hover:bg-gray-50 text-gray-700 shadow-sm hover:shadow-md"
                              }`}
                              style={{ animationDelay: `${idx * 50}ms` }}
                            >
                              {createShiftModal.shifts.length > 1 && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setCreateShiftModal((prev) => {
                                      const newShifts = prev.shifts.filter(
                                        (_, i) => i !== idx,
                                      );
                                      let newIdx =
                                        prev.reviewSelectedIndex ?? 0;
                                      if (newIdx >= newShifts.length) {
                                        newIdx = Math.max(
                                          0,
                                          newShifts.length - 1,
                                        );
                                      }
                                      // If deleted the selected one, select another
                                      if (
                                        idx === (prev.reviewSelectedIndex ?? 0)
                                      ) {
                                        // newIdx already adjusted
                                      }
                                      return {
                                        ...prev,
                                        shifts: newShifts,
                                        reviewSelectedIndex: newIdx,
                                      };
                                    });
                                  }}
                                  className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-100 text-red-500 rounded-full flex items-center justify-center opacity-0 group-hover/item:opacity-100 transition-opacity hover:bg-red-200 hover:text-red-600 shadow-sm z-10 border border-red-200"
                                  title="Remove shift"
                                >
                                  <X size={12} />
                                </button>
                              )}
                              <div
                                className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all ${(createShiftModal.reviewSelectedIndex ?? 0) === idx ? "bg-white/20" : "bg-gradient-to-br from-indigo-100 to-purple-100"}`}
                              >
                                <span
                                  className={`text-xs font-semibold ${(createShiftModal.reviewSelectedIndex ?? 0) === idx ? "text-white/80" : "text-indigo-600"}`}
                                >
                                  {format(
                                    new Date(shift.date.replace(/\//g, "-")),
                                    "MMM",
                                  )}
                                </span>
                                <span
                                  className={`text-lg font-bold -mt-0.5 ${(createShiftModal.reviewSelectedIndex ?? 0) === idx ? "" : "text-indigo-700"}`}
                                >
                                  {format(
                                    new Date(shift.date.replace(/\//g, "-")),
                                    "d",
                                  )}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold">
                                  {format(
                                    new Date(shift.date.replace(/\//g, "-")),
                                    "EEEE",
                                  )}
                                </div>
                                <div
                                  className={`text-xs ${(createShiftModal.reviewSelectedIndex ?? 0) === idx ? "text-white/70" : "text-gray-500"}`}
                                >
                                  {shift.startTime || "--"} {shift.startAMPM} -{" "}
                                  {shift.endTime || "--"} {shift.endAMPM}
                                </div>
                              </div>
                              {(createShiftModal.reviewSelectedIndex ?? 0) ===
                                idx && (
                                <span className="text-xs animate-pulse">
                                  👁️
                                </span>
                              )}
                              {shift.onCall && (
                                <span
                                  className={`text-xs px-2 py-1 rounded-lg ${(createShiftModal.reviewSelectedIndex ?? 0) === idx ? "bg-white/20" : "bg-amber-100 text-amber-700"}`}
                                >
                                  📞
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* RIGHT - Details */}
                      <div
                        className="flex-1 overflow-y-auto pr-2"
                        style={{ maxHeight: "calc(92vh - 220px)" }}
                      >
                        {(() => {
                          const selectedShift =
                            createShiftModal.shifts[
                              createShiftModal.reviewSelectedIndex ?? 0
                            ];
                          if (!selectedShift) return null;

                          let durationText = "";
                          let isOvernight = false;
                          if (
                            selectedShift.startTime &&
                            selectedShift.endTime
                          ) {
                            const [startHr, startMin] = selectedShift.startTime
                              .split(":")
                              .map(Number);
                            const [endHr, endMin] = selectedShift.endTime
                              .split(":")
                              .map(Number);
                            let startHr24 =
                              selectedShift.startAMPM === "PM" && startHr !== 12
                                ? startHr + 12
                                : selectedShift.startAMPM === "AM" &&
                                    startHr === 12
                                  ? 0
                                  : startHr;
                            let endHr24 =
                              selectedShift.endAMPM === "PM" && endHr !== 12
                                ? endHr + 12
                                : selectedShift.endAMPM === "AM" && endHr === 12
                                  ? 0
                                  : endHr;
                            let totalMins =
                              endHr24 * 60 +
                              endMin -
                              (startHr24 * 60 + startMin);
                            if (totalMins <= 0) {
                              totalMins += 24 * 60;
                              isOvernight = true;
                            }
                            const hours = Math.floor(totalMins / 60);
                            const mins = totalMins % 60;
                            durationText =
                              `${hours > 0 ? `${hours}h` : ""} ${mins > 0 ? `${mins}m` : ""}`.trim() ||
                              "0h";
                          }

                          return (
                            <div className="space-y-4">
                              {/* Date Header */}
                              <div className="bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-2xl p-5 text-white shadow-lg animate-slideDown">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <div className="text-sm opacity-80 font-medium">
                                      {format(
                                        new Date(
                                          selectedShift.date.replace(
                                            /\//g,
                                            "-",
                                          ),
                                        ),
                                        "EEEE",
                                      )}
                                    </div>
                                    <div className="text-2xl font-bold mt-1">
                                      {format(
                                        new Date(
                                          selectedShift.date.replace(
                                            /\//g,
                                            "-",
                                          ),
                                        ),
                                        "MMMM d, yyyy",
                                      )}
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    <div className="text-4xl font-bold">
                                      {format(
                                        new Date(
                                          selectedShift.date.replace(
                                            /\//g,
                                            "-",
                                          ),
                                        ),
                                        "d",
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Time & Rate */}
                              <div
                                className="grid grid-cols-2 gap-4 animate-slideUp"
                                style={{ animationDelay: "100ms" }}
                              >
                                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 hover:shadow-md transition-all duration-300">
                                  <div className="flex items-center gap-2 text-blue-600 mb-2">
                                    <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                      🕐
                                    </span>
                                    <span className="text-xs font-semibold uppercase tracking-wide">
                                      Time
                                    </span>
                                    {isOvernight && (
                                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">
                                        🌙 Overnight
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-lg font-bold text-gray-800">
                                    {selectedShift.startTime || "--"}{" "}
                                    {selectedShift.startAMPM}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    to {selectedShift.endTime || "--"}{" "}
                                    {selectedShift.endAMPM}
                                    {isOvernight && (
                                      <span className="text-purple-600 font-medium">
                                        {" "}
                                        (next day)
                                      </span>
                                    )}
                                  </div>
                                  {durationText && (
                                    <div className="mt-2 px-2 py-1 bg-blue-100 rounded-lg text-xs font-semibold text-blue-700 inline-block">
                                      ⏱️ {durationText}
                                    </div>
                                  )}
                                </div>
                                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-4 border border-green-100 hover:shadow-md transition-all duration-300">
                                  <div className="flex items-center gap-2 text-green-600 mb-2">
                                    <span className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                                      💰
                                    </span>
                                    <span className="text-xs font-semibold uppercase tracking-wide">
                                      Rate
                                    </span>
                                  </div>
                                  <div className="text-2xl font-bold text-gray-800">
                                    ${selectedShift.rate || "--"}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {selectedShift.rateType === "perHour"
                                      ? "per hour"
                                      : "per shift"}
                                  </div>
                                </div>
                              </div>

                              {/* Requirements */}
                              <div
                                className="bg-gradient-to-br from-gray-50 to-slate-50 rounded-xl p-4 border border-gray-100 animate-slideUp"
                                style={{ animationDelay: "200ms" }}
                              >
                                <div className="flex items-center gap-2 text-gray-600 mb-4">
                                  <span className="w-6 h-6 bg-gray-100 rounded-lg flex items-center justify-center text-xs">
                                    📋
                                  </span>
                                  <span className="text-xs font-semibold uppercase tracking-wide">
                                    Requirements
                                  </span>
                                </div>
                                <div className="space-y-4">
                                  {eligibilityRows.length > 0 && (
                                    <div>
                                      <div className="text-xs text-gray-400 mb-2">
                                        Eligible roles
                                      </div>
                                      <ul className="space-y-1.5">
                                        {eligibilityRows.map((row, idx) => {
                                          const specLabel =
                                            createShiftModal.specialtyOptions.find(
                                              (o) =>
                                                o.value === row.specialtyId,
                                            )?.label || "—";
                                          const skillLabel =
                                            createShiftModal.skillOptions.find(
                                              (o) =>
                                                o.value ===
                                                row.minSkillLevelId,
                                            )?.label || "—";
                                          const customRate =
                                            formatAcceptCriterionRate(row);
                                          const shiftRate =
                                            selectedShift.rate &&
                                            parseFloat(selectedShift.rate) > 0
                                              ? `$${selectedShift.rate} ${
                                                  selectedShift.rateType ===
                                                  "contracted"
                                                    ? "/shift"
                                                    : "/hr"
                                                }`
                                              : null;
                                          const rateLabel =
                                            customRate ||
                                            (row.isAnchor
                                              ? shiftRate
                                              : shiftRate
                                                ? `${shiftRate} (default)`
                                                : null);
                                          return (
                                            <li
                                              key={`${row.specialtyId}-${row.minSkillLevelId}-${idx}`}
                                              className="flex flex-wrap items-baseline justify-between gap-2 text-sm"
                                            >
                                              <span className="font-semibold text-gray-800">
                                                {specLabel} · {skillLabel}
                                              </span>
                                              {rateLabel ? (
                                                <span className="text-xs font-medium text-green-700">
                                                  {rateLabel}
                                                </span>
                                              ) : null}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </div>
                                  )}
                                  <div className="grid grid-cols-2 gap-4">
                                    <div className="group">
                                      <div className="text-xs text-gray-400 mb-1">
                                        Support
                                      </div>
                                      <div className="text-sm font-semibold text-gray-800 group-hover:text-indigo-600 transition-colors">
                                        {createShiftModal.supportLevel || "—"}
                                      </div>
                                    </div>
                                    <div className="group">
                                      <div className="text-xs text-gray-400 mb-1">
                                        On-Call
                                      </div>
                                      {(() => {
                                        const isOnCall = selectedShift.onCall;
                                        const onCallDetails =
                                          selectedShift.onCallDetails;
                                        return (
                                          <>
                                            <div
                                              className={`text-sm font-semibold ${isOnCall ? "text-amber-600" : "text-gray-400"}`}
                                            >
                                              {isOnCall ? "📞 Yes" : "No"}
                                            </div>
                                            {isOnCall && onCallDetails && (
                                              <div className="text-xs text-gray-500 mt-1 italic">
                                                "{onCallDetails}"
                                              </div>
                                            )}
                                          </>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {(createShiftModal.postMode as string) !==
                                "group" && (
                                <>
                                  {(createShiftModal.postMode as string) !==
                                    "group" && (
                                    <>
                                      {(createShiftModal.postMode as string) !==
                                        "group" && (
                                        <>
                                          {/* Provisions */}
                                          <div
                                            className="flex gap-3 animate-slideUp"
                                            style={{ animationDelay: "300ms" }}
                                          >
                                            {/* Transport Detail */}
                                            <div
                                              className={`flex-1 rounded-xl p-4 transition-all duration-300 hover:scale-102 ${
                                                createShiftModal.transportIncluded
                                                  ? "bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 shadow-sm"
                                                  : "bg-gray-50 border border-gray-100"
                                              }`}
                                            >
                                              <div className="flex items-center gap-2 mb-1">
                                                <span>
                                                  {createShiftModal.transportIncluded
                                                    ? "✈️"
                                                    : "🚫"}
                                                </span>
                                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                  Transport
                                                </div>
                                              </div>
                                              <div
                                                className={`text-sm font-bold mt-1 ${
                                                  createShiftModal.transportIncluded
                                                    ? "text-blue-700"
                                                    : "text-gray-400"
                                                }`}
                                              >
                                                {createShiftModal.transportIncluded ? (
                                                  <>
                                                    {createShiftModal.transportSelected ===
                                                      "variable" && (
                                                      <span>
                                                        $
                                                        {createShiftModal.transportRate ||
                                                          "0"}{" "}
                                                        {createShiftModal.transportType?.includes(
                                                          "no flights",
                                                        )
                                                          ? "/km"
                                                          : "/km + Flights"}
                                                      </span>
                                                    )}
                                                    {createShiftModal.transportSelected ===
                                                      "set" && (
                                                      <span>
                                                        $
                                                        {createShiftModal.transportRate ||
                                                          "0"}{" "}
                                                        Fixed
                                                      </span>
                                                    )}
                                                    {createShiftModal.transportSelected ===
                                                      "other" && (
                                                      <span>
                                                        Custom Details
                                                      </span>
                                                    )}
                                                  </>
                                                ) : (
                                                  "Not included"
                                                )}
                                              </div>
                                              {createShiftModal.transportIncluded &&
                                                createShiftModal.transportSelected ===
                                                  "other" && (
                                                  <div className="text-xs text-blue-600/80 mt-1 font-medium leading-tight truncate">
                                                    {
                                                      createShiftModal.transportOtherDetails
                                                    }
                                                  </div>
                                                )}
                                            </div>

                                            {/* Accommodation Detail */}
                                            <div
                                              className={`flex-1 rounded-xl p-4 transition-all duration-300 hover:scale-102 ${
                                                createShiftModal.accommodationIncluded
                                                  ? "bg-gradient-to-br from-cyan-50 to-sky-50 border border-cyan-200 shadow-sm"
                                                  : "bg-gray-50 border border-gray-100"
                                              }`}
                                            >
                                              <div className="flex items-center gap-2 mb-1">
                                                <span>
                                                  {createShiftModal.accommodationIncluded
                                                    ? "🏠"
                                                    : "🚫"}
                                                </span>
                                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                                  Accommodation
                                                </div>
                                              </div>
                                              <div
                                                className={`text-sm font-bold mt-1 ${
                                                  createShiftModal.accommodationIncluded
                                                    ? "text-cyan-700"
                                                    : "text-gray-400"
                                                }`}
                                              >
                                                {createShiftModal.accommodationIncluded
                                                  ? createShiftModal.accommodationType ===
                                                    "Other"
                                                    ? "Custom"
                                                    : createShiftModal.accommodationType
                                                  : "Not included"}
                                              </div>
                                              {createShiftModal.accommodationIncluded &&
                                                createShiftModal.accommodationType ===
                                                  "Other" && (
                                                  <div className="text-xs text-cyan-600/80 mt-1 font-medium leading-tight">
                                                    {
                                                      createShiftModal.accommodationOtherDetails
                                                    }
                                                  </div>
                                                )}
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </>
                                  )}
                                </>
                              )}
                              {/* Additional Files */}
                              {createShiftModal.requireAdditionalFiles &&
                                createShiftModal.additionalDocs.some((d) =>
                                  d.trim(),
                                ) && (
                                  <div
                                    className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100 animate-slideUp"
                                    style={{ animationDelay: "400ms" }}
                                  >
                                    <div className="flex items-center gap-2 text-blue-700 mb-3">
                                      <span className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center text-xs">
                                        📎
                                      </span>
                                      <span className="text-xs font-semibold uppercase tracking-wide">
                                        Required Documents
                                      </span>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                      {createShiftModal.additionalDocs
                                        .filter((d) => d.trim())
                                        .map((doc, i) => (
                                          <span
                                            key={i}
                                            className="px-3 py-1.5 bg-white rounded-lg text-sm text-blue-700 font-medium shadow-sm border border-blue-100 animate-popIn"
                                            style={{
                                              animationDelay: `${i * 50}ms`,
                                            }}
                                          >
                                            {doc}
                                          </span>
                                        ))}
                                    </div>
                                  </div>
                                )}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {hasPrefillDocuments && (
                <div
                  className={`absolute top-0 right-0 bottom-0 border-l border-violet-100 bg-gradient-to-b from-violet-50/60 to-indigo-50/40 transition-all duration-300 ${isDocumentRailCollapsed ? "w-16" : "w-[500px]"}`}
                >
                  <div className="h-full flex flex-col">
                    <div className="px-3 py-3 border-b border-violet-100 flex items-center justify-between gap-2">
                      {!isDocumentRailCollapsed && (
                        <div className="flex items-center gap-3">
                          <div className="text-base font-bold text-violet-800">
                            Reference document
                          </div>
                          <a
                            href={selectedPrefillDocumentDownloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-semibold text-violet-700 hover:text-violet-900"
                          >
                            Open <ExternalLink size={11} />
                          </a>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() =>
                          setIsDocumentRailCollapsed((prev) => !prev)
                        }
                        className="h-9 w-9 rounded-lg border border-violet-200 bg-white text-violet-700 hover:bg-violet-100 flex items-center justify-center"
                        title={
                          isDocumentRailCollapsed
                            ? "Expand document panel"
                            : "Collapse document panel"
                        }
                      >
                        {isDocumentRailCollapsed ? (
                          <ChevronLeft size={15} />
                        ) : (
                          <ChevronRight size={15} />
                        )}
                      </button>
                    </div>

                    {isDocumentRailCollapsed ? (
                      <div className="flex-1 flex items-start justify-center py-4">
                        <span className="h-8 w-8 rounded-md bg-violet-100 text-violet-700 border border-violet-200 flex items-center justify-center text-xs font-bold">
                          1
                        </span>
                      </div>
                    ) : (
                      <>
                        <div className="flex-1 p-3 min-h-0">
                          {isDocumentPreviewLoading ? (
                            <div className="w-full h-full rounded-lg border border-violet-200 bg-white flex items-center justify-center text-base text-violet-700 px-6 text-center">
                              Loading document preview...
                            </div>
                          ) : selectedPrefillDocumentPreviewUrl ? (
                            <div className="w-full h-full rounded-lg border border-violet-200 bg-white overflow-auto">
                              <iframe
                                src={selectedPrefillDocumentPreviewUrl}
                                title="AI source document"
                                className="w-full h-full bg-white"
                                style={{ border: "none" }}
                              />
                            </div>
                          ) : (
                            <div className="w-full h-full rounded-lg border border-dashed border-violet-200 bg-white flex items-center justify-center text-base text-violet-700 px-6 text-center">
                              Preview unavailable. Use Open to view the source
                              document.
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-slate-50 border-t border-gray-100 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
              <div className="flex justify-center sm:justify-start">
                {!isEditMode &&
                  !isAiReview &&
                  createShiftModal.step >= 2 &&
                  (createShiftModal.specialty ||
                    createShiftModal.shifts.some((s) => s.startTime)) && (
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        setShowSaveDialog(true);
                      }}
                      className="px-4 py-2.5 text-sm font-semibold text-amber-700 bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl hover:from-amber-100 hover:to-yellow-100 transition-all duration-200 flex items-center gap-2 hover:scale-105 active:scale-95 shadow-sm hover:shadow-md"
                    >
                      <span>💾</span> Save Template
                    </button>
                  )}
              </div>
              <div className="flex items-center justify-end gap-2 sm:gap-3">
                {(createShiftModal.step > 1 ||
                  (isClinicNetworkExperience &&
                    clinicNetworkPickerDone &&
                    !isEditMode &&
                    createShiftModal.step === 1)) && (
                  <button
                    onClick={() => {
                      if (
                        isClinicNetworkExperience &&
                        clinicNetworkPickerDone &&
                        !isEditMode &&
                        createShiftModal.step === 1
                      ) {
                        setClinicNetworkPickerDone(false);
                        return;
                      }
                      setCreateShiftModal((prev) => ({
                        ...prev,
                        step: prev.step - 1,
                      }));
                    }}
                    disabled={createShiftModal.submitting}
                    className="px-5 py-3 text-base font-semibold text-gray-600 hover:bg-gray-200 rounded-xl transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    ← Back
                  </button>
                )}
                {createShiftModal.step < TOTAL_STEPS ? (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleAdvanceStep();
                    }}
                    disabled={
                      createShiftModal.submitting ||
                      (createShiftModal.step === 1 &&
                        isClinicNetworkExperience &&
                        !isEditMode &&
                        !clinicNetworkPickerDone &&
                        (!selectedBrandId ||
                          selectedClinicIds.length === 0)) ||
                      (createShiftModal.step === 1 &&
                        (clinicNetworkPickerDone ||
                          !isClinicNetworkExperience ||
                          isEditMode) &&
                        (createShiftModal.shifts.length === 0 ||
                          (isClinicNetworkExperience &&
                            (!selectedBrandId ||
                              selectedClinicIds.length === 0)))) ||
                      (createShiftModal.step === 2 &&
                        (!createShiftModal.specialty ||
                          !createShiftModal.skillLevel)) ||
                      (createShiftModal.step === 4 &&
                        (createShiftModal.shifts.some(
                          (s) => !s.startTime || !s.endTime || !s.rate,
                        ) ||
                          createShiftModal.shifts.some(
                            (s) => s.onCall && !s.onCallDetails?.trim(),
                          )))
                    }
                    className="px-5 sm:px-7 py-3 text-base font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 shadow-lg shadow-indigo-200 whitespace-nowrap"
                  >
                    {createShiftModal.step === 4 &&
                    (createShiftModal.shifts.some(
                      (s) => !s.startTime || !s.endTime || !s.rate,
                    ) ||
                      createShiftModal.shifts.some(
                        (s) => s.onCall && !s.onCallDetails?.trim(),
                      ))
                      ? `⚠️ ${createShiftModal.shifts.filter((s) => !s.startTime || !s.endTime || !s.rate || (s.onCall && !s.onCallDetails?.trim())).length} incomplete`
                      : "Continue"}
                  </button>
                ) : (
                  <>
                    {isAiReview && onRejectAiShift && (
                      <button
                        type="button"
                        onClick={async () => {
                          if (rejectingAiShift || createShiftModal.submitting) return;
                          setRejectingAiShift(true);
                          try {
                            await onRejectAiShift();
                            closeModal();
                          } finally {
                            setRejectingAiShift(false);
                          }
                        }}
                        disabled={
                          createShiftModal.submitting || rejectingAiShift
                        }
                        className="px-5 py-2.5 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-200"
                      >
                        {rejectingAiShift ? "Rejecting…" : "Reject"}
                      </button>
                    )}
                    <button
                      onClick={submitQuickCreate}
                      disabled={
                        createShiftModal.submitting ||
                        (showPublishHospitalPicker && !publishHospitalId)
                      }
                      className="px-8 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl hover:from-green-700 hover:to-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-2 shadow-lg shadow-green-200 hover:shadow-xl hover:scale-105 active:scale-95"
                    >
                      {createShiftModal.submitting ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />{" "}
                          {isEditMode ? "Updating..." : "Creating..."}
                        </>
                      ) : (
                        <>
                          <Check size={16} />
                          {isEditMode
                            ? "Update Shift"
                            : isAiReview
                              ? "Validate & publish"
                              : `Create Shift${createShiftModal.shifts.length > 1 ? "s" : ""}`}
                        </>
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
                @keyframes modalBgIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes modalIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes popIn {
                    from { opacity: 0; transform: scale(0.8); }
                    to { opacity: 1; transform: scale(1); }
                }
                @keyframes slideDown {
                    from { opacity: 0; transform: translateY(-15px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideUp {
                    from { opacity: 0; transform: translateY(15px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes slideRight {
                    from { opacity: 0; transform: translateX(-20px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateX(10px); }
                    to { opacity: 1; transform: translateX(0); }
                }
                @keyframes pulse-subtle {
                    0%, 100% { transform: scale(1.05); }
                    50% { transform: scale(1.08); }
                }
                @keyframes bounce-subtle {
                    0%, 100% { transform: translateY(0); }
                    50% { transform: translateY(-3px); }
                }
                .animate-modalBgIn { animation: modalBgIn 0.3s ease-out; }
                .animate-modalIn { animation: modalIn 0.4s cubic-bezier(0.16, 1, 0.3, 1); }
                .animate-popIn { animation: popIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1) both; }
                .animate-slideDown { animation: slideDown 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
                .animate-slideUp { animation: slideUp 0.35s cubic-bezier(0.16, 1, 0.3, 1) both; }
                .animate-slideRight { animation: slideRight 0.4s cubic-bezier(0.16, 1, 0.3, 1) both; }
                .animate-fadeIn { animation: modalBgIn 0.25s ease-out both; }
                .animate-fadeSlideIn { animation: fadeSlideIn 0.35s cubic-bezier(0.16, 1, 0.3, 1); }
                .animate-pulse-subtle { animation: pulse-subtle 2s ease-in-out infinite; }
                .animate-bounce-subtle { animation: bounce-subtle 1.5s ease-in-out infinite; }
                .hover\\:scale-101:hover { transform: scale(1.01); }
                .hover\\:scale-102:hover { transform: scale(1.02); }
                .active\\:scale-98:active { transform: scale(0.98); }
                .active\\:scale-99:active { transform: scale(0.99); }
                
                /* Searchable select dropdown — render above Post shift (z-11000) */
                .quick-create-select-dropdown {
                    z-index: 12050 !important;
                }
                .quick-create-select-dropdown .ant-select-item-option-active {
                    background: #eef2ff !important;
                }
                
                /* Ant Design TimePicker Custom Styles */
                .time-picker-popup {
                    z-index: 12050 !important;
                }
                .time-picker-popup .ant-picker-panel-container {
                    border-radius: 16px !important;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.15) !important;
                    overflow: hidden;
                }
                .time-picker-popup .ant-picker-time-panel-column {
                    scrollbar-width: thin;
                    scrollbar-color: #e2e8f0 transparent;
                }
                .time-picker-popup .ant-picker-time-panel-column::-webkit-scrollbar {
                    width: 6px;
                }
                .time-picker-popup .ant-picker-time-panel-column::-webkit-scrollbar-thumb {
                    background: #cbd5e1;
                    border-radius: 3px;
                }
                .time-picker-popup .ant-picker-time-panel-cell-selected .ant-picker-time-panel-cell-inner {
                    background: linear-gradient(135deg, #6366f1, #8b5cf6) !important;
                    color: white !important;
                    border-radius: 8px !important;
                    font-weight: 600 !important;
                }
                .time-picker-popup .ant-picker-time-panel-cell .ant-picker-time-panel-cell-inner:hover {
                    background: #f1f5f9 !important;
                    border-radius: 8px !important;
                }
                .time-picker-popup .ant-picker-ok button {
                    background: linear-gradient(135deg, #6366f1, #8b5cf6) !important;
                    border: none !important;
                    border-radius: 8px !important;
                    font-weight: 600 !important;
                }
                .time-picker-popup .ant-picker-ok button:hover {
                    background: linear-gradient(135deg, #4f46e5, #7c3aed) !important;
                }
            `}</style>

      {/* Apply Template Dialog */}
      <Dialog
        open={showApplyTemplate}
        onOpenChange={(isOpen) => {
          setShowApplyTemplate(isOpen);
          if (!isOpen) {
            setApplyingTemplateId(null);
          }
        }}
      >
        <DialogContent
          className="sm:max-w-[460px] z-[12050]"
          overlayClassName="z-[12040]"
          style={{ zIndex: 12050 }}
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-100 text-indigo-700">
                <Calendar size={16} />
              </span>
              Apply Template
            </DialogTitle>
            <DialogDescription>
              Select a template to auto-fill this shift form.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 max-h-[340px] overflow-y-auto mt-4 px-1">
            {templatesLoading ? (
              <div className="flex flex-col items-center justify-center gap-3 py-8 text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin" />
                <p className="text-sm">Loading templates...</p>
              </div>
            ) : availableTemplates.length > 0 ? (
              availableTemplates.map((t) => {
                const displaySpecialty =
                  t.specialtyName ||
                  createShiftModal.specialtyOptions.find(
                    (o) => o.value === t.specialty,
                  )?.label ||
                  t.specialty ||
                  "";
                const displaySkill =
                  t.skillLevelName ||
                  createShiftModal.skillOptions.find(
                    (o) => o.value === t.skillLevel,
                  )?.label ||
                  t.skillLevel ||
                  "";
                const payLabel = formatTemplateDefaultRate(t);
                const doctorsLabel =
                  t.positionsRequired != null && t.positionsRequired > 0
                    ? `${t.positionsRequired} doctor${t.positionsRequired === 1 ? "" : "s"}`
                    : null;
                const alsoOpenCount = Array.isArray(t.acceptCriteria)
                  ? t.acceptCriteria.length
                  : 0;

                return (
                  <Button
                    key={t.templateId}
                    variant="outline"
                    disabled={Boolean(applyingTemplateId)}
                    onClick={() => handleApplyTemplate(t)}
                    className="justify-start text-left h-auto py-3 hover:border-indigo-300 hover:bg-indigo-50/50"
                  >
                    <div className="flex flex-col w-full gap-1">
                      <span className="font-bold text-base text-gray-900">
                        {t.templateName}
                      </span>
                      {(displaySpecialty || displaySkill) && (
                        <span className="text-xs text-gray-500 mt-1 whitespace-normal break-words">
                          {displaySpecialty}
                          {displaySpecialty && displaySkill ? " • " : ""}
                          {displaySkill}
                        </span>
                      )}
                      {(payLabel || doctorsLabel || t.onCallFlag || alsoOpenCount > 0) && (
                        <span className="text-xs text-gray-500 whitespace-normal break-words">
                          {[
                            payLabel,
                            doctorsLabel,
                            t.onCallFlag ? "On call" : null,
                            alsoOpenCount > 0
                              ? `Also open to (${alsoOpenCount})`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(" • ")}
                        </span>
                      )}
                      {alsoOpenCount > 0 && (
                        <span className="text-[11px] text-amber-800 whitespace-normal break-words">
                          {t.acceptCriteria
                            ?.slice(0, 2)
                            .map((row: any) =>
                              formatTemplateAcceptCriterionLabel(
                                row,
                                Object.fromEntries(
                                  createShiftModal.specialtyOptions.map((o) => [
                                    o.value,
                                    o.label,
                                  ]),
                                ),
                                Object.fromEntries(
                                  createShiftModal.skillOptions.map((o) => [
                                    o.value,
                                    o.label,
                                  ]),
                                ),
                              ),
                            )
                            .join(" · ")}
                          {alsoOpenCount > 2 ? ` · +${alsoOpenCount - 2} more` : ""}
                        </span>
                      )}
                      {applyingTemplateId === t.templateId && (
                        <span className="inline-flex items-center gap-2 text-xs text-indigo-600 mt-1">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Applying template...
                        </span>
                      )}
                    </div>
                  </Button>
                );
              })
            ) : (
              <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 px-4 py-6 text-center">
                <p className="text-sm font-medium text-gray-700">
                  No templates found
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Save a template first, then apply it here.
                </p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Save Template Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent
          className="sm:max-w-[500px] z-[12050]"
          overlayClassName="z-[12040]"
          style={{ zIndex: 12050 }}
        >
          <DialogHeader>
            <DialogTitle>Save Template</DialogTitle>
            <DialogDescription>
              Save your current shift configuration as a template for future
              use.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex flex-col gap-3">
              <label htmlFor="template-name" className="text-sm font-medium">
                Template Name
              </label>
              <Input
                id="template-name"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                placeholder="Enter template name..."
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              type="button"
              onClick={() => {
                setShowSaveDialog(false);
                setTemplateName("");
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSaveTemplate}>Save Template</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AiShiftValidationReviewDialog
        open={eligibleRolesDialogOpen && !isClinicNetworkExperience}
        onOpenChange={setEligibleRolesDialogOpen}
        eligibleOnly
        mappedAlerts={[]}
        eligibleSuggestions={pendingEligibleSuggestions}
        specialtyOptions={createShiftModal.specialtyOptions}
        skillOptions={createShiftModal.skillOptions}
        onAddEligibleRole={(row) => addEligibleRoles([row])}
        onAddAllEligibleRoles={() =>
          addEligibleRoles(pendingEligibleSuggestions)
        }
        onContinue={advanceToNextStep}
        advanceOnSkip={eligibilitySuggestionsPromptShown}
      />

      <InviteDoctorsModal
        open={Boolean(postCreateInviteShiftId)}
        shiftsId={postCreateInviteShiftId || ""}
        onClose={handlePostCreateInviteClose}
      />
    </div>,
    document.body,
  );
};

export default QuickCreateShiftModal;
